import React, { useCallback, useContext, useLayoutEffect, useMemo, useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import cloneDeep from 'lodash/cloneDeep';
import { format, parse, parseISO } from 'date-fns';
import formula from 'excel-formula';
import { create, all } from 'mathjs';

// Custom Components
import DataContext from '../../Contexts/DataContext';
import './projectHome.scss'
import {
  getMonthsBetweenDates,
  getAccountDetails,
  newGetAccountDetails,
  formatBackendDate
} from '../../Helpers/utils';
import {
  currencyFormatter,
  basePeriodTotal,
  changeTotal,
  forecastPeriodMonth,
  forecastPeriodTotal,
  getSingleMonthChange,
  singleMonthGetter,
  calculateRowChange,
  calculateAccountTotal,
  calculateSingleMonthChange,
  getDominantChange
} from '../../Helpers/aggrid';
import { 
  calculateMasterStatChangeAllMonths, 
  recalculateMasterStatAllMonths,
  getChildStatDrivers,
  getDrivenAccountsByStatDriver,
  isMasterStatDriver
} from '../../Helpers/masterStatCalculations';
import {
  buildReferenceAccountMap,
  findAffectedCalculatedAccounts,
  logAffectedCalculatedAccounts,
  calculateAllMonthlyImpacts
} from '../../Helpers/calculatedAccountCalculations';
import {
  processChangeColumns,
  insertChangeColumnGroups,
  rebuildForecastColumn,
  removeOldChangeColumns
} from '../../Helpers/changeColumnBuilder';
import GridUpdateOrchestrator from '../../Helpers/gridUpdateOrchestrator';
import { debugLogger } from '../../Helpers/debugLogger';
import { getProjectParentAccountsTotals, getProjectDataByGlKey, getAccountsByGlKeys, getParentAccountDepartments, getSavedViews, createSavedView, updateSavedView, deleteSavedView } from '../../Helpers/dataApi'
import { useAuth } from '../../Contexts/AuthContext'
import {
  getAcctTypeSection,
  addNetRevenueSummaryRow,
  addBottomLineSummaryRow,
} from '../../Helpers/incomeStatementCalculations'
// Icons
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FilterListIcon from '@mui/icons-material/FilterList';
import Alert from '@mui/material/Alert';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Snackbar from '@mui/material/Snackbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import AuditTrailTooltip from '../../Components/AuditTrailTooltip';
import ProjectHomeFilterDrawer from './ProjectHomeFilterDrawer';
import { BATCH_SIZE, SNACKBAR_ERROR_DURATION_MS } from '../../Helpers/constants';
import { saveFilters, loadFilters, setLastActiveProjectId, getLastActiveProjectId } from '../../Helpers/filterPersistence';

// Converts a "yyyy-MM-dd" date string to an "MMM yyyy" column key used throughout the grid.
// Also handles "MMM yyyy" format (returns as-is if already in that format).
// Returns the original string if parsing fails, rather than throwing.
const toMonthYear = (dateString) => {
  if (!dateString) return dateString;

  // If already in "MMM yyyy" format, return as-is
  if (/^[A-Z][a-z]{2} \d{4}$/.test(dateString)) {
    return dateString;
  }
  
  // Try to parse as ISO date (yyyy-MM-dd); return original string on any failure
  try {
    const [year, month, day] = dateString.split('-');
    const date = parseISO(`${year}-${month}-${day}T00:00:00`);
    return format(date, 'MMM yyyy');
  } catch (e) {
    return dateString;
  }
};

// Custom Tooltip Component for Calculated Accounts and Master Stats
const CalculatedAccountTooltip = forwardRef((props, ref) => {
  const { context, api, rowIndex } = props;
  const [auditTrail, setAuditTrail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // AG Grid requires this for custom tooltip components
  useImperativeHandle(ref, () => ({}));
  
  const rowNode = api?.getDisplayedRowAtIndex(rowIndex);
  
  // For group rows, get data from the first child
  let data = rowNode?.data;
  if (rowNode?.group && !data) {
    const firstChild = rowNode.allLeafChildren?.[0];
    data = firstChild?.data;
  }
  
  useEffect(() => {
    let isMounted = true;

    const fetchAuditTrail = async () => {
      if (!data || !context?.accessToken) {
        if (isMounted) {
          setIsLoading(false);
        }
        return;
      }

      let trail = [];

      if (data.isMasterStatChild && data.masterStatId) {
        // Fetch master stat audit trail using the numeric ID and project ID from context
        if (context.fetchAndCacheMasterStatAuditTrail && context.projectId) {
          trail = await context.fetchAndCacheMasterStatAuditTrail(
            data.masterStatId,
            context.projectId,
            context.accessToken
          );
        }
      } else if (data.isCalculatedAccount && data.calculatedAccountId) {
        // Fetch calculated account audit trail (uses same endpoint as changes)
        if (context.fetchAndCacheAuditTrail) {
          trail = await context.fetchAndCacheAuditTrail(
            data.calculatedAccountId,
            context.accessToken
          );
        }
      }

      if (isMounted) {
        setAuditTrail(trail);
        setIsLoading(false);
      }
    };
    
    fetchAuditTrail();

    return () => {
      isMounted = false;
    };
  }, [data, context]);
  
  if (!data || (!data.isCalculatedAccount && !data.isMasterStatChild)) {
    return null;
  }
  
  // Render Calculated Account Tooltip
  if (data.isCalculatedAccount) {
    return (
      <AuditTrailTooltip
        title="Calculated Account"
        auditEntries={auditTrail}
        isLoading={isLoading}
      >
        <div style={{ marginBottom: '8px' }}>
          <span style={{ color: '#aaa' }}>Formula:</span> {data.formula || 'N/A'}
        </div>
        {data.referenceAccounts && data.referenceAccounts.length > 0 && (
          <>
            <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>Reference Accounts:</div>
            {data.referenceAccounts.map((refAcct, idx) => (
              <div key={idx} style={{ marginLeft: '8px', marginBottom: '2px' }}>
                <span style={{ fontWeight: 'bold' }}>{refAcct.rowLetter}</span> {refAcct.glKey} - {refAcct.accountName || 'Unknown'}
              </div>
            ))}
          </>
        )}
      </AuditTrailTooltip>
    );
  }

  // Render Master Stat Tooltip (via child row data when the group node is hovered)
  if (data.isMasterStatChild) {
    return (
      <AuditTrailTooltip
        title={data.masterStatName || 'Master Stat'}
        auditEntries={auditTrail}
        isLoading={isLoading}
      />
    );
  }
  
  return null;
});

// Custom Tooltip Component for Change Columns
const ChangeColumnTooltip = forwardRef((props, ref) => {
  const { colDef, context } = props;
  const [auditTrail, setAuditTrail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // AG Grid requires this for custom tooltip components
  useImperativeHandle(ref, () => ({}));
  
  // Always read changeId from tooltipComponentParams (the proper AG Grid way)
  const changeId = colDef?.tooltipComponentParams?.changeId;
  
  useEffect(() => {
    let isMounted = true;

    if (!changeId || !context?.fetchAndCacheAuditTrail) {
      if (isMounted) {
        setIsLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }
    
    // Fetch audit trail (cache is handled internally in the method).
    // This function returns an array and does not throw; an empty array indicates an error.
    const fetchAuditTrail = async () => {
      const trail = await context.fetchAndCacheAuditTrail(changeId, context.accessToken);
      if (isMounted) {
        setAuditTrail(trail);
        setIsLoading(false);
      }
    };
    
    fetchAuditTrail();

    return () => {
      isMounted = false;
    };
  }, [changeId, context]);
  
  if (!changeId || !context) {
    return null;
  }
  
  // Find the change object
  const change = context.changes?.find(c => c.id === changeId);
  
  if (!change) {
    return null;
  }
  
  // Don't show tooltip if there's no audit trail - backwards compatible with old changes
  if (!isLoading && (!auditTrail || auditTrail.length === 0)) {
    return (
      <AuditTrailTooltip
        title={change.change_label}
        auditEntries={[]}
        isLoading={false}
      />
    );
  }
  
  return (
    <AuditTrailTooltip
      title={change.change_label}
      auditEntries={auditTrail}
      isLoading={isLoading}
    />
  );
});

// Custom Header Group Component for Parent Change column groups.
// Shows a stacked-columns icon (hints at nested changes) plus a chevron for expand/collapse.
const ParentChangeGroupHeader = (props) => {
  const { displayName, columnGroup, setExpanded } = props;
  const [isExpanded, setIsExpandedState] = useState(
    columnGroup?.isExpanded?.() ?? false
  );

  // Keep local state in sync when AG Grid changes the group externally
  useEffect(() => {
    const listener = () => {
      setIsExpandedState(columnGroup?.isExpanded?.() ?? false);
    };
    columnGroup?.addEventListener?.('expandedChanged', listener);
    return () => columnGroup?.removeEventListener?.('expandedChanged', listener);
  }, [columnGroup]);

  const handleToggle = () => {
    const next = !isExpanded;
    setExpanded(next);
    setIsExpandedState(next);
  };

  return (
    <div
      onClick={handleToggle}
      style={{
        alignItems: 'center',
        cursor: 'pointer',
        display: 'flex',
        gap: '5px',
        height: '100%',
        width: '100%',
      }}
      title={isExpanded ? 'Collapse group' : 'Expand to see individual changes'}
    >
      {/* Stacked-columns icon — subtle hint that nested changes exist */}
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          flexShrink: 0,
          gap: '2px',
          opacity: 0.55,
        }}
      >
        <span style={{ background: 'currentColor', borderRadius: '1px', display: 'block', height: '3px', width: '12px' }} />
        <span style={{ background: 'currentColor', borderRadius: '1px', display: 'block', height: '3px', marginLeft: '3px', width: '9px' }} />
        <span style={{ background: 'currentColor', borderRadius: '1px', display: 'block', height: '3px', marginLeft: '6px', width: '6px' }} />
      </span>

      {/* Label */}
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {displayName}
      </span>

      {/* Chevron — uses AG Grid's own icon classes to match all other column groups */}
      <span
        className={`ag-header-expand-icon ag-header-expand-icon-${isExpanded ? 'expanded' : 'contracted'}`}
        style={{ flexShrink: 0, marginLeft: 'auto' }}
      >
        <span className={`ag-icon ag-icon-${isExpanded ? 'expanded' : 'contracted'}`} />
      </span>
    </div>
  );
};

// Portal tooltip that renders at fixed coordinates and shows the full audit trail.
const TooltipPortal = ({ top, left, changeId, change, context }) => {
  const [auditTrail, setAuditTrail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    if (!changeId || !context?.fetchAndCacheAuditTrail) {
      setIsLoading(false);
      return () => { isMounted = false; };
    }
    const fetch = async () => {
      const trail = await context.fetchAndCacheAuditTrail(changeId, context.accessToken);
      if (isMounted) { setAuditTrail(trail); setIsLoading(false); }
    };
    fetch();
    return () => { isMounted = false; };
  }, [changeId, context]);

  return (
    <div style={{ position: 'fixed', top, left, zIndex: 10000, pointerEvents: 'none' }}>
      <AuditTrailTooltip
        title={change?.change_label}
        auditEntries={auditTrail || []}
        isLoading={isLoading}
      />
    </div>
  );
};

// Custom Header Group Component for Change Columns with Tooltip
const ChangeColumnHeader = (props) => {
  const [tooltipPos, setTooltipPos] = useState(null); // {top, left} when visible
  const headerRef = useRef(null);
  
  const { displayName, columnGroup, setExpanded, context } = props;

  // Track expanded state for the expand/collapse chevron
  const [isExpanded, setIsExpandedState] = useState(
    columnGroup?.isExpanded?.() ?? false
  );

  useEffect(() => {
    const listener = () => setIsExpandedState(columnGroup?.isExpanded?.() ?? false);
    columnGroup?.addEventListener?.('expandedChanged', listener);
    return () => columnGroup?.removeEventListener?.('expandedChanged', listener);
  }, [columnGroup]);

  const handleToggle = () => {
    const next = !isExpanded;
    setExpanded(next);
    setIsExpandedState(next);
  };

  // Pull changeId — prefer it directly from the group colDef, fall back to first leaf column
  const changeId = columnGroup?.getColGroupDef?.()?.changeId
    || columnGroup?.getColGroupDef?.()?.metadata?.changeId
    || columnGroup?.getLeafColumns?.()[0]?.getUserProvidedColDef?.()?.changeId
    || columnGroup?.getLeafColumns?.()[0]?.getColDef?.()?.metadata?.changeId;

  const change = changeId && context?.changes
    ? context.changes.find(c => c.id === changeId)
    : null;
  
  // Show tooltip for any change that exists — audit trail fetched on hover
  const hasTooltipData = !!change;

  // Use a callback ref so we get notified as soon as the DOM node is available.
  // Walking up to .ag-header-group-cell covers the full cell width whether the
  // group is collapsed (one Total column) or expanded (multiple month columns).
  // Coordinates are computed at mouseenter time so they're always fresh.
  const setHeaderRef = useCallback((node) => {
    if (!node || !hasTooltipData) return;
    headerRef.current = node;
    const cell = node.closest('.ag-header-group-cell') || node;
    const handleEnter = () => {
      const rect = cell.getBoundingClientRect();
      // Guard against zero-size rects (e.g. if layout hasn't settled yet)
      if (!rect.width && !rect.height) return;
      const tooltipWidth = 400;
      const idealLeft = rect.left + rect.width / 2 - tooltipWidth / 2;
      const clampedLeft = Math.max(8, Math.min(idealLeft, window.innerWidth - tooltipWidth - 8));
      setTooltipPos({
        top: rect.bottom + 8,
        left: clampedLeft,
      });
    };
    const handleLeave = () => setTooltipPos(null);
    cell.addEventListener('mouseenter', handleEnter);
    cell.addEventListener('mouseleave', handleLeave);
    node._tooltipCleanup = () => {
      cell.removeEventListener('mouseenter', handleEnter);
      cell.removeEventListener('mouseleave', handleLeave);
    };
  }, [hasTooltipData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Run cleanup on unmount
  useEffect(() => {
    return () => { headerRef.current?._tooltipCleanup?.(); };
  }, []);
  
  return (
    <div 
      ref={setHeaderRef}
      style={{ 
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        position: 'relative',
        overflow: 'visible',
        cursor: 'pointer',
      }}
      onClick={handleToggle}
    >
      {/* Label */}
      <span style={{
        flex: 1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        color: context?.calculatingChangeIds?.has?.(changeId) ? '#9e9e9e' : undefined,
        transition: 'color 0.4s ease',
      }}>
        {displayName}
      </span>

      {/* Calculating spinner — shown while stat driver math is running */}
      {context?.calculatingChangeIds?.has?.(changeId) && (
        <span
          title="Calculating driven account values…"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: '14px',
            height: '14px',
          }}
        >
          <span
            style={{
              display: 'block',
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              border: '2px solid rgba(25, 118, 210, 0.25)',
              borderTopColor: '#1976d2',
              animation: 'calc-spinner-rotate 0.75s linear infinite',
              flexShrink: 0,
            }}
          />
        </span>
      )}

      {/* Expand/collapse chevron — matches AG Grid's own icon classes */}
      <span
        className={`ag-header-expand-icon ag-header-expand-icon-${isExpanded ? 'expanded' : 'contracted'}`}
        style={{ flexShrink: 0, marginLeft: 'auto' }}
      >
        <span className={`ag-icon ag-icon-${isExpanded ? 'expanded' : 'contracted'}`} />
      </span>

      {/* Tooltip — rendered via a portal with coordinates calculated at hover time */}
      {tooltipPos && hasTooltipData && typeof document !== 'undefined' && ReactDOM.createPortal(
        <TooltipPortal
          top={tooltipPos.top}
          left={tooltipPos.left}
          changeId={changeId}
          change={change}
          context={context}
        />,
        document.body
      )}
    </div>
  );
};

// const filterButtons = [
//   'apply',
//   'reset',
// ];

/**
 * Gets entity name from entity ID by looking up in existing rows
 * @param {String} entityId - Entity ID
 * @param {Array} rows - Grid row data
 * @returns {String} Entity name
 */
const getEntityName = (entityId, rows) => {
  const row = rows.find(r => r.entityId === entityId);
  return row?.entity || entityId;
};

/**
 * Gets department name from department ID by looking up in existing rows
 * @param {String} departmentId - Department ID
 * @param {Array} rows - Grid row data
 * @returns {String} Department name
 */
const getDepartmentName = (departmentId, rows) => {
  const row = rows.find(r => r.departmentId === departmentId);
  return row?.department || departmentId;
};

/**
 * Calculates the value for a Master Stat cell by summing child stat values
 * Used as a valueGetter for change columns in Phase 3
 * 
 * This function will be used when implementing change logic for Master Stats.
 * When changes are applied to child stats, this function dynamically calculates
 * the Master Stat's value for a given month by summing the current values of
 * all child stats (including any applied changes).
 * 
 * Example usage in Phase 3:
 * ```
 * valueGetter: (params) => {
 *   if (params.node?.group && params.node?.field === 'parentAccount') {
 *     // Master Stat group row: sum child stat values via AG Grid row node lookup
 *     const childGlKeys = params.node.allLeafChildren
 *       .filter(n => n.data?.isMasterStatChild)
 *       .map(n => n.data?.glKey);
 *     return calculateMasterStatMonthValue(childGlKeys, month, params.api);
 *   }
 *   return params.data?.[month];
 * }
 * ```
 * 
 * @param {Array} childStatGlKeys - Array of child stat GL keys
 * @param {String} month - Month string (e.g., 'Jan 2023')
 * @param {Object} gridApi - AG Grid API instance
 * @returns {Number} Sum of child stat values for the month
 */
const calculateMasterStatMonthValue = (childStatGlKeys, month, gridApi) => {
  if (!gridApi || !childStatGlKeys || childStatGlKeys.length === 0) {
    return 0;
  }
  
  return childStatGlKeys.reduce((sum, childGlKey) => {
    const childNode = gridApi.getRowNode(childGlKey);
    if (childNode && childNode.data) {
      const childValue = childNode.data[month] || 0;
      return sum + childValue;
    }
    return sum;
  }, 0);
};



/**
 * Factory for a fresh empty filter state — called wherever a clean slate is needed.
 * Using a factory (not a constant) prevents mutable Set aliasing: if code ever
 * accidentally calls .add()/.delete() on a Set from state, it won't corrupt a
 * shared constant used elsewhere (useState initialisers, loadFilters fallback, etc.).
 *
 * Adding a new filter type: add a key here + handle it in applyFiltersToRowData.
 */
const makeEmptyFilters = () => ({
  entityIds:         new Set(),
  deptIds:           new Set(),
  dateRange:         null,
  disabledChangeIds: new Set(),
});

/** Convenience alias used in JSX and effects — always produces a fresh object. */
const EMPTY_FILTERS = makeEmptyFilters();

export default function ProjectHomeAgGrid(props) {
  const context = useContext(DataContext);
  const { loadingOrchestrator, newChange, setNewChange, pendingCalculatingLabel, onPendingCalculatingLabelConsumed } = props; // Get props

  const gridRef = useRef(); // For accessing AG Grid's API
  const gridOrchestratorRef = useRef(); // Grid update orchestrator
  const masterStatsInitialLoadRef = useRef(false); // Track if master stats have been initially loaded
  const isInitialLoadRef = useRef(true); // Track if we're in initial load phase
  const initialMasterStatsLengthRef = useRef(null); // Capture initial master stats length
  const netRevenueCacheRef = useRef({}); // Cache for getNetRevenueValue results, keyed by columnId then department
  const bottomLineCacheRef = useRef({}); // Cache for getBottomLineValue results, keyed by columnId
  // Pre-computed per-department base values for Net Revenue and Bottom Line summary rows.
  // Populated eagerly at grid load time by fetching dept-level data for NPR/Other Revenue/Exp.
  // Structure: { dept: { [columnId]: value } }  where dept is "deptId - deptName" string.
  const nrDeptBaseValuesRef = useRef({}); // { dept → { columnId → netRevenue } }
  const blDeptBaseValuesRef = useRef({}); // { dept → { columnId → bottomLine } }
  // Stable dept list set during initial load; reused by subsequent setRowData calls so
  // they don't lose the NR/BL synthetic rows due to stale context closures.
  const currentDeptsRef = useRef([]);
  const deleteCalcOriginalIndexRef = useRef(-1); // Stable ref for rollback index in deleteCalcAccount

  // Track all timeouts for cleanup
  const timeoutsRef = useRef([]);
  const isMountedRef = useRef(true);
  
  let {
    activeProjectAttributes,
    activeProjectInfo,
    deleteAccountChange,
    setActiveProjectAttributes
  } = context;

  let {
    changes,
    drivers,
  } = activeProjectAttributes;

  // Use calculatedAccounts directly from context instead of local state
  const calculatedAccounts = activeProjectAttributes.calculatedAccounts || [];
  
  const [firstRender, setFirstRender] = useState(false);

  const auth = useAuth()
  const activeUser = auth.activeUser

  const [changesData, setChangesData] = useState([]);
  const [rowData, setRowData] = useState([]); // Grid row data for client-side model

  const [basePeriodDataLoaded ,setBasePeriodDataLoaded] = useState(false)
  const setSidebarEnabled = () => {}; // no-op — sidebar replaced by custom ProjectHomeFilterDrawer
  const [gridReady, setGridReady] = useState(false); // Track if grid is initialized
  const [expandingParents, setExpandingParents] = useState(new Set()); // Track which parent accounts are currently loading children
  // Mirror expandingParents in a ref so autoGroupColumnDef's innerRenderer can read
  // the current value without the useMemo depending on the state — prevents a full
  // column-definition rebuild (and grid re-render) on every expand/collapse event.
  const expandingParentsRef = useRef(new Set());
  // Wrapper that keeps the ref in sync with the state on every update.
  const setExpandingParentsWithRef = useCallback((updater) => {
    setExpandingParents(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      expandingParentsRef.current = next;
      return next;
    });
  }, []);
  const [lazyLoadError, setLazyLoadError] = useState(null); // Error message for lazy-load failures

  // ── Unified filter state ──────────────────────────────────────────────────
  // Single source of truth for all active filters. Adding a new filter type
  // (taxonomy, account type, etc.) is a one-key addition to this object and
  // its corresponding handling in applyFiltersToRowData / doesExternalFilterPass.
  //
  // Shape:
  //   entityIds        Set<string>  — entity_id strings.  Empty = All Entities.
  //   deptIds          Set<string>  — "deptId - deptName". Empty = All Departments.
  //   dateRange        object|null  — { from, to, startDate, endDate } or null = full range.
  //   disabledChangeIds Set<number> — change IDs hidden from the forecast.
  const [activeFilters, setActiveFilters] = useState(makeEmptyFilters);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  // ── Saved filter views state ──────────────────────────────────────────
  const [savedViews, setSavedViews] = useState([]);
  const [savedViewsSaving, setSavedViewsSaving] = useState(false);

  // ── Filter restore pulse ──────────────────────────────────────────────
  // Briefly pulses the filter bar after a project loads with active filters,
  // reminding the user that previously-applied filters are in effect.
  const [filterPulseActive, setFilterPulseActive] = useState(false);

  // Guards the auto-save useEffect from writing empty filters during the async
  // restore window on mount/project-switch. Set to true once applyInitialFilters
  // completes (or the project-switch reset runs). Any activeFilters change that
  // originates from user interaction will always see this as true.
  const filtersRestoredRef = useRef(false);

  // Stable ref — AG Grid callbacks and valueGetters read this synchronously.
  const activeFiltersRef = useRef(makeEmptyFilters());
  useLayoutEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  // Convenience aliases so the rest of the code stays readable without
  // deeply destructuring activeFilters everywhere.  These are derived values
  // only — never set them directly; always go through setActiveFilters.
  const selectedEntityIds   = activeFilters.entityIds;
  const selectedDeptIds     = activeFilters.deptIds;
  const dateRangeFilter     = activeFilters.dateRange;
  const disabledChangeIds   = activeFilters.disabledChangeIds;

  // Stable refs for values read synchronously inside AG Grid callbacks.
  // Kept in sync with activeFilters via the useLayoutEffect below.
  const selectedEntityIdsRef  = useRef(new Set());
  const selectedDeptIdsRef    = useRef(new Set());
  const disabledChangeIdsRef  = useRef(new Set());
  useLayoutEffect(() => {
    selectedEntityIdsRef.current  = activeFilters.entityIds;
    selectedDeptIdsRef.current    = activeFilters.deptIds;
    disabledChangeIdsRef.current  = activeFilters.disabledChangeIds;
  }, [activeFilters]);

  // Set of change IDs currently being calculated — drives the spinner in ChangeColumnHeader
  // and the blue border/tint on the calculating column's group header.
  // Covers two change types with meaningfully long processing times:
  //   - Stat driver changes: heavy frontend JS (recalculateDrivenAccountsFromStatChanges)
  //   - Master stat changes: backend async distribution calculation, polled up to ~37s
  const [calculatingChangeIds, setCalculatingChangeIds] = useState(new Set());

  // When a stat driver change is identified in changeDetails (before the API call),
  // projectHome passes us the change label via pendingCalculatingLabel so we can
  // start the column loading state immediately — covering the full API + calculation
  // window rather than just the frontend calculation phase.
  //
  // We match by label because the change ID isn't assigned until after the API call.
  // Once changesVersion fires and we have the real ID, we swap to ID-based tracking.
  //
  // When pendingCalculatingLabel goes back to null (failure/cancel), clear any
  // loading state that was started optimistically.
  const pendingCalculatingLabelRef = useRef(null);
  useEffect(() => {
    if (!pendingCalculatingLabel) {
      // Failure or cancel — if we started a spinner optimistically, clear it
      if (pendingCalculatingLabelRef.current) {
        pendingCalculatingLabelRef.current = null;
        setCalculatingChangeIds(new Set());
      }
      return;
    }
    pendingCalculatingLabelRef.current = pendingCalculatingLabel;

    // Find an existing change with this label (edit scenario) — if found, use its ID.
    // For new changes the ID doesn't exist yet; the changesVersion useEffect will pick it up.
    const existingChange = (activeProjectAttributes?.changes || [])
      .find(c => c.change_label === pendingCalculatingLabel
        && (c.affects_stat_drivers || c.is_master_stat_change));
    if (existingChange) {
      setCalculatingChangeIds(new Set([existingChange.id]));
      onPendingCalculatingLabelConsumed?.();
      pendingCalculatingLabelRef.current = null;
    }
    // For new changes, leave pendingCalculatingLabelRef set — changesVersion will consume it
  }, [pendingCalculatingLabel]); // eslint-disable-line react-hooks/exhaustive-deps

  // When calculatingChangeIds changes, highlight the calculating column header
  // using inline styles — more reliable than CSS class selectors which conflict
  // with AG Grid's own specificity rules.
  //
  // Active state:  calculating col group header → blue border + tinted bg + blue text
  //                calculating col leaf headers  → blue text
  // Exit:          transition styles back over 0.5s, then remove
  useEffect(() => {
    const gridEl = document.querySelector('.project-home-ag-grid');
    if (!gridEl) return;

    if (calculatingChangeIds.size === 0) {
      // Transition out the calculating column styling
      gridEl.querySelectorAll('.ag-change-calculating').forEach(el => {
        el.classList.remove('ag-change-calculating');
        el.style.transition = 'box-shadow 0.5s ease, color 0.5s ease, background-color 0.5s ease';
        el.style.boxShadow = '';
        el.style.color = '';
        el.style.backgroundColor = '';
      });
      return;
    }

    // Clear any stale state first
    gridEl.querySelectorAll('.ag-change-calculating').forEach(el => {
      el.classList.remove('ag-change-calculating');
      el.style.removeProperty('box-shadow');
      el.style.removeProperty('color');
      el.style.removeProperty('background-color');
    });

    gridEl.querySelectorAll('[col-id]').forEach(el => {
      const colId = el.getAttribute('col-id');
      if (!colId) return;

      // Is this element part of a calculating column?
      let isCalc = false;
      for (const changeId of calculatingChangeIds) {
        const id = String(changeId);
        if (colId === `${id}_0` || colId === `${id} total` || colId.startsWith(`${id} `)) {
          isCalc = true;
          break;
        }
      }

      if (isCalc) {
        el.classList.add('ag-change-calculating');
        if (el.classList.contains('ag-header-group-cell')) {
          el.style.transition = 'box-shadow 0.4s ease, color 0.4s ease, background-color 0.4s ease';
          el.style.boxShadow = 'inset 0 3px 0 0 #1976d2, inset 3px 0 0 0 #1976d2, inset -3px 0 0 0 #1976d2';
          el.style.backgroundColor = 'rgba(25, 118, 210, 0.07)';
          el.style.color = '#9e9e9e';
        } else if (el.classList.contains('ag-header-cell')) {
          // Leaf headers (e.g. "Total") — grey text to signal pending state
          el.style.transition = 'color 0.4s ease';
          el.style.color = '#9e9e9e';
        }
      }
    });
  }, [calculatingChangeIds]);

  // Reset flags when project changes
  useEffect(() => {
    masterStatsInitialLoadRef.current = false;
    isInitialLoadRef.current = true;
    // Clear dept base value refs so stale data from the previous project
    // doesn't display before the new project's loadNRBLDeptBaseValues completes.
    nrDeptBaseValuesRef.current = {};
    blDeptBaseValuesRef.current = {};
    currentDeptsRef.current = [];
    setSidebarEnabled(false);    setBasePeriodDataLoaded(false);
    setChangesData([]);
    // ── Filter reset on project switch ────────────────────────────────
    // IMPORTANT: Always clear filters synchronously to empty first.
    // This prevents the prior project's entity IDs, dept IDs, or
    // disabledChangeIds from being applied — even briefly — to the new
    // project's row data during the async fetch window. A stale entity
    // filter would make the grid appear blank; stale disabledChangeIds
    // could silently hide a change in the new project that happens to
    // share the same integer ID.
    filtersRestoredRef.current = false; // suppress auto-save until restore completes
    setActiveFilters(makeEmptyFilters());
    setSavedViews([]);

    // Write the last active project ID synchronously here — before the async
    // applyInitialFilters call reads it — so isSameProject is always based on
    // the value from the *previous* project, not a stale write from a prior effect.
    if (activeProjectInfo?.id) {
      setLastActiveProjectId(activeProjectInfo.id);
    }

    // Then asynchronously apply the right initial state.
    //
    // Priority rules:
    //   - Same project as last session (reload / re-login):
    //       → restore last-used filters from localStorage (default view ignored)
    //   - Switching to a different project:
    //       → apply default saved view if one exists, else localStorage, else empty
    //
    // This ensures active filters always survive a reload or session timeout,
    // while default views still apply automatically when navigating to a new project.
    const applyInitialFilters = async () => {
      if (!activeProjectInfo?.id) {
        // Guard: ID not yet available — release the save guard so user interactions
        // in this brief window are still persisted when the effect re-fires.
        filtersRestoredRef.current = true;
        return;
      }

      const isSameProject = String(getLastActiveProjectId()) === String(activeProjectInfo.id);

      // When returning to the same project, skip the default saved view entirely
      // and go straight to the user's last-used filter state.
      if (isSameProject) {
        // Still fetch saved views so the panel is populated, but don't apply the default.
        if (activeUser?.accessToken) {
          try {
            const res = await getSavedViews(activeProjectInfo.id, activeUser.accessToken);
            if (Array.isArray(res?.data)) setSavedViews(res.data);
          } catch {
            // Non-critical
          }
        }
        const savedFilters = loadFilters(activeProjectInfo.id, makeEmptyFilters());
        if (savedFilters) setActiveFilters(savedFilters);
        filtersRestoredRef.current = true;
        // Pulse if restored filters are non-empty — checked after setActiveFilters
        // so we read the value we just set, not the stale closure from mount.
        const restoredFilters = savedFilters || makeEmptyFilters();
        if (restoredFilters.entityIds.size > 0 || restoredFilters.deptIds.size > 0 ||
            restoredFilters.dateRange != null || restoredFilters.disabledChangeIds.size > 0) {
          setFilterPulseActive(true);
          setTimeout(() => setFilterPulseActive(false), 5000);
        }
        return;
      }

      // Switching to a new project: apply default saved view first, then localStorage fallback.
      let restoredFilters = null;
      if (activeUser?.accessToken) {
        try {
          const res = await getSavedViews(activeProjectInfo.id, activeUser.accessToken);
          if (Array.isArray(res?.data)) {
            setSavedViews(res.data);
            const defaultView = res.data.find(v => v.is_default);
            if (defaultView) {
              restoredFilters = deserialiseFilterState(defaultView.filter_state);
              setActiveFilters(restoredFilters);
              filtersRestoredRef.current = true;
              if (restoredFilters.entityIds.size > 0 || restoredFilters.deptIds.size > 0 ||
                  restoredFilters.dateRange != null || restoredFilters.disabledChangeIds.size > 0) {
                setFilterPulseActive(true);
                setTimeout(() => setFilterPulseActive(false), 5000);
              }
              return;
            }
          }
        } catch {
          // Non-critical — fall through to localStorage
        }
      }

      // No default saved view — use localStorage last-used filters for this project.
      const savedFilters = loadFilters(activeProjectInfo.id, makeEmptyFilters());
      if (savedFilters) {
        setActiveFilters(savedFilters);
        if (savedFilters.entityIds.size > 0 || savedFilters.deptIds.size > 0 ||
            savedFilters.dateRange != null || savedFilters.disabledChangeIds.size > 0) {
          setFilterPulseActive(true);
          setTimeout(() => setFilterPulseActive(false), 5000);
        }
      }
      filtersRestoredRef.current = true;
      // If nothing saved, stay empty (already set synchronously above)
    };
    applyInitialFilters();
    originalPlaceholderValuesRef.current = null;
  }, [activeProjectInfo?.id]);

  // Initialize Grid Update Orchestrator
  useEffect(() => {
    isMountedRef.current = true;
    
    if (gridRef.current?.api) {
      gridOrchestratorRef.current = new GridUpdateOrchestrator(gridRef, {
        enableLogging: process.env.NODE_ENV === 'development',
        onError: (msg, data) => {
          // Log errors in development for debugging; integrate with CloudWatch in production
          if (process.env.NODE_ENV === 'development') {
            console.error('[GridUpdateOrchestrator Error]', msg, data);
          }
          // TODO: Integrate with CloudWatch logging in production
        },
        onWarning: (msg, data) => {
          // Log warnings in development for debugging
          if (process.env.NODE_ENV === 'development') {
            console.warn('[GridUpdateOrchestrator Warning]', msg, data);
          }
        }
      });
      
      // Expose grid API on window for E2E testing (development only)
      if (process.env.NODE_ENV !== 'production') {
        window.__agGridApi = gridRef.current.api;
      }
    }
    
    return () => {
      isMountedRef.current = false;
      
      // Cancel any pending operations on unmount
      gridOrchestratorRef.current?.cancelAll();
      
      // Clear any pending column change debounce timer
      if (columnChangeTimerRef.current) {
        clearTimeout(columnChangeTimerRef.current);
      }
      
      // Clear all tracked timeouts
      timeoutsRef.current.forEach(timeoutId => clearTimeout(timeoutId));
      timeoutsRef.current = [];
    };
  }, [gridRef.current?.api]);

  // Helper function for safe synchronous grid API access
  // This provides null-guarded access to the grid API but does NOT provide timing protection
  // For timing-safe operations, use gridOrchestratorRef.current methods instead
  const safeGridApi = useCallback(() => {
    if (!gridRef.current?.api) {
      return null;
    }
    return gridRef.current.api;
  }, []);

  // Helper function to safely schedule timeouts with automatic cleanup tracking
  const safeSetTimeout = useCallback((callback, delay) => {
    if (!isMountedRef.current) {
      return null;
    }
    
    const timeoutId = setTimeout(() => {
      if (isMountedRef.current) {
        callback();
      }
      // Remove from tracking array
      const index = timeoutsRef.current.indexOf(timeoutId);
      if (index > -1) {
        timeoutsRef.current.splice(index, 1);
      }
    }, delay);
    
    // Track timeout for cleanup
    timeoutsRef.current.push(timeoutId);
    return timeoutId;
  }, []);

  /**
   * Eagerly loads per-department base values for Net Revenue and Bottom Line rows
   * by calling the department API for each NPR/Other Revenue/Operating Expense parent account.
   * Results are stored in nrDeptBaseValuesRef and blDeptBaseValuesRef so the valueGetters
   * can display correct values without requiring parent accounts to be expanded first.
   *
   * @param {Array} placeholderRows - The initial grid rows (hasChildren placeholders)
   * @param {number} projectId - Current project id
   * @param {string} accessToken - Auth token
   */
  const loadNRBLDeptBaseValues = async (placeholderRows, projectId, accessToken) => {
    // Find all parent accounts in NPR, Other Revenue, and Operating Expenses sections
    const relevantRows = (placeholderRows || []).filter(r =>
      r.acctTypeSection === 'Net Patient Revenue' ||
      r.acctTypeSection === 'Other Revenue' ||
      r.acctTypeSection === 'Operating Expenses'
    );

    if (relevantRows.length === 0) return;

    // Fetch department data for each parent account in parallel
    const fetches = relevantRows.map(async (row) => {
      try {
        const resp = await getParentAccountDepartments(projectId, row.parentAccount, accessToken);
        const isError = resp instanceof Error || resp?.isAxiosError;
        const status = resp?.status ?? resp?.response?.status;
        if (isError || status !== 200) return null;
        const depts = resp.data?.departments;
        if (!depts?.length) return null;
        return { parentAccount: row.parentAccount, acctTypeSection: row.acctTypeSection, acctType: row.acctType, depts };
      } catch {
        return null;
      }
    });

    const results = (await Promise.all(fetches)).filter(Boolean);

    // Aggregate per-department values by section
    // Structure: { deptKey: { [columnId]: { npr: 0, otherRev: 0, expenses: 0 } } }
    const deptAgg = {};

    results.forEach(({ acctTypeSection, acctType, depts }) => {
      depts.forEach(dept => {
        const deptKey = `${dept.department_id} - ${dept.department_name}`;
        if (!deptAgg[deptKey]) {
          deptAgg[deptKey] = {};
        }

        // Each column key in dept.dates is a yyyy-MM-dd string
        Object.entries(dept.dates || {}).forEach(([dateStr, rawValue]) => {
          const monthYear = toMonthYear(dateStr);
          const value = Number(rawValue) || 0;

          if (!deptAgg[deptKey][monthYear]) {
            deptAgg[deptKey][monthYear] = { npr: 0, otherRev: 0, expenses: 0 };
          }

          if (acctTypeSection === 'Net Patient Revenue') {
            // NPR = Revenue - Deductions.  The API returns the per-acctType sum,
            // so Revenue adds and Deductions subtract.
            const isRevenue = acctType === 'Revenue' || acctType === 'Rev';
            const isDeduction = acctType === 'Deduction' || acctType === 'Ded';
            if (isRevenue) deptAgg[deptKey][monthYear].npr += value;
            else if (isDeduction) deptAgg[deptKey][monthYear].npr -= value;
          } else if (acctTypeSection === 'Other Revenue') {
            deptAgg[deptKey][monthYear].otherRev += value;
          } else if (acctTypeSection === 'Operating Expenses') {
            deptAgg[deptKey][monthYear].expenses += value;
          }
        });
      });
    });

    // Convert to nrDeptBaseValuesRef / blDeptBaseValuesRef
    // Store both base period keys ("Jan 2023") and forecast period keys ("Jan 2023 forecast")
    // since forecast base = base period (changes appear in separate bridge columns, not here).
    // Also compute totals for 'base total' and 'forecast total'.
    const newNR = {};
    const newBL = {};

    Object.entries(deptAgg).forEach(([deptKey, monthMap]) => {
      newNR[deptKey] = {};
      newBL[deptKey] = {};

      let nrTotal = 0;
      let blTotal = 0;

      Object.entries(monthMap).forEach(([monthYear, { npr, otherRev, expenses }]) => {
        const nr = npr + otherRev;
        const bl = nr - expenses;
        // Base period column (e.g. "Jan 2023")
        newNR[deptKey][monthYear] = nr;
        newBL[deptKey][monthYear] = bl;
        // Forecast period column (e.g. "Jan 2023 forecast") — mirrors base when no changes
        newNR[deptKey][`${monthYear} forecast`] = nr;
        newBL[deptKey][`${monthYear} forecast`] = bl;
        nrTotal += nr;
        blTotal += bl;
      });

      newNR[deptKey]['base total'] = nrTotal;
      newBL[deptKey]['base total'] = blTotal;
      newNR[deptKey]['forecast total'] = nrTotal;
      newBL[deptKey]['forecast total'] = blTotal;
    });

    nrDeptBaseValuesRef.current = newNR;
    blDeptBaseValuesRef.current = newBL;

    // Invalidate value caches so the grid picks up the new pre-computed values
    netRevenueCacheRef.current = {};
    bottomLineCacheRef.current = {};
  };

  /**
   * Clears the forecast-period entries from nrDeptBaseValuesRef / blDeptBaseValuesRef.
   * Called whenever a change is added, removed, or modified so the forecast columns
   * for NR/BL dept rows fall through to the live grid scan (which reflects changes)
   * rather than returning the stale pre-load base values.
   * Base-period entries are preserved — they never change with bridge columns.
   */
  const invalidateNRBLForecastCache = () => {
    const clearForecast = (ref) => {
      Object.values(ref.current).forEach(colMap => {
        Object.keys(colMap).forEach(key => {
          if (key.includes('forecast')) delete colMap[key];
        });
      });
    };
    clearForecast(nrDeptBaseValuesRef);
    clearForecast(blDeptBaseValuesRef);
  };

  /**
   * Helper function to get Net Revenue value for a specific column
   * Calculates Net Patient Revenue + Other Revenue for the given column
   * If department is specified, calculates only for that department
   * Respects filters - only aggregates visible/displayed nodes
   *
   * Results are cached per (columnId, department) key and invalidated on
   * filter changes to avoid scanning all rows on every cell render (O(N²)).
   */
  const getNetRevenueValue = (params, columnId) => {
    if (!params.api) return null;
    
    // Check if this is a department-level Net Revenue calculation.
    // Synthetic leaf rows have isNetRevenueDepartment=true and department=dept.
    // The visible level-2 department group node has allLeafChildren with isNetRevenueDepartment.
    // Guard to level === 2: the level-0 Net Revenue group also has allLeafChildren that are
    // all isNetRevenueDepartment, so without the level check it would incorrectly pick the
    // first dept's value instead of computing the grand total.
    let department = params.data?.isNetRevenueDepartment ? params.data.department : null;
    if (!department && params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
      if (params.node.allLeafChildren.every(n => n.data?.isNetRevenueDepartment)) {
        department = params.node.allLeafChildren[0].data.department;
      }
    }
    const cacheKey = department || '_total_';
    
    // Return cached value if available (cache is cleared on filter change)
    if (netRevenueCacheRef.current[columnId]?.[cacheKey] !== undefined) {
      return netRevenueCacheRef.current[columnId][cacheKey];
    }
    
    let result;
    
    if (department) {
      // Department-level: first check pre-computed values eagerly loaded at startup.
      // These are populated by loadNRBLDeptBaseValues() which calls the dept API for
      // all NPR/Other Revenue parent accounts — available before user expands anything.
      if (nrDeptBaseValuesRef.current[department]?.[columnId] !== undefined) {
        result = nrDeptBaseValuesRef.current[department][columnId];
        // Store in cache so subsequent renders hit the two-key object lookup, not the ref map.
        if (!netRevenueCacheRef.current[columnId]) netRevenueCacheRef.current[columnId] = {};
        netRevenueCacheRef.current[columnId][cacheKey] = result;
      } else {
        // Pre-computed forecast key was invalidated (changes applied) and leaf children
        // are not in the row model until their parent is expanded (lazy loading).
        // Fall back to the base period value as a stable placeholder; the correct
        // forecast value will show once the parent group is expanded and leaf nodes
        // are loaded into the row model, after which the leaf scan below will run.
        if (!netRevenueCacheRef.current[columnId]) {
          netRevenueCacheRef.current[columnId] = {};
        }

        // Try leaf scan first — works after parent expansion when leaf rows exist.
        const deptTotals = {};
        params.api.forEachNodeAfterFilterAndSort((node) => {
          if (node.group) return;
          const data = node.data;
          if (!data || data.isNetRevenueDepartment || data.isBottomLineDepartment) return;
          const section = data.acctTypeSection;
          if (section !== 'Net Patient Revenue' && section !== 'Other Revenue') return;
          const dept = data.department;
          if (!dept || dept === 'Total') return;
          if (!deptTotals[dept]) deptTotals[dept] = { netPatientRevenue: 0, otherRevenue: 0 };
          const value = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
          if (section === 'Net Patient Revenue') {
            const acctType = data.acctType;
            if (acctType === 'Deduction' || acctType === 'Ded') {
              deptTotals[dept].netPatientRevenue -= value;
            } else {
              deptTotals[dept].netPatientRevenue += value;
            }
          } else if (section === 'Other Revenue') {
            deptTotals[dept].otherRevenue += value;
          }
        });

        const foundDepts = Object.keys(deptTotals);
        if (foundDepts.length > 0) {
          // Leaf scan succeeded (parent was expanded) — cache all dept results.
          foundDepts.forEach(dept => {
            const t = deptTotals[dept];
            netRevenueCacheRef.current[columnId][dept] = t.netPatientRevenue + t.otherRevenue;
          });
          result = netRevenueCacheRef.current[columnId][department] ?? 0;
        } else {
          // Leaf rows not yet loaded — use base period value as stable placeholder.
          // Derive the base columnId: strip ' forecast' suffix or use 'base total' for totals.
          const baseColumnId = columnId === 'forecast total'
            ? 'base total'
            : columnId.replace(' forecast', '');
          result = nrDeptBaseValuesRef.current[department]?.[baseColumnId] ?? 0;
          // Do NOT cache this placeholder — let the next render retry the leaf scan.
        }
      }
    } else {
      // Top-level: sum from Net Patient Revenue and Other Revenue group nodes
      // These group nodes' aggregated values automatically respect filters
      let netPatientRevenueValue = 0;
      let otherRevenueValue = 0;
      
      // Use forEachNodeAfterFilterAndSort to get only visible nodes
      params.api.forEachNodeAfterFilterAndSort((node) => {
        // Only process level 0 group nodes that are displayed
        if (node.group && node.level === 0) {
          if (node.key === 'Net Patient Revenue') {
            netPatientRevenueValue = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
          } else if (node.key === 'Other Revenue') {
            otherRevenueValue = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
          }
        }
      });
      
      result = netPatientRevenueValue + otherRevenueValue;
    }
    
    // Store in cache
    if (!netRevenueCacheRef.current[columnId]) {
      netRevenueCacheRef.current[columnId] = {};
    }
    netRevenueCacheRef.current[columnId][cacheKey] = result;
    
    return result;
  };

  /**
   * Helper function to get Bottom Line value for a specific column
   * Calculates Net Revenue - Operating Expenses for the given column
   * If department is specified, calculates only for that department
   * Respects filters - only aggregates visible/displayed nodes
   *
   * Results are cached per (columnId, department) key and invalidated on
   * filter changes to avoid scanning all rows on every cell render (O(N²)).
   */
  const getBottomLineValue = (params, columnId) => {
    if (!params.api) return null;

    // Check if this is a department-level Bottom Line calculation.
    // Synthetic leaf rows have isBottomLineDepartment=true and department=dept.
    // The visible level-2 department group node has allLeafChildren with isBottomLineDepartment.
    // Guard to level === 2: the level-0 Bottom Line group also has allLeafChildren that are
    // all isBottomLineDepartment, so without the level check it would incorrectly pick the
    // first dept's value instead of computing the grand total.
    let department = params.data?.isBottomLineDepartment ? params.data.department : null;
    if (!department && params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
      if (params.node.allLeafChildren.every(n => n.data?.isBottomLineDepartment)) {
        department = params.node.allLeafChildren[0].data.department;
      }
    }
    const cacheKey = department || '_total_';

    // Return cached value if available (cache is cleared on filter change)
    if (bottomLineCacheRef.current[columnId]?.[cacheKey] !== undefined) {
      return bottomLineCacheRef.current[columnId][cacheKey];
    }
    
    if (!bottomLineCacheRef.current[columnId]) {
      bottomLineCacheRef.current[columnId] = {};
    }

    if (department) {
      // Department-level: first check pre-computed base values eagerly loaded at startup.
      if (blDeptBaseValuesRef.current[department]?.[columnId] !== undefined) {
        const result = blDeptBaseValuesRef.current[department][columnId];
        // Store in cache so subsequent renders hit the two-key object lookup, not the ref map.
        bottomLineCacheRef.current[columnId][cacheKey] = result;
        return result;
      }

      // Fallback: try leaf scan first (works after expansion), then fall back to
      // base period value as a stable placeholder when leaves aren't loaded yet.
      const deptTotals = {};

      params.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.group) return;
        const data = node.data;
        if (!data || data.isNetRevenueDepartment || data.isBottomLineDepartment) return;
        const section = data.acctTypeSection;
        if (section !== 'Net Patient Revenue' && section !== 'Other Revenue' && section !== 'Operating Expenses') return;
        const dept = data.department;
        if (!dept || dept === 'Total') return;
        if (!deptTotals[dept]) {
          deptTotals[dept] = { netPatientRevenue: 0, otherRevenue: 0, expenses: 0 };
        }
        const value = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
        if (section === 'Net Patient Revenue') {
          const acctType = data.acctType;
          if (acctType === 'Deduction' || acctType === 'Ded') {
            deptTotals[dept].netPatientRevenue -= value;
          } else {
            deptTotals[dept].netPatientRevenue += value;
          }
        } else if (section === 'Other Revenue') {
          deptTotals[dept].otherRevenue += value;
        } else if (section === 'Operating Expenses') {
          deptTotals[dept].expenses += value;
        }
      });

      const foundDepts = Object.keys(deptTotals);
      if (foundDepts.length > 0) {
        // Leaf scan succeeded — cache all dept results.
        foundDepts.forEach(dept => {
          const t = deptTotals[dept];
          const netRev = t.netPatientRevenue + t.otherRevenue;
          bottomLineCacheRef.current[columnId][dept] = netRev - t.expenses;
        });
        return bottomLineCacheRef.current[columnId][department] ?? 0;
      } else {
        // Leaf rows not yet loaded — use base period value as stable placeholder.
        const baseColumnId = columnId === 'forecast total'
          ? 'base total'
          : columnId.replace(' forecast', '');
        return blDeptBaseValuesRef.current[department]?.[baseColumnId] ?? 0;
      }
    } else {
      // Top-level: sum from Net Revenue and Operating Expenses group nodes
      let netRevenueValue = 0;
      let operatingExpensesValue = 0;

      // These group nodes' aggregated values automatically respect filters
      params.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.group && node.level === 0) {
          if (node.key === 'Net Revenue') {
            netRevenueValue = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
          } else if (node.key === 'Operating Expenses') {
            operatingExpensesValue = params.api.getCellValue({ colKey: columnId, rowNode: node }) || 0;
          }
        }
      });

      const result = netRevenueValue - operatingExpensesValue;
      bottomLineCacheRef.current[columnId]['_total_'] = result;
      return result;
    }
  };

  /**
   * Recursively collects leaf (non-group) nodes from childrenAfterFilter,
   * making the traversal filter-aware unlike allLeafChildren.
   */
  const getFilteredLeafChildren = (node) => {
    const leaves = [];
    if (!node.childrenAfterFilter) return leaves;
    node.childrenAfterFilter.forEach(child => {
      if (child.group) {
        leaves.push(...getFilteredLeafChildren(child));
      } else {
        leaves.push(child);
      }
    });
    return leaves;
  };

  /**
   * Custom aggregation function for Net Patient Revenue calculation
   * For "Net Patient Revenue" group: Revenue - Deductions (filter-aware)
   * For "Statistics" group: No aggregation (return null)
   * For all other groups: Sum
   */
  const netRevenueAggFunc = (params) => {
    // Check if this is the "Net Patient Revenue" section at level 0
    if (params.rowNode.level === 0 && params.rowNode.key === 'Net Patient Revenue') {
      let revenueSum = 0;
      let deductionSum = 0;
      
      // Use filter-aware childrenAfterFilter traversal instead of allLeafChildren
      if (params.api && params.column) {
        const filteredLeaves = getFilteredLeafChildren(params.rowNode);
        filteredLeaves.forEach(child => {
          // Use AG Grid API to get the computed value (handles valueGetters)
          const value = params.api.getCellValue({ colKey: params.column, rowNode: child });
          
          if (value !== undefined && value !== null && typeof value === 'number') {
            const acctType = child.data?.acctType;
            if (acctType === 'Revenue' || acctType === 'Rev') {
              revenueSum += value;
            } else if (acctType === 'Deduction' || acctType === 'Ded') {
              deductionSum += value;
            }
          }
        });
      }
      
      return revenueSum - deductionSum;
    }
    
    // Check if this is the "Statistics" section - don't aggregate
    if (params.rowNode.level === 0 && params.rowNode.key === 'Statistics') {
      return null;
    }
    
    // Check if this is the "Net Revenue" summary row - calculate from other groups
    if (params.rowNode.level === 0 && params.rowNode.key === 'Net Revenue') {
      if (!params.api || !params.column) {
        return null;
      }
      
      let netPatientRevenueValue = 0;
      let otherRevenueValue = 0;
      
      // Find and sum Net Patient Revenue and Other Revenue groups using only visible/filtered nodes
      params.api.forEachNodeAfterFilterAndSort((node) => {
        if (node.group && node.level === 0) {
          if (node.key === 'Net Patient Revenue') {
            netPatientRevenueValue = params.api.getCellValue({ colKey: params.column, rowNode: node }) || 0;
          } else if (node.key === 'Other Revenue') {
            otherRevenueValue = params.api.getCellValue({ colKey: params.column, rowNode: node }) || 0;
          }
        }
      });
      
      return netPatientRevenueValue + otherRevenueValue;
    }
    
    // For all other groups, use standard sum
    if (params.values && params.values.length > 0) {
      return params.values.reduce((sum, val) => sum + (val || 0), 0);
    }
    
    return null;
  };

  const buildColumns = (activeProjectInfo, activeProjectAttributes) => {
    let columns = [
      {
        aggFunc: 'sum',
        comparator: (valueA, valueB, nodeA, nodeB, isInverted) => {
          // Define the desired order of account type sections
          const sectionOrder = {
            'Net Patient Revenue': 1,
            'Other Revenue': 2,
            'Net Revenue': 2.5, // Summary row between Other Revenue and Operating Expenses
            'Operating Expenses': 3,
            'Bottom Line': 3.5, // Summary row between Operating Expenses and Statistics
            'Statistics': 4,
            'Calculated Accounts': 5,
            'Other': 99
          };
          
          // Get sort order from section names
          const orderA = sectionOrder[valueA] ?? 99;
          const orderB = sectionOrder[valueB] ?? 99;
          
          return orderA - orderB;
        },
        enableRowGroup: true,
        field: 'acctTypeSection',
        floatingFilter: false,
        headerName: 'Account Type',
        hide: true,
        resizable: true,
        rowGroup: true,
        rowGroupIndex: 0,
        sort: 'asc', // Sort using the custom comparator above
      },
      {
        aggFunc: 'sum',
        enableRowGroup: true,
        field: 'parentAccount',
        floatingFilter: true,
        headerName: 'Parent Account',
        hide: true,
        resizable: true,
        rowGroup: true,
        rowGroupIndex: 1,
        tooltipComponent: 'calculatedAccountTooltip',
        tooltipField: 'parentAccount',
      },
      {
        aggFunc: 'sum',
        enableRowGroup: true,
        field: 'accountName',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Account',
        hide: true,
        resizable: true,
        rowGroup: true,
        rowGroupIndex: 3,
        suppressColumnsToolPanel: true,
        tooltipComponent: 'calculatedAccountTooltip',
        tooltipValueGetter: (params) => {
          // Show tooltip for Calculated Account rows
          if (params.data?.isCalculatedAccount) {
            return 'show';
          }
          return null;
        },
      },
      {
        aggFunc: 'sum',
        enableRowGroup: true,
        field: 'department',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Department',
        hide: true,
        resizable: true,
        rowGroup: true,
        rowGroupIndex: 2,
      },
      {
        aggFunc: 'sum',
        children: [],
        headerName: 'Base Period',
        field: 'basePeriod',
        filter: true,
        marryChildren: true,
        openByDefault: false,
        resizable: true,
        type: 'numericRight',
        valueFormatter: currencyFormatter,
      },
    ];

    if (activeProjectInfo) {
      const startDate = formatBackendDate(activeProjectInfo.start_date);
      const endDate = formatBackendDate(activeProjectInfo.end_date);
      const months = getMonthsBetweenDates(startDate, endDate);

      const basePeriodMonths = months.map((month) => ({
        aggFunc: netRevenueAggFunc,
        columnGroupShow: 'open',
        field: month,
        headerName: month,
        resizable: true,
        type: 'numericRight',
        valueFormatter: currencyFormatter,
        valueGetter: (params) => {
          // Check for Net Revenue group node or department row
          if (params.node?.key === 'Net Revenue' || params.data?.isNetRevenueDepartment) {
            return getNetRevenueValue(params, month);
          }
          // Check for Bottom Line group node or department row
          if (params.node?.key === 'Bottom Line' || params.data?.isBottomLineDepartment) {
            return getBottomLineValue(params, month);
          }
          // Department group nodes (level 2) under Net Revenue / Bottom Line
          if (params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
            const leaves = params.node.allLeafChildren;
            if (leaves.every(n => n.data?.isNetRevenueDepartment)) return getNetRevenueValue(params, month);
            if (leaves.every(n => n.data?.isBottomLineDepartment)) return getBottomLineValue(params, month);
          }
          // Level-0 group header rows for all other sections (Operating Expenses, Net Patient Revenue, etc.)
          // aggFunc has already computed the correct aggregate — read it from aggData so the
          // header row shows the total rather than blank. Footer rows are suppressed separately.
          if (params.node?.group && params.node.level === 0 && !params.node.footer) {
            const agg = params.node.aggData?.[month];
            if (agg !== undefined && agg !== null) return agg;
          }
          return singleMonthGetter(params);
        },
      }));

      basePeriodMonths.push({
        aggFunc: netRevenueAggFunc,
        columnGroupShow: 'closed',
        field: 'base total',
        headerName: 'Total',
        resizable: true,
        type: 'numericRight',
        valueFormatter: currencyFormatter,
        valueGetter: (params) => {
          // Check for Net Revenue group node or department row
          if (params.node?.key === 'Net Revenue' || params.data?.isNetRevenueDepartment) {
            return getNetRevenueValue(params, 'base total');
          }
          // Check for Bottom Line group node or department row
          if (params.node?.key === 'Bottom Line' || params.data?.isBottomLineDepartment) {
            return getBottomLineValue(params, 'base total');
          }
          // Department group nodes under Net Revenue / Bottom Line
          if (params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
            const leaves = params.node.allLeafChildren;
            if (leaves.every(n => n.data?.isNetRevenueDepartment)) return getNetRevenueValue(params, 'base total');
            if (leaves.every(n => n.data?.isBottomLineDepartment)) return getBottomLineValue(params, 'base total');
          }
          // Level-0 group header rows for all other sections
          if (params.node?.group && params.node.level === 0 && !params.node.footer) {
            const agg = params.node.aggData?.['base total'];
            if (agg !== undefined && agg !== null) return agg;
          }
          return basePeriodTotal(params, effectiveDateRangeRef.current);
        },
      });

      const basePeriodIndex = columns.findIndex((col) => col.field === 'basePeriod');
      if (basePeriodIndex !== -1) {
        columns[basePeriodIndex].children = basePeriodMonths;
      }

      // If there are changes in the active project, create columns using shared helper
      if (context.activeProjectAttributes && context.activeProjectAttributes.changes?.length > 0) {
        const changeColumnGroups = processChangeColumns(
          activeProjectAttributes.changes,
          activeProjectAttributes,
          null,                  // disabledChangeIdsRef — not available in buildColumns useMemo
          effectiveDateRangeRef  // date range filter ref
        );
        insertChangeColumnGroups(columns, changeColumnGroups, activeProjectAttributes, effectiveDateRangeRef);
      }

      // Add Forecast Period column
      const forecastDates = getMonthsBetweenDates(startDate, endDate);

      const forecastPeriodMonths = forecastDates.map((month) => ({
        aggFunc: netRevenueAggFunc,
        columnGroupShow: 'open',
        field: `${month} forecast`,
        headerName: month,
        resizable: true,
        type: 'numericRight',
        valueFormatter: currencyFormatter,
        valueGetter: (params) => {
          // Check for Net Revenue group node or department row
          if (params.node?.key === 'Net Revenue' || params.data?.isNetRevenueDepartment) {
            return getNetRevenueValue(params, `${month} forecast`);
          }
          // Check for Bottom Line group node or department row
          if (params.node?.key === 'Bottom Line' || params.data?.isBottomLineDepartment) {
            return getBottomLineValue(params, `${month} forecast`);
          }
          // Department group nodes under Net Revenue / Bottom Line
          if (params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
            const leaves = params.node.allLeafChildren;
            if (leaves.every(n => n.data?.isNetRevenueDepartment)) return getNetRevenueValue(params, `${month} forecast`);
            if (leaves.every(n => n.data?.isBottomLineDepartment)) return getBottomLineValue(params, `${month} forecast`);
          }
          // Level-0 group header rows for all other sections
          if (params.node?.group && params.node.level === 0 && !params.node.footer) {
            const agg = params.node.aggData?.[`${month} forecast`];
            if (agg !== undefined && agg !== null) return agg;
          }
          const activeChanges = disabledChangeIdsRef.current.size
            ? changes.filter((c) => !disabledChangeIdsRef.current.has(c.id))
            : changes;
          return forecastPeriodMonth(month, params, activeChanges);
        },
      }));

      forecastPeriodMonths.push({
        aggFunc: netRevenueAggFunc,
        columnGroupShow: 'closed',
        field: 'forecast total',
        headerName: 'Total',
        pinned: 'right',
        resizable: true,
        type: 'numericRight',
        valueFormatter: currencyFormatter,
        valueGetter: (params) => {
          // Check for Net Revenue group node or department row
          if (params.node?.key === 'Net Revenue' || params.data?.isNetRevenueDepartment) {
            return getNetRevenueValue(params, 'forecast total');
          }
          // Check for Bottom Line group node or department row
          if (params.node?.key === 'Bottom Line' || params.data?.isBottomLineDepartment) {
            return getBottomLineValue(params, 'forecast total');
          }
          // Department group nodes under Net Revenue / Bottom Line
          if (params.node?.group && params.node.level === 2 && params.node.allLeafChildren?.length > 0) {
            const leaves = params.node.allLeafChildren;
            if (leaves.every(n => n.data?.isNetRevenueDepartment)) return getNetRevenueValue(params, 'forecast total');
            if (leaves.every(n => n.data?.isBottomLineDepartment)) return getBottomLineValue(params, 'forecast total');
          }
          // Level-0 group header rows for all other sections
          if (params.node?.group && params.node.level === 0 && !params.node.footer) {
            const agg = params.node.aggData?.['forecast total'];
            if (agg !== undefined && agg !== null) return agg;
          }
          const activeChanges = disabledChangeIdsRef.current.size
            ? changes.filter((c) => !disabledChangeIdsRef.current.has(c.id))
            : changes;
          return forecastPeriodTotal(params, activeChanges, effectiveDateRangeRef.current);
        },
      });

      const forecastColumn = {
        children: forecastPeriodMonths,
        field: 'forecastPeriod',
        filter: true,
        groupId: 'forecastPeriod',
        headerName: 'Forecast Period',
        marryChildren: true,
        openByDefault: false,
        resizable: true,
        type: 'numericRight',
      };

      columns.push(forecastColumn);
    }
    
    return columns;
  };

  // const [columns, setColumns] = useState(buildColumns(activeProjectInfo, activeProjectAttributes));

  // Use more specific dependencies to avoid unnecessary column rebuilds
  // Create stable memoized values for dates and change IDs
  const projectStartDate = useMemo(() => activeProjectInfo?.start_date, [activeProjectInfo?.start_date]);
  const projectEndDate = useMemo(() => activeProjectInfo?.end_date, [activeProjectInfo?.end_date]);

  // ── Date range filter state ───────────────────────────────────────────────
  // Converts a "yyyy-MM-dd" backend date to { year, month } for react-month-picker
  const toPickerDate = useCallback((dateStr) => {
    if (!dateStr) return null;
    const [y, m] = dateStr.split('-').map(Number);
    return { year: y, month: m };
  }, []);

  const monthPickerRange = useMemo(() => {
    const min = toPickerDate(projectStartDate);
    const max = toPickerDate(projectEndDate);
    if (!min || !max) return null;
    return { min, max };
  }, [projectStartDate, projectEndDate, toPickerDate]);

  // Derive the effective picker value: filtered range or full project range
  const effectiveDateRange = useMemo(() => {
    if (activeFilters.dateRange) return activeFilters.dateRange;
    if (monthPickerRange) return { from: monthPickerRange.min, to: monthPickerRange.max };
    return null;
  }, [activeFilters.dateRange, monthPickerRange]);

  // Keep a ref so valueGetters can read the current filter without closing over stale state
  const effectiveDateRangeRef = useRef(null);
  useLayoutEffect(() => {
    effectiveDateRangeRef.current = effectiveDateRange;
  }, [effectiveDateRange]);

  // Persist activeFilters to localStorage whenever they change, keyed by project ID.
  // Also records this as the last active project so applyInitialFilters can distinguish
  // "reloading the same project" (restore last-used filters) from "switching to a new
  // project" (apply default saved view if one exists).
  // Skipped when there's no active project (e.g. initial mount before project loads).
  // Skipped during the async restore window (filtersRestoredRef = false) to prevent the
  // synchronous empty-reset from overwriting the persisted filters before loadFilters runs.
  useEffect(() => {
    if (!activeProjectInfo?.id) return;
    if (!filtersRestoredRef.current) return;
    saveFilters(activeProjectInfo.id, activeFilters);
    setLastActiveProjectId(activeProjectInfo.id);
  }, [activeFilters, activeProjectInfo?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: lastActiveProjectId is written synchronously at the top of the
  // project-switch useEffect (above), before applyInitialFilters reads it.
  // The pulse is triggered inside applyInitialFilters after setActiveFilters,
  // so it reads the actual restored value rather than a stale closure.

  // ── Saved views: serialise activeFilters (Sets → arrays) for backend ─
  const serialiseFilters = (filters) => {
    const SET_KEYS = new Set(['entityIds', 'deptIds', 'disabledChangeIds']);
    const out = {};
    Object.entries(filters).forEach(([k, v]) => {
      out[k] = SET_KEYS.has(k) ? [...v] : v;
    });
    // Embed metadata so staleness can be detected on load:
    //   _projectId — the project this view was saved against. If a view is applied
    //   to a different project, the dept/entity trim effects (Gap A) will clean up
    //   stale IDs automatically, but having the projectId lets us detect it explicitly.
    //   _schemaVersion — increment if the filter_state shape changes so old stored
    //   values can be migrated or discarded rather than causing silent wrong behaviour.
    out._projectId     = activeProjectInfo?.id ?? null;
    out._schemaVersion = 1;
    return out;
  };

  // ── Saved views: deserialise backend filter_state → activeFilters ────
  const deserialiseFilterState = (filterState) => {
    const empty = makeEmptyFilters();
    if (!filterState || typeof filterState !== 'object') return empty;
    // Warn when applying a view that was saved for a different project. The trim
    // effects added for Gap A will remove any stale dept/entity IDs automatically,
    // but a console warning helps during debugging and future observability work.
    if (
      filterState._projectId != null &&
      activeProjectInfo?.id != null &&
      String(filterState._projectId) !== String(activeProjectInfo.id)
    ) {
      console.warn(
        `[deserialiseFilterState] Saved view was created for project ${filterState._projectId}` +
        ` but is being applied to project ${activeProjectInfo?.id}.` +
        ` Stale dept/entity IDs will be trimmed automatically.`
      );
    }
    return {
      entityIds:         Array.isArray(filterState.entityIds)         ? new Set(filterState.entityIds)         : new Set(),
      deptIds:           Array.isArray(filterState.deptIds)           ? new Set(filterState.deptIds)           : new Set(),
      dateRange:         filterState.dateRange ?? null,
      disabledChangeIds: Array.isArray(filterState.disabledChangeIds) ? new Set(filterState.disabledChangeIds) : new Set(),
    };
  };

  const handleSaveView = async (name, isDefault) => {
    if (!activeProjectInfo?.id || !activeUser?.id || !activeUser?.accessToken) return;
    setSavedViewsSaving(true);
    try {
      const res = await createSavedView(
        activeProjectInfo.id,
        activeUser.id,
        null, // organizationId — unused by API, kept for signature compatibility
        name,
        serialiseFilters(activeFilters),
        isDefault,
        activeUser.accessToken,
      );
      if (res?.data) {
        setSavedViews(prev => {
          // If new view is default, clear existing defaults in local state
          const updated = isDefault
            ? prev.map(v => ({ ...v, is_default: false }))
            : [...prev];
          return [...updated, res.data].sort((a, b) => a.name.localeCompare(b.name));
        });
      }
    } finally {
      setSavedViewsSaving(false);
    }
  };

  const handleApplySavedView = (view) => {
    // Kept for potential future use (e.g. applying a view programmatically
    // from outside the drawer, such as a default view on project load).
    const restored = deserialiseFilterState(view.filter_state);
    setActiveFilters(restored);
  };

  const handleSetDefaultView = async (view) => {
    if (!activeUser?.accessToken) return;
    const newDefault = !view.is_default;
    setSavedViewsSaving(true);
    try {
      const res = await updateSavedView(view.id, { is_default: newDefault }, activeUser.accessToken);
      if (res?.data) {
        setSavedViews(prev =>
          prev.map(v => ({
            ...v,
            is_default: v.id === view.id ? newDefault : (newDefault ? false : v.is_default),
          }))
        );
      }
    } finally {
      setSavedViewsSaving(false);
    }
  };

  const handleDeleteSavedView = async (view) => {
    if (!activeUser?.accessToken) return;
    setSavedViewsSaving(true);
    try {
      await deleteSavedView(view.id, activeUser.accessToken);
      setSavedViews(prev => prev.filter(v => v.id !== view.id));
    } finally {
      setSavedViewsSaving(false);
    }
  };

  const handleRenameSavedView = async (view, newName) => {
    if (!activeUser?.accessToken) return;
    setSavedViewsSaving(true);
    try {
      const res = await updateSavedView(view.id, { name: newName }, activeUser.accessToken);
      if (res?.data) {
        setSavedViews(prev =>
          prev.map(v => v.id === view.id ? { ...v, name: newName } : v)
              .sort((a, b) => a.name.localeCompare(b.name))
        );
      } else {
        // API returned a non-data response (4xx/5xx) — throw so the edit field stays open
        throw new Error(res?.response?.data?.message || 'Failed to rename view');
      }
    } finally {
      setSavedViewsSaving(false);
    }
  };

  // When the project changes, reset the date range filter
  // (handled in the project-change useEffect below via setActiveFilters)

  const changesIds = useMemo(
    () => activeProjectAttributes?.changes?.map(c => c.id).join(',') || '',
    [activeProjectAttributes?.changes]
  );

  // Trim stale disabledChangeIds whenever the change list updates (e.g. a change
  // was deleted while filters were persisted). Prevents inflated badge counts.
  useEffect(() => {
    if (!activeProjectAttributes?.changes) return;
    const liveIds = new Set(activeProjectAttributes.changes.map(c => c.id));
    setActiveFilters(prev => {
      if (prev.disabledChangeIds.size === 0) return prev;
      const trimmed = new Set([...prev.disabledChangeIds].filter(id => liveIds.has(id)));
      if (trimmed.size === prev.disabledChangeIds.size) return prev; // nothing to trim
      return { ...prev, disabledChangeIds: trimmed };
    });
  }, [changesIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Trim stale deptIds and entityIds whenever the project's department/entity list
  // changes (e.g. after a GL key bulk edit adds or removes departments, or on project
  // switch where the saved filter referenced departments that don't exist in the new
  // project). Mirrors the disabledChangeIds trim above.
  useEffect(() => {
    if (!activeProjectAttributes?.departments) return;
    const liveDeptKeys = new Set(
      (activeProjectAttributes.departments || []).map(d => `${d.departmentId} - ${d.departmentName}`)
    );
    setActiveFilters(prev => {
      if (prev.deptIds.size === 0) return prev;
      const trimmed = new Set([...prev.deptIds].filter(k => liveDeptKeys.has(k)));
      if (trimmed.size === prev.deptIds.size) return prev;
      return { ...prev, deptIds: trimmed };
    });
  }, [activeProjectAttributes?.departments]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activeProjectAttributes?.entities) return;
    const liveEntityIds = new Set(
      (activeProjectAttributes.entities || []).map(e => String(e.entity_id))
    );
    setActiveFilters(prev => {
      if (prev.entityIds.size === 0) return prev;
      const trimmed = new Set([...prev.entityIds].filter(id => liveEntityIds.has(id)));
      if (trimmed.size === prev.entityIds.size) return prev;
      return { ...prev, entityIds: trimmed };
    });
  }, [activeProjectAttributes?.entities]); // eslint-disable-line react-hooks/exhaustive-deps

  const parentChangesIds = useMemo(
    () => activeProjectAttributes?.parent_changes?.map(pc => pc.label).join(',') || '',
    [activeProjectAttributes?.parent_changes]
  );

  // Memoized list of department strings used for Net Revenue summary rows.
  // Format: "<departmentId> - <departmentName>" for each department in the project.
  const departmentsList = useMemo(
    () => (activeProjectAttributes?.departments || [])
      .map(dept => `${dept.departmentId} - ${dept.departmentName}`)
      .filter(Boolean),
    [activeProjectAttributes?.departments]
  );

  // Keep a ref that always points to the latest departmentsList so async callbacks
  // that captured a stale closure can still read the current dept list.
  const departmentsListRef = useRef([]);
  useEffect(() => { departmentsListRef.current = departmentsList; }, [departmentsList]);

  const columnDefs = useMemo(() => {
    // Only rebuild if we have the necessary data
    // Skip building columns until projectInfo is loaded
    // This prevents the grid from initializing with empty columns and triggering getRows prematurely
    if (!activeProjectInfo || !projectStartDate || !projectEndDate) {
      return [];
    }

    if (!activeProjectAttributes) {
      return [];
    }

    return buildColumns(activeProjectInfo, activeProjectAttributes)
  }, [projectStartDate, projectEndDate, changesIds, parentChangesIds])

  const columnTypes = {
    numericRight: {
      cellClass: 'ag-right-aligned-cell',
    },
    change: {
      cellClass: 'ag-right-aligned-cell',
      filter: true,
      resizable: true,
      sortable: true,
      valueFormatter: currencyFormatter,
    },
    changeTotal: {
      // Define the properties for 'changeTotal'
      cellClass: 'ag-right-aligned-cell',
      filter: true,
      resizable: true,
      sortable: true,
      valueFormatter: currencyFormatter,
    },
  };

  /**
   * After all standard changes have been applied, recalculate dominant change column
   * values so they reflect the correct adjusted delta (absorbing standard changes).
   * Also triggers a grid refresh so parent row aggregations update.
   *
   * Must be called after buildNewChange has finished applying standard changes,
   * and again whenever any standard change is added, edited, or deleted.
   */
  const calculateDominantChanges = () => {
    const allChanges = activeProjectAttributes.changes || [];
    const dominantChanges = allChanges.filter(c => c.is_dominant_change);

    if (dominantChanges.length === 0) return;

    const standardChanges = allChanges.filter(c => !c.is_dominant_change);

    dominantChanges.forEach((change) => {
      const dates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) || [];

      change.selected_accounts.forEach((glKey) => {
        const row = gridRef.current?.api?.getRowNode(glKey);
        if (!row) return;

        const rowData = cloneDeep(row.data);

        // Build the standard delta per month for this row so getDominantChange
        // can compute the correct adjusted value.
        const standardDeltaByMonth = {};
        dates.forEach((month) => {
          const monthDate = parse(month, 'MMM yyyy', new Date());
          let standardTotal = 0;
          standardChanges.forEach((sc) => {
            const scStart = parse(sc.date_range[0], 'yyyy-MM-dd', new Date());
            const scEnd = parse(sc.date_range[1], 'yyyy-MM-dd', new Date());
            if (monthDate >= scStart && monthDate <= scEnd) {
              const delta = getSingleMonthChange(month, { data: rowData }, sc);
              if (delta !== '') standardTotal += delta;
            }
          });
          standardDeltaByMonth[month] = standardTotal;
        });

        const dominantChangeCalcs = getDominantChange(rowData, change, standardDeltaByMonth);

        dates.forEach((month) => {
          rowData[`${change.id} ${month}`] = dominantChangeCalcs[month];
        });

        const changeIndex = (rowData.changes || []).findIndex(c => c === change.id);
        if (changeIndex === -1) {
          rowData.changes = [...(rowData.changes || []), change.id];
        }

        row.updateData(rowData);
      });
    });

    // Refresh cells and re-aggregate parent rows
    if (gridRef.current?.api) {
      gridRef.current.api.refreshClientSideRowModel('aggregate');
      gridRef.current.api.refreshCells({ force: true });
    }
  }

  /**
   * Recalculates dominant change adjusted values on parent/department rows that are
   * currently in the grid. Called after buildNewChange so that when a new standard
   * change is added that overlaps with a dominant change, the parent rows immediately
   * reflect the updated dominant adjusted values without requiring a page reload.
   *
   * Parent rows store values under ${change_label} ${month} (raw entry delta) and
   * ${change.id} ${month} (adjusted delta). We recompute the adjusted delta using
   * the current standard change totals already stored on each row.
   */
  const recalculateDominantOnParentRows = () => {
    const allChanges = activeProjectAttributes.changes || [];
    const dominantChanges = allChanges.filter(c => c.is_dominant_change);
    if (dominantChanges.length === 0) return;

    const standardChanges = allChanges.filter(c => !c.is_dominant_change);

    // Walk all grid rows and update hasChildren rows that have dominant change data
    gridRef.current?.api?.forEachNode(node => {
      if (!node.data?.hasChildren) return;
      const rowData = { ...node.data };
      let updated = false;

      dominantChanges.forEach(domChange => {
        const dates = getMonthsBetweenDates(domChange.date_range[0], domChange.date_range[1]);
        if (!dates) return;

        // Check if this row has any entry delta for this dominant change
        const sampleMonth = dates[0];
        const entryDeltaSample = rowData[`${domChange.change_label} ${sampleMonth}`];
        if (entryDeltaSample === undefined || entryDeltaSample === null) return;

        let adjustedTotal = 0;
        dates.forEach(month => {
          const entryDelta = Number(rowData[`${domChange.change_label} ${month}`] || 0);

          // Sum current standard deltas stored on this row.
          // Note: reads label-keyed pre-stored values (${sc.change_label} ${month}).
          // This covers percentage/absoluteValue changes. Reference account changes
          // store values differently and are unlikely to coexist with dominant changes
          // on the same GL keys, so this is acceptable for now.
          let standardDelta = 0;
          standardChanges.forEach(sc => {
            const storedVal = rowData[`${sc.change_label} ${month}`];
            if (storedVal !== undefined && storedVal !== null) {
              standardDelta += Number(storedVal);
            }
          });

          const adjustedDelta = entryDelta - standardDelta;
          rowData[`${domChange.id} ${month}`] = adjustedDelta;
          adjustedTotal += adjustedDelta;
        });

        rowData[`${domChange.id} total`] = adjustedTotal;
        updated = true;
      });

      if (updated) {
        node.setData(rowData);
      }
    });

    // Refresh so the updated values render
    if (gridRef.current?.api) {
      gridRef.current.api.refreshCells({ force: true });
    }
  }

  /**
   * Stamps pre-computed change column values onto all hasChildren placeholder rows
   * that are currently in the grid. Called both at initial load (inline) and whenever
   * a new change is added via buildNewChange, so that collapsed parent rows always
   * reflect the full set of changes without requiring the user to expand the group.
   *
   * This mirrors the same logic used during the initial grid construction loop
   * (getProjectParentAccountsTotals processing) but operates on live grid nodes
   * rather than raw row objects, so it can be called at any time after the grid
   * is ready.
   *
   * @param {Array} changes - Full set of changes to apply (activeProjectAttributes.changes)
   */
  const refreshPlaceholderRowChangeValues = (changes) => {
    if (!gridRef.current?.api || !changes || changes.length === 0) return;

    const standardChanges = changes.filter(c => !c.is_dominant_change);
    const dominantChanges = changes.filter(c => c.is_dominant_change);

    gridRef.current.api.forEachNode(node => {
      // Only target hasChildren placeholder rows — leaf rows and department-level
      // placeholder rows (id contains ':') compute their own values elsewhere.
      if (!node.data?.hasChildren) return;
      if (String(node.data?.id || '').includes(':')) return;

      const rowData = { ...node.data };
      const childAccountsSet = new Set(rowData.child_accounts || []);

      // ── Standard changes (percentage / absoluteValue / pro forma) ───────────
      standardChanges.forEach(change => {
        const { change_label: changeLabel, function_operator: op,
                operator_value: opVal, date_range: dateRange } = change;

        // Pro forma changes: sum monthly_values for any reference account that is
        // a child of this placeholder row.
        if (change.reference_accounts && Array.isArray(change.reference_accounts)) {
          const monthlyTotals = {};
          change.reference_accounts.forEach(refAcct => {
            if (!childAccountsSet.has(refAcct.glKey)) return;
            (refAcct.monthly_values || []).forEach(mv => {
              monthlyTotals[mv.month_year] = (monthlyTotals[mv.month_year] || 0) + (mv.value || 0);
            });
          });
          Object.keys(monthlyTotals).forEach(monthYear => {
            rowData[`${changeLabel} ${monthYear}`] = monthlyTotals[monthYear];
          });
          const proFormaTotal = Object.values(monthlyTotals).reduce((s, v) => s + v, 0);
          if (proFormaTotal !== 0) rowData[`${changeLabel} total`] = proFormaTotal;
        }

        // Percentage / absolute changes
        if ((op === 'percentage' || op === 'absoluteValue') && dateRange) {
          const selectedInParent = (change.selected_accounts || []).filter(
            glKey => childAccountsSet.has(glKey)
          );
          if (selectedInParent.length === 0) return;

          const dateList = getMonthsBetweenDates(dateRange[0], dateRange[1]) || [];
          let monthlyTotal = 0;
          dateList.forEach(monthYear => {
            const baseVal = Number(rowData[monthYear]) || 0;
            let delta = 0;
            if (op === 'percentage') {
              delta = baseVal * ((Number(opVal) || 0) / 100);
            } else if (op === 'absoluteValue') {
              delta = (Number(opVal) || 0) * selectedInParent.length;
            }
            if (delta !== 0) {
              rowData[`${changeLabel} ${monthYear}`] = delta;
              monthlyTotal += delta;
            }
          });
          if (monthlyTotal !== 0) rowData[`${changeLabel} total`] = monthlyTotal;
        }
      });

      // ── Stat driver changes ──────────────────────────────────────────────────
      // For stat driver changes, recalculateDrivenAccountsFromStatChanges already
      // wrote the per-driven-account values into eagerLoadedRowDataRef. We sum
      // those cached values across any driven accounts that are children of this
      // parent placeholder and stamp the aggregate onto the parent row.
      // Using child_accounts (not accountsMetadata) avoids a race condition
      // where accountsMetadata may not yet be loaded when this runs.
      standardChanges.forEach(change => {
        if (!change.affects_stat_drivers || !change.affected_stat_drivers) return;
        const { change_label: changeLabel, date_range: dateRange } = change;
        if (!dateRange) return;

        // Collect driven account GL keys that belong to this parent placeholder
        const drivenGlKeysInParent = [];
        change.affected_stat_drivers.forEach(driver => {
          (driver.drivenAccounts || []).forEach(a => {
            const glKey = `${a.entity_id}-${a.department_id}-${a.account_id}`;
            if (childAccountsSet.has(glKey)) drivenGlKeysInParent.push(glKey);
          });
        });
        if (drivenGlKeysInParent.length === 0) return;

        // Sum pre-computed values from the eager cache for each month in the change range
        const dateList = getMonthsBetweenDates(dateRange[0], dateRange[1]) || [];
        let monthlyTotal = 0;
        dateList.forEach(monthYear => {
          let monthSum = 0;
          drivenGlKeysInParent.forEach(glKey => {
            const eagerRow = eagerLoadedRowDataRef.current.get(glKey);
            if (eagerRow) monthSum += Number(eagerRow[`${changeLabel} ${monthYear}`]) || 0;
          });
          if (monthSum !== 0) {
            rowData[`${changeLabel} ${monthYear}`] = monthSum;
            monthlyTotal += monthSum;
          }
        });
        if (monthlyTotal !== 0) rowData[`${changeLabel} total`] = monthlyTotal;
      });

      // ── Dominant changes ─────────────────────────────────────────────────────
      dominantChanges.forEach(domChange => {
        const { function_operator: domOp, operator_value: domOpVal,
                date_range: domDateRange, change_label: domLabel } = domChange;
        if (!domDateRange || (domOp !== 'percentage' && domOp !== 'absoluteValue')) return;

        const selectedInParent = (domChange.selected_accounts || []).filter(
          glKey => childAccountsSet.has(glKey)
        );
        if (selectedInParent.length === 0) return;

        const dateList = getMonthsBetweenDates(domDateRange[0], domDateRange[1]) || [];
        let adjustedTotal = 0;

        dateList.forEach(monthYear => {
          const baseVal = Number(rowData[monthYear]) || 0;
          let entryDelta = 0;
          if (domOp === 'percentage') {
            entryDelta = baseVal * ((Number(domOpVal) || 0) / 100);
          } else if (domOp === 'absoluteValue') {
            entryDelta = (Number(domOpVal) || 0) * selectedInParent.length;
          }
          // Standard delta already stamped above for this month
          let standardDelta = 0;
          standardChanges.forEach(sc => {
            const storedVal = rowData[`${sc.change_label} ${monthYear}`];
            if (storedVal !== undefined && storedVal !== null) {
              standardDelta += Number(storedVal);
            }
          });
          const adjustedDelta = entryDelta - standardDelta;
          rowData[`${domChange.id} ${monthYear}`] = adjustedDelta;
          adjustedTotal += adjustedDelta;
        });

        rowData[`${domChange.id} total`] = adjustedTotal;
      });

      node.setData(rowData);
    });

    // Trigger a targeted re-aggregate so group nodes pick up the refreshed leaf values
    if (gridRef.current?.api) {
      gridRef.current.api.refreshClientSideRowModel('aggregate');
      gridRef.current.api.refreshCells({ force: true });
    }
  };

  const updateColumnsWithNewChange = async (changes, activeProjectAttributes) => {
    if (!changes || changes.length === 0) {
      return;
    }

    if (!gridOrchestratorRef.current?.isGridReady()) {
      console.warn('[updateColumnsWithNewChange] Grid API not available yet, skipping column update');
      return;
    }

    try {
      const columnDefs = await gridOrchestratorRef.current.getColumnDefs();
      const columns = cloneDeep(columnDefs);

      // Check if change columns are already present (regardless of order).
      // Compare by change ID (groupId) so user-reordered columns don't trigger
      // a spurious remove+re-insert blink.
      const changeIds = changes
        .filter(c => c.function_operator !== 'calculatedAccount' && c.function_operator !== 'recursiveCalculatedAccount')
        .map(c => String(c.id));

      // Collect all change IDs already represented in the column tree
      // (top-level groups and children of parent-change wrapper groups)
      const existingChangeIdSet = new Set();
      columns.forEach((col) => {
        if (col.groupId && changeIds.includes(col.groupId)) {
          existingChangeIdSet.add(col.groupId);
        }
        if (col.children) {
          col.children.forEach((child) => {
            if (child.groupId && changeIds.includes(child.groupId)) {
              existingChangeIdSet.add(child.groupId);
            }
          });
        }
      });

      const changeIdSet = new Set(changeIds);
      const alreadyCorrect =
        changeIdSet.size === existingChangeIdSet.size &&
        [...changeIdSet].every(id => existingChangeIdSet.has(id));

      if (!alreadyCorrect) {
        // Remove old change columns using shared helper
        removeOldChangeColumns(columns, changes);

        // Process change columns using shared helper (automatically filters calculated accounts)
        const changeColumnGroups = processChangeColumns(changes, activeProjectAttributes, disabledChangeIdsRef, effectiveDateRangeRef);
        
        // Insert change columns using shared helper
        insertChangeColumnGroups(columns, changeColumnGroups, activeProjectAttributes, effectiveDateRangeRef);
      }

      // Always rebuild the forecast column — its valueGetter closure captures the
      // changes array, so it must be refreshed whenever changes are added/edited/deleted
      // even if the change columns themselves haven't changed.
      rebuildForecastColumn(columns, changes, activeProjectInfo, disabledChangeIdsRef, effectiveDateRangeRef);

      // Update grid columns
      if (gridOrchestratorRef.current) {
        await gridOrchestratorRef.current.setColumnDefs(columns, { delay: 0 });
      }
    } catch (error) {
      // Silently fail - errors are handled by orchestrator
    }
  };


  /**
   * Applies Master Stat changes by distributing to child stats
   * @param {Object} change - Change data with Master Stat flag and dates object
   * @param {Object} activeProjectAttributes - Project attributes including masterStats
   */
  const applyMasterStatChanges = async (change, activeProjectAttributes) => {
    if (!change.affected_master_stats || change.affected_master_stats.length === 0) {
      return;
    }
    
    // Use the pre-calculated dates object if available
    if (change.dates && Object.keys(change.dates).length > 0) {
      
      // Apply changes to each child stat from the dates object
      for (const [childGlKey, monthValues] of Object.entries(change.dates)) {
        const childNode = await gridOrchestratorRef.current.getRowNode(childGlKey).catch(() => null);
        
        if (childNode && childNode.data) {
          const rowData = { ...childNode.data };
          
          // Apply each month's value
          Object.entries(monthValues).forEach(([month, changeAmount]) => {
            const changeColumnField = `${change.change_label} ${month}`;
            rowData[changeColumnField] = changeAmount;
          });
          
          // Track this change in the row's changes array
          if (!rowData.changes) {
            rowData.changes = [];
          }
          if (!rowData.changes.includes(change.id)) {
            rowData.changes.push(change.id);
          }
          
          // Update the row
          childNode.setData(rowData);
        }
      }
      
      // Update Master Stat rows with the sum of child changes
      for (const masterStat of change.affected_master_stats) {
        const masterStatNode = await gridOrchestratorRef.current.getRowNode(masterStat.gl_key).catch(() => null);
        
        if (masterStatNode && masterStatNode.data) {
          const masterRowData = { ...masterStatNode.data };
          
          // Calculate Master Stat's change values (sum of child changes)
          const dateRange = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) || [];
          
          dateRange.forEach(month => {
            let totalChange = 0;
            
            // Sum up all child stat changes for this month
            masterStat.child_stats.forEach(childGlKey => {
              if (change.dates[childGlKey] && change.dates[childGlKey][month] !== undefined) {
                totalChange += change.dates[childGlKey][month];
              }
            });
            
            const changeColumnField = `${change.change_label} ${month}`;
            masterRowData[changeColumnField] = totalChange;
          });
          
          // Track this change
          if (!masterRowData.changes) {
            masterRowData.changes = [];
          }
          if (!masterRowData.changes.includes(change.id)) {
            masterRowData.changes.push(change.id);
          }
          
          // Update Master Stat row
          masterStatNode.setData(masterRowData);
        }
      }
    } else {
      // Fallback: recalculate if dates object is not available (legacy behavior)
      console.warn('[applyMasterStatChanges] No dates object found, recalculating (legacy mode)');
      // console.log('[applyMasterStatChanges] change:', change);
      // console.log('[applyMasterStatChanges] affected_master_stats:', change.affected_master_stats);
      
      // Get current grid rows using orchestrator
      const gridRows = [];
      await gridOrchestratorRef.current.forEachNode(node => {
        if (node.data) {
          gridRows.push(node.data);
        }
      }).catch(error => {
        console.error('[applyMasterStatChanges] Failed to get grid rows:', error);
      });
      
      // console.log('[applyMasterStatChanges] Total grid rows:', gridRows.length);
      
      // Process each affected Master Stat
      for (const masterStat of change.affected_master_stats) {
        // console.log('[applyMasterStatChanges] Processing Master Stat:', masterStat.gl_key);
        
        // Calculate adjustments for all months
        const monthlyAdjustments = calculateMasterStatChangeAllMonths(
          change,
          masterStat,
          gridRows
        );
        
        // console.log('[applyMasterStatChanges] monthlyAdjustments:', monthlyAdjustments);
        // console.log('[applyMasterStatChanges] monthlyAdjustments keys:', Object.keys(monthlyAdjustments));
        
        // Apply adjustments to child stat rows
        for (const month of Object.keys(monthlyAdjustments)) {
          // console.log('[applyMasterStatChanges] Processing month:', month);
          const adjustments = monthlyAdjustments[month];
          // console.log('[applyMasterStatChanges] Adjustments for', month, ':', adjustments);
          
          for (const adjustment of adjustments) {
            // console.log('[applyMasterStatChanges] Processing adjustment for:', adjustment.gl_key);
            const childNode = await gridOrchestratorRef.current.getRowNode(adjustment.gl_key).catch(() => null);
            
            if (childNode && childNode.data) {
              const rowData = { ...childNode.data };
              
              // Store the change amount in the change column field
              const changeColumnField = `${change.change_label} ${month}`;
              rowData[changeColumnField] = adjustment.change_amount;
              
              // console.log(`[Master Stat Legacy] Setting ${adjustment.gl_key} field "${changeColumnField}" = ${adjustment.change_amount}`);
              
              // Track this change in the row's changes array
              if (!rowData.changes) {
                rowData.changes = [];
              }
              if (!rowData.changes.includes(change.id)) {
                rowData.changes.push(change.id);
              }
              
              // Update the row
              childNode.setData(rowData);
            } else {
              console.warn('[applyMasterStatChanges] Could not find node for:', adjustment.gl_key);
            }
          }
        }
        
        // Update Master Stat row itself
        const masterStatNode = await gridOrchestratorRef.current.getRowNode(masterStat.gl_key).catch(() => null);
        
        if (masterStatNode && masterStatNode.data) {
          const masterRowData = { ...masterStatNode.data };
          
          // Calculate Master Stat's change values (sum of child changes)
          const dateRange = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) || [];
          
          dateRange.forEach(month => {
            const adjustments = monthlyAdjustments[month] || [];
            const totalChange = adjustments.reduce((sum, adj) => sum + adj.change_amount, 0);
            
            const changeColumnField = `${change.change_label} ${month}`;
            masterRowData[changeColumnField] = totalChange;
          });
          
          // Track this change
          if (!masterRowData.changes) {
            masterRowData.changes = [];
          }
          if (!masterRowData.changes.includes(change.id)) {
            masterRowData.changes.push(change.id);
          }
          
          // Update Master Stat row
          masterStatNode.setData(masterRowData);
        }
      }
    }
  };


  /**
   * Recalculates Master Stat rows when their child stats have been changed
   * @param {Object} change - The change being applied
   * @param {Object} activeProjectAttributes - Active project attributes
   */
  const recalculateMasterStatsFromChildren = async (change, activeProjectAttributes) => {
    if (!change.affects_master_stats_via_children || !change.affected_master_stats_by_children) {
      return;
    }
    
    if (!gridOrchestratorRef.current?.isGridReady()) {
      console.warn('Grid API not available for recalculateMasterStatsFromChildren');
      return;
    }
    
    // Get date range for the change
    const dateRange = getMonthsBetweenDates(
      change.date_range[0],
      change.date_range[1]
    ) || [];
    
    // Get all currently applied changes
    const allChanges = activeProjectAttributes.changes || [];
    
    // Get current grid rows using orchestrator
    const allRows = [];
    await gridOrchestratorRef.current.forEachNode(node => {
      if (node.data) allRows.push(node.data);
    }).catch(error => {
      console.error('[recalculateMasterStatsFromChildren] Failed to get grid rows:', error);
    });
    
    // Track which Master Stats we've already updated to avoid duplicates
    const updatedMasterStats = new Set();
    
    // For each affected Master Stat, recalculate its values
    for (const item of change.affected_master_stats_by_children) {
      for (const masterStat of item.parentMasterStats) {
        // Skip if we've already updated this Master Stat
        if (updatedMasterStats.has(masterStat.gl_key)) {
          continue;
        }
        updatedMasterStats.add(masterStat.gl_key);
        
        const masterStatRow = allRows.find(r => r.glKey === masterStat.gl_key);
        
        if (!masterStatRow) {
          console.warn(`Master Stat row not found: ${masterStat.gl_key}`);
          continue;
        }
        
        // Recalculate Master Stat values for each month in the change date range
        const recalculatedValues = recalculateMasterStatAllMonths(
          masterStat,
          dateRange,
          allRows,
          allChanges.concat([change]) // Include the current change
        );
        
        // Update the Master Stat row in the grid
        const masterStatNode = await gridOrchestratorRef.current.getRowNode(masterStat.gl_key).catch(() => null);
        
        if (masterStatNode && masterStatNode.data) {
          const updatedMasterStatData = { ...masterStatNode.data };
          
          // For each month, calculate the change amount (difference from base)
          dateRange.forEach(month => {
            const newTotal = recalculatedValues[month] || 0;
            const baseValue = masterStatRow[month] || 0;
            const changeAmount = newTotal - baseValue;
            
            // Store in change column field
            const changeColumnField = `${change.change_label} ${month}`;
            updatedMasterStatData[changeColumnField] = changeAmount;
          });
          
          // Track this change in the Master Stat row
          if (!updatedMasterStatData.changes) {
            updatedMasterStatData.changes = [];
          }
          if (!updatedMasterStatData.changes.includes(change.id)) {
            updatedMasterStatData.changes.push(change.id);
          }
          
          // Update the grid row
          masterStatNode.setData(updatedMasterStatData);
        }
      }
    }
    
    // Refresh the grid to show updated values; clear cache first so summary rows
    // recalculate with the freshly applied data.
    if (gridOrchestratorRef.current) {
      netRevenueCacheRef.current = {};
      bottomLineCacheRef.current = {};
      invalidateNRBLForecastCache();
      gridOrchestratorRef.current.refreshCells({ delay: 0 })
        .catch(error => {
          console.warn('[recalculateMasterStatsFromChildren] Failed to refresh cells:', error);
        });
    }
  };


  /**
   * Calculates Master Stat change value by summing child stat change values
   * @param {Array} childStatGlKeys - Array of child stat GL keys
   * @param {string} fieldName - Change column field name (e.g., "Change 1 Jan 2023")
   * @param {Object} gridApi - AG Grid API
   * @returns {number} Sum of child stat change values
   */
  function calculateMasterStatChangeValue(childStatGlKeys, fieldName, gridApi) {
    if (!gridApi || !childStatGlKeys || childStatGlKeys.length === 0) {
      return 0;
    }
    
    return childStatGlKeys.reduce((sum, childGlKey) => {
      const childNode = gridApi.getRowNode(childGlKey);
      if (childNode && childNode.data) {
        const childValue = childNode.data[fieldName] || 0;
        return sum + childValue;
      }
      return sum;
    }, 0);
  }


  /**
   * Recalculates driven variable accounts when stat drivers change (Phase 5)
   * @param {Array} affectedStatDrivers - Array of stat drivers affected by the change
   * @param {Object} change - The change being applied
   */
  const recalculateDrivenAccountsFromStatChanges = async (
    affectedStatDrivers,
    change
  ) => {
    if (!gridOrchestratorRef.current?.isGridReady() || !affectedStatDrivers || affectedStatDrivers.length === 0) return;

    const gridApi = gridRef.current?.api;
    if (!gridApi) return;

    // Get date range for the change
    const dateRange = getMonthsBetweenDates(
      change.date_range[0],
      change.date_range[1]
    ) || [];

    const changeLabel = change.change_label;

    // Snapshot grid rows synchronously — one pass, no async overhead.
    // Build a Map keyed by glKey for O(1) driven-account lookups.
    const gridRowsByGlKey = new Map();
    gridApi.forEachNode(node => {
      if (node.data?.glKey) gridRowsByGlKey.set(node.data.glKey, node.data);
    });

    // Accumulate all row updates here — applied in a single applyTransaction
    // at the end instead of one setData() per driven account.
    const transactionUpdates = [];

    for (const driver of affectedStatDrivers) {
      // Find the driver row — grid first, then eager cache
      let driverRow = gridRowsByGlKey.get(driver.gl_key)
        ?? eagerLoadedRowDataRef.current.get(driver.gl_key)
        ?? null;

      if (!driverRow) {
        console.warn(`[recalculateDrivenAccountsFromStatChanges] Driver row not found: ${driver.gl_key}`);
        continue;
      }

      // Compute driver delta per month once — shared across all driven accounts
      const driverChangePerMonth = {};
      dateRange.forEach(month => {
        const driverBaseValue = Number(driverRow[month]) || 0;
        const delta = calculateSingleMonthChange(
          month,
          driver.gl_key,
          [],
          driverRow,
          change.selected_accounts || [],
          change.function_operator,
          change.operator_value,
          change.dates,
          change.reference_accounts || [],
          change.ref_account_formula || ''
        );
        driverChangePerMonth[month] = { base: driverBaseValue, delta: Number(delta) || 0 };
      });

      for (const drivenAccount of driver.drivenAccounts) {
        const drivenGlKey = `${drivenAccount.entity_id}-${drivenAccount.department_id}-${drivenAccount.account_id}`;

        let drivenRow = gridRowsByGlKey.get(drivenGlKey)
          ?? eagerLoadedRowDataRef.current.get(drivenGlKey)
          ?? null;

        if (!drivenRow) {
          debugLogger.warn('GRID_DATA', `Driven account row not found: ${drivenGlKey}`);
          continue;
        }

        // Ratio-driven calculation: (drivenBase / driverBase) × driverDelta
        const changeFields = {};
        let changeTotal = 0;
        dateRange.forEach(month => {
          const { base: driverBase, delta: driverDelta } = driverChangePerMonth[month];
          let val = 0;
          if (driverBase !== 0 && driverDelta !== 0) {
            const drivenBase = Number(drivenRow[month]) || 0;
            val = drivenBase !== 0 ? (drivenBase / driverBase) * driverDelta : 0;
          }
          changeFields[`${changeLabel} ${month}`] = val;
          changeTotal += val;
        });
        changeFields[`${changeLabel} total`] = changeTotal;

        // Queue grid update if the row is currently rendered
        const existingNode = gridApi.getRowNode(drivenGlKey);
        if (existingNode?.data) {
          const updatedRow = { ...existingNode.data, ...changeFields };
          if (!updatedRow.changes) updatedRow.changes = [];
          if (!updatedRow.changes.includes(change.id)) updatedRow.changes.push(change.id);
          transactionUpdates.push(updatedRow);
          // Keep gridRowsByGlKey in sync so the parent aggregation below
          // sees the updated values when reading from the grid snapshot.
          gridRowsByGlKey.set(drivenGlKey, updatedRow);
        }

        // Write into eagerLoadedRowDataRef for lazy-loaded rows
        const existingEager = eagerLoadedRowDataRef.current.get(drivenGlKey);
        eagerLoadedRowDataRef.current.set(
          drivenGlKey,
          existingEager
            ? { ...existingEager, ...changeFields }
            : { glKey: drivenGlKey, ...changeFields }
        );
      }
    }

    // Aggregate change values up to parent placeholder rows.
    const accountsFullDetails = activeProjectAttributes?.accountsMetadata || [];
    const parentTotalsMap = new Map(); // parentAccount → { month → total }

    for (const driver of affectedStatDrivers) {
      for (const drivenAccount of driver.drivenAccounts) {
        const drivenGlKey = `${drivenAccount.entity_id}-${drivenAccount.department_id}-${drivenAccount.account_id}`;
        const eagerRow = eagerLoadedRowDataRef.current.get(drivenGlKey);
        if (!eagerRow) continue;

        const detail = accountsFullDetails.find(a => a.gl_key === drivenGlKey);
        const parentAccount = detail?.parent_account;
        if (!parentAccount) continue;

        if (!parentTotalsMap.has(parentAccount)) parentTotalsMap.set(parentAccount, {});
        const totals = parentTotalsMap.get(parentAccount);
        dateRange.forEach(month => {
          totals[month] = (totals[month] || 0) + (Number(eagerRow[`${changeLabel} ${month}`]) || 0);
        });
      }
    }

    for (const [parentAccount, monthMap] of parentTotalsMap) {
      const parentChangeFields = {};
      let parentTotal = 0;
      dateRange.forEach(month => {
        const val = monthMap[month] || 0;
        parentChangeFields[`${changeLabel} ${month}`] = val;
        parentTotal += val;
      });
      parentChangeFields[`${changeLabel} total`] = parentTotal;

      // Update eager cache
      const existingParentEager = eagerLoadedRowDataRef.current.get(parentAccount);
      eagerLoadedRowDataRef.current.set(
        parentAccount,
        existingParentEager
          ? { ...existingParentEager, ...parentChangeFields }
          : { id: parentAccount, ...parentChangeFields }
      );

      // Queue parent node update if rendered
      const parentNode = gridApi.getRowNode(parentAccount);
      if (parentNode?.data) {
        transactionUpdates.push({ ...parentNode.data, ...parentChangeFields });
      }
    }

    // Apply all row updates in a single transaction — O(1) grid operations
    // instead of O(N) individual setData() calls.
    if (transactionUpdates.length > 0) {
      gridApi.applyTransaction({ update: transactionUpdates });
    }

    // Re-aggregate group nodes and refresh cells once
    netRevenueCacheRef.current = {};
    bottomLineCacheRef.current = {};
    invalidateNRBLForecastCache();
    gridApi.refreshClientSideRowModel('aggregate');
    gridOrchestratorRef.current.refreshCells({ delay: 0 }).catch(() => {});
  };

  /**
   * Updates the AG Grid with a newly added change. Only used for new changes. 
   * Creates a new change column and updates existing row data with the new values.
   * @param {Object} change New change data
   * @param {Object} activeProjectAttributes Reference data used to build project changes
   */

  /**
   * For pro forma changes (those with reference_accounts containing monthly_values),
   * pre-stamps each account's monthly change values into both:
   *   1. eagerLoadedRowDataRef — picked up by formatChildForGrid when the row is
   *      lazy-loaded later, so the monthly spread shows without needing to expand first.
   *   2. Live grid nodes via setData — for accounts already rendered in the grid.
   *
   * Without this, the monthly open columns are blank after saving a pro forma because
   * the account rows are still behind collapsed placeholders (ids don't match the
   * reference_account glKey), so getSingleMonthChange finds no matching refAccount.
   *
   * @param {Array} changes - Full changes array to scan for pro forma entries
   */
  const preStampProFormaMonthlyValues = async (changes) => {
    if (!changes || changes.length === 0) return;

    for (const change of changes) {
      // Only process changes that carry monthly_values in reference_accounts
      if (!change.reference_accounts || !Array.isArray(change.reference_accounts)) continue;

      const hasMonthlyValues = change.reference_accounts.some(
        ra => ra.monthly_values && ra.monthly_values.length > 0
      );
      if (!hasMonthlyValues) continue;

      const changeLabel = change.change_label;

      for (const refAcct of change.reference_accounts) {
        if (!refAcct.glKey || !refAcct.monthly_values || refAcct.monthly_values.length === 0) continue;

        // Build the field map for this account: { "ProFormaName Jan 2024": 500, ... }
        const changeFields = {};
        let changeTotal = 0;
        refAcct.monthly_values.forEach(mv => {
          if (mv.month_year && mv.value !== null && mv.value !== undefined) {
            changeFields[`${changeLabel} ${mv.month_year}`] = mv.value;
            changeTotal += mv.value;
          }
        });
        changeFields[`${changeLabel} total`] = changeTotal;

        // 1. Write into eagerLoadedRowDataRef so lazy-loaded rows pick up the values
        const existingEager = eagerLoadedRowDataRef.current.get(refAcct.glKey);
        if (existingEager) {
          eagerLoadedRowDataRef.current.set(refAcct.glKey, { ...existingEager, ...changeFields });
        } else {
          eagerLoadedRowDataRef.current.set(refAcct.glKey, { glKey: refAcct.glKey, id: refAcct.glKey, ...changeFields });
        }

        // 2. Update the live node if it's already in the grid
        if (gridOrchestratorRef.current?.isGridReady()) {
          const rowNode = await gridOrchestratorRef.current.getRowNode(refAcct.glKey).catch(() => null);
          if (rowNode && rowNode.data) {
            rowNode.setData({ ...rowNode.data, ...changeFields });
          }
        }
      }
    }
  };

  const buildNewChange = async (changes, activeProjectAttributes, skipColumnUpdate = false) => {
    if (changes && changes.length > 0) {
      // Check if grid orchestrator is ready
      if (!gridOrchestratorRef.current?.isGridReady()) {
        console.warn('Grid API not available yet, skipping change build');
        return;
      }
      
      // PHASE 1: Detect calculated accounts affected by changes
      if (activeProjectAttributes.calculatedAccounts && activeProjectAttributes.calculatedAccounts.length > 0) {
        logAffectedCalculatedAccounts(changes, activeProjectAttributes.calculatedAccounts);
      }
      
      // Process changes to prepare data structures
      if (changes && Array.isArray(changes)) {
        for (const change of changes) {
          // For Master Stat changes, calculate distribution values and populate change.dates
          // This prepares the data for child stat row creation (which happens on-demand when user expands)
          if (change.is_master_stat_change && change.affected_master_stats) {
            // console.log('[buildNewChange] Preparing Master Stat change data:', change.id);
            
            // Get current grid rows for calculation using orchestrator
            const gridRows = [];
            await gridOrchestratorRef.current.forEachNode(node => {
              if (node.data) {
                gridRows.push(node.data);
              }
            }).catch(error => {
              console.error('[buildNewChange] Failed to get grid rows:', error);
            });
            
            // Initialize dates object if it doesn't exist
            if (!change.dates) {
              change.dates = {};
            }
            
            // For each affected Master Stat, calculate distribution to child stats
            change.affected_master_stats.forEach(masterStat => {
              // console.log('[buildNewChange] Calculating distribution for Master Stat:', masterStat.gl_key);
              
              // Calculate monthly adjustments for all child stats
              const monthlyAdjustments = calculateMasterStatChangeAllMonths(
                change,
                masterStat,
                gridRows
              );
              
              // console.log('[buildNewChange] Monthly adjustments:', monthlyAdjustments);
              
              // Convert monthlyAdjustments to change.dates format
              // change.dates[childGlKey] = { "Jan 2024": value, "Feb 2024": value, ... }
              Object.keys(monthlyAdjustments).forEach(month => {
                const adjustments = monthlyAdjustments[month];
                
                adjustments.forEach(adjustment => {
                  // Initialize dates object for this child stat if needed
                  if (!change.dates[adjustment.gl_key]) {
                    change.dates[adjustment.gl_key] = {};
                  }
                  
                  // Store the change amount for this month
                  change.dates[adjustment.gl_key][month] = adjustment.change_amount;
                });
              });
              
              // console.log('[buildNewChange] Populated change.dates for child stats:', Object.keys(change.dates));
            });
          }
          
          // Phase 4: Recalculate Master Stats when child stats are changed
          if (change.affects_master_stats_via_children && change.affected_master_stats_by_children) {
            await recalculateMasterStatsFromChildren(change, activeProjectAttributes);
          }
          
          // Phase 5: Recalculate driven accounts when stat drivers change
          if (change.affects_stat_drivers && change.affected_stat_drivers) {
            await recalculateDrivenAccountsFromStatChanges(
              change.affected_stat_drivers,
              change
            );
          }
          
          // Phase 2: Apply changes to calculated accounts
          await applyCalculatedAccountChanges(change, activeProjectAttributes);
        }
      }
      
      // Skip column update on initial load — columnDefs useMemo already built them.
      // Only update columns when a new change is actually added/removed.
      if (!skipColumnUpdate) {
        await updateColumnsWithNewChange(changes, activeProjectAttributes);
        // Re-stamp all hasChildren placeholder rows with the full updated change set.
        // updateColumnsWithNewChange adds the column but doesn't write values into
        // existing placeholder rows. Without this, collapsed parent rows show blank
        // or stale values for the new change until the user expands the group.
        refreshPlaceholderRowChangeValues(changes);
      }

      // After all standard changes and columns are settled, recalculate dominant
      // change column values so they absorb any standard changes on the same GL keys.
      // This covers both leaf rows (via calculateDominantChanges) and parent/department
      // rows currently in the grid.
      calculateDominantChanges();
      recalculateDominantOnParentRows();
    }
  };

  // Track whether the initial change columns have been built by columnDefs useMemo.
  // On first load, columnDefs already includes change columns — updateColumnsWithNewChange
  // should be skipped to avoid the remove+re-insert blink.
  const initialChangeColumnsBuiltRef = useRef(false);

  // Tracks which change IDs the user has excluded from the forecast via the
  // filter drawer. The ref proxy (disabledChangeIdsRef) is read synchronously
  // by forecast valueGetters; reads are always current via activeFiltersRef.

  // How long to wait after the last columnVisible event before reading settled
  // column state. AG Grid fires hide + immediate re-show due to suppressReactUi
  // + marryChildren reconciliation. 20ms is enough to outlast that burst while
  // minimising the window where disabledChangeIdsRef may be behind column state.
  const COLUMN_VISIBLE_DEBOUNCE_MS = 20;

  // Timer ref for debouncing the columnVisible burst.
  const forecastRefreshTimerRef = useRef(null);

  /**
   * Handle AG Grid columnVisible events fired from the Columns panel (sidebar).
   *
   * The Columns panel fires hide then immediately re-show for the same toggle
   * due to suppressReactUi + marryChildren internal reconciliation. To work
   * around this, we debounce the handler and read the *actual* final column
   * visibility from getColumnState() after the burst settles — rather than
   * trusting event.visible, which reflects an intermediate state.
   *
   * We derive the set of disabled change IDs by checking whether each change's
   * "total" leaf column is hidden in the settled column state.
   */
  const onColumnVisible = useCallback((event) => {
    // Only care about events that involve a change column leaf.
    // Leaf colIds are "<changeId> <month>" or "<changeId> total".
    const cols = event.columns?.length
      ? event.columns
      : event.column ? [event.column] : [];

    const touchedAChange = cols.some((col) => {
      const colId = col?.getColId?.() ?? '';
      const changeIdStr = colId.includes(' ') ? colId.split(' ')[0] : colId;
      const changeId = parseInt(changeIdStr, 10);
      return !isNaN(changeId) && activeProjectAttributes?.changes?.some((c) => c.id === changeId);
    });

    if (!touchedAChange) return;

    // Immediately sync disabledChangeIdsRef from the current column state so that
    // forecast valueGetters read the correct set even during the debounce window.
    // We can't trust event.visible (AG Grid fires intermediate states), but we can
    // read getColumnState() right now for a best-effort early update. The debounce
    // below will do the authoritative read and commit to React state once the burst settles.
    if (gridRef.current?.api) {
      const immediateColState = gridRef.current.api.getColumnState();
      const immediateDisabled = new Set();
      (activeProjectAttributes?.changes || []).forEach((c) => {
        if (c.function_operator === 'calculatedAccount' ||
            c.function_operator === 'recursiveCalculatedAccount') return;
        const totalLeaf = immediateColState.find((s) => s.colId === `${c.id} total`);
        if (totalLeaf?.hide) immediateDisabled.add(c.id);
      });
      disabledChangeIdsRef.current = immediateDisabled;
    }

    // Debounce — let the full burst of events (hide + re-show reconciliation) settle.
    if (forecastRefreshTimerRef.current) clearTimeout(forecastRefreshTimerRef.current);
    forecastRefreshTimerRef.current = setTimeout(() => {
      forecastRefreshTimerRef.current = null;

      if (!gridRef.current?.api) return;

      // Read settled column state from AG Grid's source of truth.
      const columnState = gridRef.current.api.getColumnState();

      // A change group is "hidden" when all its leaf columns are hidden.
      // We check the "total" leaf (colId = "<changeId> total") as a proxy.
      const nextDisabled = new Set();
      (activeProjectAttributes?.changes || []).forEach((c) => {
        if (c.function_operator === 'calculatedAccount' ||
            c.function_operator === 'recursiveCalculatedAccount') return;
        const totalLeaf = columnState.find((s) => s.colId === `${c.id} total`);
        if (totalLeaf?.hide) nextDisabled.add(c.id);
      });

      // Update activeFilters (single source of truth) with the new disabled set.
      // Guard against no-op updates to prevent a feedback loop:
      // setColumnsVisible → onColumnVisible → setActiveFilters → disabledChangeIds
      // effect → setColumnsVisible again. If the set content is identical, bail out.
      const current = activeFiltersRef.current.disabledChangeIds;
      const isIdentical = nextDisabled.size === current.size &&
        [...nextDisabled].every(id => current.has(id));
      if (!isIdentical) {
        setActiveFilters(prev => ({ ...prev, disabledChangeIds: nextDisabled }));
      }

      gridRef.current.api.refreshCells({ force: true });
    }, COLUMN_VISIBLE_DEBOUNCE_MS); // outlasts AG Grid's hide+re-show reconciliation burst
  }, [activeProjectAttributes?.changes, gridRef]);

  /**
   * Applies column visibility for all change columns whenever disabledChangeIds
   * in activeFilters changes. This covers both the drawer Apply path (batch) and
   * the AG Grid sidebar path (already reflected in activeFilters via onColumnVisible).
   *
   * `handleChangeToggle` is kept for the sidebar sync-back path only — it is no
   * longer called directly by the drawer; the drawer commits changes via onApplyFilters.
   */
  useEffect(() => {
    if (!gridRef.current?.api) return;
    const api = gridRef.current.api;
    const changes = activeProjectAttributes?.changes || [];
    const allCols = api.getColumnState();

    changes.forEach((change) => {
      if (change.function_operator === 'calculatedAccount' ||
          change.function_operator === 'recursiveCalculatedAccount') return;
      const visible = !activeFilters.disabledChangeIds.has(change.id);
      const changeColIds = allCols
        .map(c => c.colId)
        .filter(id => id === `${change.id} total` || id.startsWith(`${change.id} `));
      if (changeColIds.length > 0) {
        api.setColumnsVisible(changeColIds, visible);
      }
    });

    api.refreshCells({ force: true });
  }, [activeFilters.disabledChangeIds]); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Sync-back from the AG Grid sidebar column panel.
   * When the user toggles a change column directly via the sidebar, `onColumnVisible`
   * fires and writes the result back into activeFilters. The sidebar is the only
   * remaining caller of this path — the filter drawer uses onApplyFilters instead.
   */
  const handleChangeToggle = useCallback((changeId, visible) => {
    if (!gridRef.current?.api) return;
    const changes = activeProjectAttributes?.changes || [];
    const change = changes.find(c => c.id === changeId);
    if (!change) return;
    const api = gridRef.current.api;
    const allCols = api.getColumnState();
    const changeColIds = allCols
      .map(c => c.colId)
      .filter(id => id === `${changeId} total` || id.startsWith(`${changeId} `));
    if (changeColIds.length > 0) {
      api.setColumnsVisible(changeColIds, visible);
      // onColumnVisible fires automatically and writes back to activeFilters
    }
  }, [activeProjectAttributes?.changes]);
  // Stored as a Map of glKey → row data, NOT added to the grid (avoids grouping issues).
  const eagerLoadedGlKeysRef = useRef(new Set());
  const eagerLoadedRowDataRef = useRef(new Map()); // glKey → row data with monthly values

  /**
   * Eagerly fetch full account data for all GL keys referenced by changes.
   * Stores results in eagerLoadedRowDataRef for use by calculation functions.
   * Does NOT add rows to the grid — avoids grouping/display issues.
   */
  const eagerLoadChangeAccounts = async (changes) => {
    if (!changes || changes.length === 0) return;
    if (!activeUser?.accessToken || !activeProjectInfo?.id) return;

    // Collect all unique GL keys across all changes
    const allGlKeys = new Set();
    changes.forEach(change => {
      (change.selected_accounts || []).forEach(k => allGlKeys.add(k));
      (change.driven_accounts || []).forEach(k => allGlKeys.add(k));
      (change.affected_stat_drivers || []).forEach(driver => {
        (driver.drivenAccounts || []).forEach(a => {
          if (a.gl_key) allGlKeys.add(a.gl_key);
        });
      });
    });

    // Skip keys already in the grid or already fetched
    const existingGlKeys = new Set(eagerLoadedGlKeysRef.current);
    if (gridOrchestratorRef.current?.isGridReady()) {
      try {
        await gridOrchestratorRef.current.forEachNode(node => {
          if (node.data?.id) existingGlKeys.add(node.data.id);
          if (node.data?.glKey) existingGlKeys.add(node.data.glKey);
        });
        // Persist grid-resident keys back into the ref so subsequent calls to
        // eagerLoadChangeAccounts don't re-fetch rows that are already in the grid.
        existingGlKeys.forEach(k => eagerLoadedGlKeysRef.current.add(k));
      } catch {
        // Grid not ready — proceed with what we have
      }
    }

    const keysToFetch = [...allGlKeys].filter(k => !existingGlKeys.has(k));
    if (keysToFetch.length === 0) return;

    debugLogger.log('GRID_DATA', `Eager loading ${keysToFetch.length} change account(s)`);

    try {
      const response = await getAccountsByGlKeys(
        activeProjectInfo.id,
        keysToFetch,
        activeUser.accessToken
      );

      if (response?.status !== 200 || !response?.data?.accounts) {
        debugLogger.warn('GRID_DATA', 'Eager load failed', response);
        return;
      }

      const accounts = response.data.accounts;
      accounts.forEach(account => {
        const row = { id: account.gl_key, glKey: account.gl_key };
        if (account.dates) {
          Object.keys(account.dates).forEach(dateString => {
            const monthYear = toMonthYear(dateString);
            row[monthYear] = account.dates[dateString];
            row[dateString] = account.dates[dateString];
          });
        }
        eagerLoadedRowDataRef.current.set(account.gl_key, row);
        eagerLoadedGlKeysRef.current.add(account.gl_key);
      });

      debugLogger.log('GRID_DATA', `Eager loaded ${accounts.length} account(s) into calculation cache`);
    } catch (err) {
      debugLogger.error('GRID_DATA', 'Error during eager load', err);
    }
  };

  /**
   * Applies changes to calculated accounts when their reference accounts are changed
   * @param {Object} change - The change being applied
   * @param {Object} activeProjectAttributes - Project attributes including calculatedAccounts
   */
  const applyCalculatedAccountChanges = async (change, activeProjectAttributes) => {
    if (!activeProjectAttributes.calculatedAccounts || activeProjectAttributes.calculatedAccounts.length === 0) {
      return;
    }
    
    if (!gridOrchestratorRef.current?.isGridReady()) {
      console.warn('[applyCalculatedAccountChanges] Grid API not available');
      return;
    }
    
    // Find calculated accounts affected by this change
    const affectedCalcAccounts = findAffectedCalculatedAccounts(
      change,
      activeProjectAttributes.calculatedAccounts
    );
    
    if (affectedCalcAccounts.length === 0) {
      return;
    }
    
    debugLogger.log('CALC_ACCOUNT', `Change "${change.change_label}" affects ${affectedCalcAccounts.length} calculated account(s)`);
    
    // Get all grid rows for reference account data
    const allRows = [];
    await gridOrchestratorRef.current.forEachNode(node => {
      if (node.data) allRows.push(node.data);
    }).catch(error => {
      console.error('[applyCalculatedAccountChanges] Failed to get grid rows:', error);
    });
    
    // Build reference account data map
    const referenceAccountData = {};
    allRows.forEach(row => {
      if (row.glKey) {
        referenceAccountData[row.glKey] = row;
      }
    });
    
    // Process each affected calculated account
    for (const calcAccount of affectedCalcAccounts) {
      debugLogger.log('CALC_ACCOUNT', `Processing calculated account: ${calcAccount.change_label}`);
      
      // Calculate monthly impacts for this calculated account
      const monthlyImpacts = calculateAllMonthlyImpacts(
        calcAccount,
        [change], // Array of changes affecting this calc account
        referenceAccountData,
        activeProjectInfo
      );
      
      debugLogger.log('CALC_ACCOUNT', 'Monthly impacts:', monthlyImpacts);
      
      // Update the calculated account row in the grid
      const calcAccountGlKey = `CALC-${calcAccount.id}`;
      const calcAccountNode = await gridOrchestratorRef.current.getRowNode(calcAccountGlKey).catch(() => null);
      
      if (calcAccountNode && calcAccountNode.data) {
        const updatedRowData = { ...calcAccountNode.data };
        
        // Apply change values to the row
        Object.entries(monthlyImpacts).forEach(([month, changeValue]) => {
          const changeColumnField = `${change.change_label} ${month}`;
          updatedRowData[changeColumnField] = changeValue;
        });
        
        // Compute and set the total for this change across all months
        const changeTotalField = `${change.change_label} total`;
        const totalChangeValue = Object.values(monthlyImpacts || {}).reduce(
          (sum, value) => sum + (Number(value) || 0),
          0
        );
        updatedRowData[changeTotalField] = totalChangeValue;
        
        // Track this change in the row's changes array
        if (!updatedRowData.changes) {
          updatedRowData.changes = [];
        }
        if (!updatedRowData.changes.includes(change.id)) {
          updatedRowData.changes.push(change.id);
        }
        
        // Update the row
        calcAccountNode.setData(updatedRowData);
        debugLogger.log('CALC_ACCOUNT', `Updated calculated account row: ${calcAccountGlKey}`);
      } else {
        console.warn(`Calculated account row not found: ${calcAccountGlKey}`);
      }
    }
    
    console.groupEnd();
    
    // Refresh the grid to show updated values; clear cache first so summary rows
    // recalculate with the freshly applied data.
    if (gridOrchestratorRef.current) {
      netRevenueCacheRef.current = {};
      bottomLineCacheRef.current = {};
      invalidateNRBLForecastCache();
      gridOrchestratorRef.current.refreshCells({ delay: 0 })
        .catch(error => {
          console.warn('[applyCalculatedAccountChanges] Failed to refresh cells:', error);
        });
    }
  };



  const cellClassRules = {
    'cell-gray-bg': (params) => {
      return !params.data.isParent;
    },
  };

  const  defaultColDef = {
    filter: true,
    sortable: true,
    tooltipComponent: 'calculatedAccountTooltip',
    tooltipValueGetter: (params) => {
      // Check if this is a change column (they have their own tooltip)
      const colType = params.colDef.type;
      const isChangeColumn = Array.isArray(colType) && (
        colType.includes('changeTotal') || 
        colType.includes('changeMonth')
      );
      
      // Don't show calc/master stat tooltip for change columns
      if (isChangeColumn) {
        return null;
      }
      
      // Show tooltip for Calculated Account rows
      if (params.data?.isCalculatedAccount) {
        return 'show';
      }
      
      return null;
    },
  };


  const getMainMenuItems = (params) => {
    const colType = params.column.getColDef().type;
    const isChangeColumn = Array.isArray(colType) && colType.includes('changeTotal');
    if (isChangeColumn) {
      const changeMenuItems = params.defaultItems.slice(0);
      changeMenuItems.push('separator');
      changeMenuItems.push({
        name: 'Edit Change',
        action: () => editChange(params),
      });
      changeMenuItems.push('separator');
      changeMenuItems.push({
        name: 'Delete Change',
        action: () => deleteChange(params),
      });
      return changeMenuItems;
    }
    return params.defaultItems;
  };

  const getContextMenuItems = useCallback((params) => {
    // Only show calculated account options if this is a calculated account row
    const isCalculatedAccountRow = params.node?.data?.isCalculatedAccount;
    
    const menuItems = [...(params.defaultItems || [])];
    
    if (isCalculatedAccountRow) {
      menuItems.push('separator');
      menuItems.push({
        name: 'Edit Calculated Account',
        action: () => editCalcAccount(params),
      });
      menuItems.push({
        name: 'Delete Calculated Account',
        action: () => deleteCalcAccount(params),
      });
    }
    
    return menuItems;
  }, []);

  const sideBarDef = false; // sidebar replaced by custom ProjectHomeFilterDrawer

  const cellClickedListener = useCallback(event => {
    // console.log('cellClicked', event);
  }, []);
  const editChange = (changeColumn) => {
    const changeId = changeColumn.column.userProvidedColDef.changeId;
    const changeIndex = changes.findIndex(obj => obj.id === changeId);
    if (changeIndex > -1) {
      const change = changes[changeIndex];
      props.handleStartEdit(change);
    }
  }

  const deleteChange = async (changeColumn) => {
    if (!gridOrchestratorRef.current?.isGridReady()) {
      console.warn('Grid API not available for deleteChange');
      return;
    }

    const changeId = changeColumn.column.userProvidedColDef.changeId;
    const changeIndex = changes.findIndex(change => change.id === changeId);
    if (changeIndex === -1) return;

    const change = changes[changeIndex];

    // Fire the API call first — only clean up the grid if the backend confirms success.
    // This prevents the grid from showing the change as gone when the delete actually failed.
    const success = await props.deleteChange(change, change.parent_change);
    if (!success) {
      // API failed — leave the grid untouched; the error toast is shown by the caller.
      return;
    }

    // API confirmed deletion — now clean up grid state.
    const dates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) || [];

    // Handle Master Stat changes
    if (change.is_master_stat_change && change.affected_master_stats) {
      for (const masterStat of change.affected_master_stats) {
        // Remove change data from Master Stat row
        const masterStatNode = await gridOrchestratorRef.current.getRowNode(masterStat.gl_key).catch(() => null);
        if (masterStatNode && masterStatNode.data) {
          let masterRowData = masterStatNode.data;
          dates.forEach(month => {
            delete masterRowData[`${changeId} ${month}`];
          });
          delete masterRowData[`${changeId} total`];
          if (masterRowData.changes) {
            masterRowData.changes = masterRowData.changes.filter(c => c !== changeId);
          }
          masterStatNode.setData(masterRowData);
        }

        // Remove change data from child stat rows
        for (const childGlKey of masterStat.child_stats) {
          const childNode = await gridOrchestratorRef.current.getRowNode(childGlKey).catch(() => null);
          if (childNode && childNode.data) {
            let childRowData = childNode.data;
            dates.forEach(month => {
              delete childRowData[`${changeId} ${month}`];
            });
            delete childRowData[`${changeId} total`];
            if (childRowData.changes) {
              childRowData.changes = childRowData.changes.filter(c => c !== changeId);
            }
            childNode.setData(childRowData);
          }
        }
      }
    }

    // Phase 4: Handle child stat changes that affect Master Stats
    if (change.affects_master_stats_via_children && change.affected_master_stats_by_children) {
      const processedMasterStats = new Set();
      for (const item of change.affected_master_stats_by_children) {
        for (const masterStat of item.parentMasterStats) {
          if (processedMasterStats.has(masterStat.gl_key)) {
            continue;
          }
          processedMasterStats.add(masterStat.gl_key);

          const masterStatNode = await gridOrchestratorRef.current.getRowNode(masterStat.gl_key).catch(() => null);
          if (masterStatNode && masterStatNode.data) {
            let masterRowData = masterStatNode.data;
            dates.forEach(month => {
              delete masterRowData[`${changeId} ${month}`];
            });
            delete masterRowData[`${changeId} total`];
            if (masterRowData.changes) {
              masterRowData.changes = masterRowData.changes.filter(c => c !== changeId);
            }
            masterStatNode.setData(masterRowData);
          }
        }
      }
    }

    // Phase 5: Handle stat driver changes - revert driven accounts
    if (change.affects_stat_drivers && change.affected_stat_drivers) {
      for (const driver of change.affected_stat_drivers) {
        for (const drivenAccount of driver.drivenAccounts) {
          const drivenGlKey = `${drivenAccount.entity_id}-${drivenAccount.department_id}-${drivenAccount.account_id}`;
          const drivenNode = await gridOrchestratorRef.current.getRowNode(drivenGlKey).catch(() => null);

          if (drivenNode && drivenNode.data) {
            let drivenRowData = drivenNode.data;
            dates.forEach(month => {
              delete drivenRowData[`${changeId} ${month}`];
            });
            delete drivenRowData[`${changeId} total`];
            if (drivenRowData.changes) {
              drivenRowData.changes = drivenRowData.changes.filter(c => c !== changeId);
            }
            drivenNode.setData(drivenRowData);
          }
        }
      }
    }

    // Phase 2: Handle calculated account changes - remove change data
    if (activeProjectAttributes.calculatedAccounts && activeProjectAttributes.calculatedAccounts.length > 0) {
      const affectedCalcAccounts = findAffectedCalculatedAccounts(
        change,
        activeProjectAttributes.calculatedAccounts
      );

      for (const calcAccount of affectedCalcAccounts) {
        const calcAccountGlKey = `CALC-${calcAccount.id}`;
        const calcAccountNode = await gridOrchestratorRef.current.getRowNode(calcAccountGlKey).catch(() => null);

        if (calcAccountNode && calcAccountNode.data) {
          let calcRowData = calcAccountNode.data;
          dates.forEach(month => {
            delete calcRowData[`${change.change_label} ${month}`];
          });
          delete calcRowData[`${change.change_label} total`];
          if (calcRowData.changes) {
            calcRowData.changes = calcRowData.changes.filter(c => c !== changeId);
          }
          calcAccountNode.setData(calcRowData);
        }
      }
    }

    for (const glKey of change.selected_accounts) {
      const row = await gridOrchestratorRef.current.getRowNode(glKey).catch(() => null);
      if (row) {
        let rowData = row.data;
        dates.forEach((month) => {
          delete rowData[`${changeId} ${month}`];
        });
        delete rowData[`${changeId} total`];
        rowData.changes = rowData.changes.filter(c => c !== changeId);
        row.setData(rowData);
      }
    }
    if (change.driven_accounts.length > 0) {
      for (const glKey of change.driven_accounts) {
        const row = await gridOrchestratorRef.current.getRowNode(glKey).catch(() => null);
        if (row) {
          let rowData = row.data;
          dates.forEach((month) => {
            delete rowData[`${changeId} ${month}`];
          });
          delete rowData[`${changeId} total`];
          rowData.changes = rowData.changes.filter(c => c !== changeId);
          row.setData(rowData);
        }
      }
    }

    const columns = await gridOrchestratorRef.current.getColumnDefs().catch(() => []);
    let updatedColumnDefs = columns;
    const parentChangeIndex = columns.findIndex(col => { return col.field === change.parent_change });
    if (parentChangeIndex > -1) {
      const parentGroup = columns[parentChangeIndex];
      // Filter the deleted change out of the parent wrapper's children.
      // Children include: a Total leaf (no changeId) + one group per change (has changeId).
      const remainingChildren = parentGroup.children.filter((col) => col.changeId !== changeId);
      // Count how many actual change groups remain (Total leaf has no changeId)
      const remainingChangeChildren = remainingChildren.filter((col) => col.changeId != null);

      if (remainingChangeChildren.length <= 1) {
        // Only 0 or 1 change left — unwrap: remove the parent wrapper entirely and
        // promote the surviving change group (if any) to the top level in its place.
        const before = updatedColumnDefs.slice(0, parentChangeIndex);
        const after = updatedColumnDefs.slice(parentChangeIndex + 1);
        // The surviving child needs columnGroupShow reset so it's always visible at top level
        const promotedChildren = remainingChangeChildren.map((col) => {
          const { columnGroupShow: _removed, ...rest } = col;
          return rest;
        });
        updatedColumnDefs = [...before, ...promotedChildren, ...after];
      } else {
        // 2+ changes remain — keep the wrapper, update its children
        updatedColumnDefs[parentChangeIndex].children = remainingChildren;
      }
    }
    else {
      updatedColumnDefs = columns.filter((col) => { return col.changeId !== changeId });
    }

    if (gridOrchestratorRef.current) {
      await gridOrchestratorRef.current.setColumnDefs(updatedColumnDefs, { delay: 0 }).catch(error => {
        console.error('[deleteChange] Failed to set column defs:', error);
      });
    }
  }

  const editCalcAccount = useCallback((params) => {
    if (!params.node || !params.node.data) {
      console.warn('No row data available for editCalcAccount');
      return;
    }

    const rowData = params.node.data;
    
    // Find the calculated account in activeProjectAttributes.changes
    // Calculated accounts are stored as changes with function_operator 'calculatedAccount' or 'recursiveCalculatedAccount'
    const calcAccount = activeProjectAttributes.changes?.find(
      change => change.id === rowData.calculatedAccountId && 
                (change.function_operator === 'calculatedAccount' || 
                 change.function_operator === 'recursiveCalculatedAccount')
    );
    
    if (calcAccount) {
      props.handleStartEditCalcAccount(calcAccount);
    } else {
      console.error('Calculated account not found:', rowData.calculatedAccountId);
    }
  }, [activeProjectAttributes.changes, props]);

  const deleteCalcAccount = useCallback(async (params) => {
    if (!params.node || !params.node.data) {
      console.warn('No row data available for deleteCalcAccount');
      return;
    }
    const deletedRow = params.node.data;

    // Optimistically remove the row, capture its original index for rollback,
    // and invalidate the Net Revenue cache.
    netRevenueCacheRef.current = {};
    bottomLineCacheRef.current = {};
    invalidateNRBLForecastCache();
    deleteCalcOriginalIndexRef.current = -1;
    setRowData(prev => {
      deleteCalcOriginalIndexRef.current = prev.findIndex(row => row.glKey === deletedRow.glKey);
      return prev.filter(row => row.glKey !== deletedRow.glKey);
    });

    try {
      await props.deleteChange(deletedRow.calculatedAccountId);
    } catch (error) {
      // Backend delete failed – roll back the optimistic removal at original position.
      console.error('[deleteCalcAccount] Backend delete failed, rolling back:', error);
      setRowData(prev => {
        const alreadyPresent = prev.some(row => row.glKey === deletedRow.glKey);
        if (alreadyPresent) return prev;
        // Reinsert at the original position to preserve group/section ordering.
        const originalIndex = deleteCalcOriginalIndexRef.current;
        if (originalIndex >= 0 && originalIndex <= prev.length) {
          const updated = [...prev];
          updated.splice(originalIndex, 0, deletedRow);
          return updated;
        }
        return [...prev, deletedRow];
      });
    }
  }, [props]);

  const autoSizeAll = useCallback(async (skipHeader) => {
    if (!gridOrchestratorRef.current?.isGridReady()) {
      console.warn('Grid API not available for autoSizeAll');
      return;
    }

    try {
      await gridOrchestratorRef.current.autoSizeAllColumns(skipHeader);
    } catch (error) {
      console.error('[autoSizeAll] Failed:', error);
    }
  }, []);

  const generateUniqueId = (baseRow) => {
    // If glKey is present, use it as the id
    if (baseRow.glKey) {
      return baseRow.glKey;
    }
    // For group rows without glKey, use a combination of parentAccount and accountName
    return `${baseRow.parentAccount || ''}${baseRow.accountName ? '-' + baseRow.accountName : ''}`;
  };

  const buildGridRowData = async (masterStatsOverride = null, calculatedAccountsOverride = null) => {
    // Use overrides if provided, otherwise fall back to context
    const masterStatsToUse = masterStatsOverride !== null ? masterStatsOverride : activeProjectAttributes?.masterStats;
    const calculatedAccountsToUse = calculatedAccountsOverride !== null ? calculatedAccountsOverride : activeProjectAttributes?.calculatedAccounts;
    
    debugLogger.log('GRID_DATA', 'Building grid row data', {
      masterStatsCount: masterStatsToUse?.length || 0,
      calculatedAccountsCount: calculatedAccountsToUse?.length || 0,
      hasOverrides: {
        masterStats: masterStatsOverride !== null,
        calculatedAccounts: calculatedAccountsOverride !== null
      }
    });

    const projectId = activeProjectInfo.id;
    let loadedRows = [];

        const formatMonthlyValues = (monthlyValues, baseRow = {}, childAccounts = []) => {
          const formattedRow = { ...baseRow };

          // Add a unique ID to each row
          formattedRow.id = generateUniqueId(baseRow);

          Object.keys(monthlyValues).forEach(dateString => {
            const monthYear = toMonthYear(dateString);
            formattedRow[monthYear] = monthlyValues[dateString];
            formattedRow[dateString] = monthlyValues[dateString];
          });

          formattedRow.child_accounts = childAccounts;
          return formattedRow;
        };

        const buildRegularAccounts = function (parentAccountData) {
          const distinctMonths = new Set();
          const changeDates = new Set();
          const rows = [];

          Object.keys(parentAccountData).forEach(parentAccount => {
            if ((parentAccountData[parentAccount].function_operator || '').toLowerCase().indexOf("calculatedaccount") !== -1) return;

            const monthly = parentAccountData[parentAccount].monthly_values;
            const childAccounts = parentAccountData[parentAccount].child_accounts;
            const acctType = parentAccountData[parentAccount].acct_type || '';
            
            // Add distinct month keys to the set
            Object.keys(monthly).forEach(month => {
              distinctMonths.add(toMonthYear(month));
              changeDates.add(month);
            });

            // Get account type section info from the acct_type field
            const sectionInfo = getAcctTypeSection(acctType);

            const formattedRow = formatMonthlyValues(monthly, { 
              parentAccount, 
              acctType,
              acctTypeSection: sectionInfo.section,
              acctTypeSortOrder: sectionInfo.sortOrder,
              acctTypeSubOrder: sectionInfo.subOrder
            }, childAccounts);
            rows.push(formattedRow);
          });

          // Convert set to array
          return { monthlyValues: Array.from(distinctMonths), changeDates: Array.from(changeDates), rows };
        }

        try {
            const getParentAccountData = await getProjectParentAccountsTotals(projectId, activeUser.accessToken);
            
            // dataApi helpers catch and return the error object instead of throwing,
            // so check for error/non-200 before dereferencing .data.
            const isError = getParentAccountData instanceof Error || getParentAccountData?.isAxiosError;
            const status = getParentAccountData?.status ?? getParentAccountData?.response?.status;
            if (isError || (status !== undefined && status !== 200)) {
              const errData = getParentAccountData?.response?.data ?? getParentAccountData?.data;
              const errMsg = errData?.errors
                ? (typeof errData.errors === 'string' ? errData.errors : JSON.stringify(errData.errors))
                : (getParentAccountData?.message || `HTTP ${status ?? 'unknown'}`);
              throw new Error(`Failed to load parent account data: ${errMsg}`);
            }
            
            const parentAccountData = getParentAccountData.data.parent_account_totals;

            // Collect all GL keys that are children of Master Stats
            const masterStatChildGlKeys = new Set();
            const masterStatGlKeys = new Set();
            if (masterStatsToUse && masterStatsToUse.length > 0) {
              masterStatsToUse.forEach(masterStat => {
                if (masterStat.gl_key) {
                  masterStatGlKeys.add(masterStat.gl_key);
                }
                if (masterStat.child_stats && Array.isArray(masterStat.child_stats)) {
                  masterStat.child_stats.forEach(glKey => masterStatChildGlKeys.add(glKey));
                }
              });
            }

            //loop through changes to see if this row is affected, apply changes here
            const { rows } = buildRegularAccounts(parentAccountData);

            // Remove child stats from their original parent groups
            // Child stats should only appear under master stats, not in their original parent groups.
            // Use reduce to avoid mutating the original row objects while filtering and mapping in one pass.
            const filteredRows = rows.reduce((acc, row) => {
              // Filter out master stat GL keys themselves (virtual aggregations)
              if (masterStatGlKeys.has(row.parentAccount)) {
                return acc;
              }

              if (!row.child_accounts || row.child_accounts.length === 0) {
                acc.push(row);
                return acc;
              }

              // Remove master stat children from the child_accounts array without mutating
              const filteredChildAccounts = row.child_accounts.filter(childGlKey =>
                !masterStatChildGlKeys.has(childGlKey)
              );

              // If ALL children were master stat children, filter out this parent row entirely
              if (row.child_accounts.length > 0 && filteredChildAccounts.length === 0) {
                return acc;
              }

              // Only create a new object if child_accounts actually changed
              acc.push(
                filteredChildAccounts.length === row.child_accounts.length
                  ? row
                  : { ...row, child_accounts: filteredChildAccounts }
              );
              return acc;
            }, []);

            // Apply Pro Forma and other changes to parent rows
            filteredRows.forEach((row) => {
              (activeProjectAttributes?.changes || []).forEach(change => {
                const changeLabel = change.change_label;
                const { function_operator: op, operator_value: opVal, date_range: dateRange } = change;

                // For Pro Forma changes with monthly_values in reference_accounts
                if (change.reference_accounts && Array.isArray(change.reference_accounts)) {
                  // Sum all monthly_values for this row's child accounts
                  let monthlyTotals = {};

                  change.reference_accounts.forEach(refAcct => {
                    // Check if this reference account is a child of the current row
                    const isChild = row.child_accounts && row.child_accounts.includes(refAcct.glKey);

                    if (isChild) {
                      if (refAcct.monthly_values && Array.isArray(refAcct.monthly_values)) {
                        refAcct.monthly_values.forEach(mv => {
                          const monthYear = mv.month_year;
                          if (!monthlyTotals[monthYear]) {
                            monthlyTotals[monthYear] = 0;
                          }
                          monthlyTotals[monthYear] += (mv.value || 0);
                        });
                      }
                    }
                  });

                  // Add the aggregated values to the row for each month
                  Object.keys(monthlyTotals).forEach(monthYear => {
                    const fieldName = `${changeLabel} ${monthYear}`;
                    row[fieldName] = monthlyTotals[monthYear];
                  });

                  // Calculate and add total
                  const total = Object.values(monthlyTotals).reduce((sum, val) => sum + val, 0);
                  if (total !== 0) {
                    const fieldName = `${changeLabel} total`;
                    row[fieldName] = total;
                  }
                }

                // For standard percentage/absolute changes, pre-compute monthly values
                // so the monthly columns show immediately without requiring child rows to load.
                if ((op === 'percentage' || op === 'absoluteValue') && dateRange) {
                  // Use child_accounts on the row to find which selected accounts belong here.
                  // This avoids a dependency on accountsMetadata which may not be loaded yet.
                  const rowChildAccounts = new Set(row.child_accounts || []);
                  const selectedInParent = (change.selected_accounts || []).filter(
                    glKey => rowChildAccounts.has(glKey)
                  );
                  if (selectedInParent.length === 0) return;

                  const dateList = getMonthsBetweenDates(dateRange[0], dateRange[1]) || [];
                  let monthlyTotal = 0;

                  dateList.forEach(monthYear => {
                    const baseVal = Number(row[monthYear]) || 0;
                    let delta = 0;
                    if (op === 'percentage') {
                      delta = baseVal * ((Number(opVal) || 0) / 100);
                    } else if (op === 'absoluteValue') {
                      delta = (Number(opVal) || 0) * selectedInParent.length;
                    }
                    if (delta !== 0) {
                      row[`${changeLabel} ${monthYear}`] = delta;
                      monthlyTotal += delta;
                    }
                  });

                  if (monthlyTotal !== 0) {
                    row[`${changeLabel} total`] = monthlyTotal;
                  }
                }
              });

              // Second pass: for dominant changes, store the adjusted column value
              // (entryDelta - standardDelta) under ${change.id} ${month} so the
              // change column displays the correct adjusted value.
              const allChangesForRow = activeProjectAttributes?.changes || [];
              const standardChangesForRow = allChangesForRow.filter(c => !c.is_dominant_change);
              const dominantChangesForRow = allChangesForRow.filter(c => c.is_dominant_change);

              dominantChangesForRow.forEach(domChange => {
                const { function_operator: domOp, operator_value: domOpVal,
                        date_range: domDateRange, change_label: domLabel } = domChange;
                if (!domDateRange || (domOp !== 'percentage' && domOp !== 'absoluteValue')) return;

                const rowChildAccounts = new Set(row.child_accounts || []);
                const selectedInParent = (domChange.selected_accounts || []).filter(
                  glKey => rowChildAccounts.has(glKey)
                );
                if (selectedInParent.length === 0) return;

                const dateList = getMonthsBetweenDates(domDateRange[0], domDateRange[1]) || [];
                let adjustedTotal = 0;

                dateList.forEach(monthYear => {
                  const baseVal = Number(row[monthYear]) || 0;
                  // Raw entry delta for this dominant change
                  let entryDelta = 0;
                  if (domOp === 'percentage') {
                    entryDelta = baseVal * ((Number(domOpVal) || 0) / 100);
                  } else if (domOp === 'absoluteValue') {
                    entryDelta = (Number(domOpVal) || 0) * selectedInParent.length;
                  }

                  // Sum standard deltas for this month
                  let standardDelta = 0;
                  standardChangesForRow.forEach(sc => {
                    const storedVal = row[`${sc.change_label} ${monthYear}`];
                    if (storedVal !== undefined && storedVal !== null) {
                      standardDelta += Number(storedVal);
                    }
                  });

                  // Adjusted column value = entryDelta - standardDelta
                  const adjustedDelta = entryDelta - standardDelta;
                  row[`${domChange.id} ${monthYear}`] = adjustedDelta;
                  adjustedTotal += adjustedDelta;
                });

                if (adjustedTotal !== 0) {
                  row[`${domChange.id} total`] = adjustedTotal;
                }
              });
            });

            // For accounts without child accounts, add the parent row directly
            // For accounts with children, we'll load the detailed child rows
            const parentsWithoutChildren = filteredRows.filter(row => 
              !row.child_accounts || row.child_accounts.length === 0
            );
            
            // Populate missing grouping fields so the 4-level hierarchy
            // (Account Type → Parent Account → Account → Department) does not
            // produce "(Blank)" intermediate group nodes for these leaf parents.
            parentsWithoutChildren.forEach(row => {
              if (!row.accountName) {
                row.accountName = row.parentAccount || '';
              }
              if (!row.department) {
                row.department = 'Total';
              }
            });

            // Add parent rows that have no children (leaf parent accounts)
            loadedRows.push(...parentsWithoutChildren);
            
            // LAZY LOADING: Don't load child accounts initially - they'll be loaded
            // on-demand when the user expands a parent account group.
            // This dramatically improves initial load time for large projects.
            const parentsWithChildren = filteredRows.filter(row => 
              row.child_accounts && row.child_accounts.length > 0
            );
            
            // Mark parents as having children so AG Grid shows the expand icon
            // Also populate grouping fields so the hierarchy displays correctly
            parentsWithChildren.forEach(row => {
              row.hasChildren = true;
              row.childrenLoaded = false; // Flag to track if we've loaded children
              
              // Populate grouping fields for proper hierarchy display
              // These parent rows show aggregated totals and will have children loaded on-demand
              if (!row.accountName) {
                row.accountName = row.parentAccount || '';
              }
              if (!row.department) {
                row.department = 'Total';
              }
            });
            
            // Add parent rows with children to the grid (without their children)
            loadedRows.push(...parentsWithChildren);
            
            // Inject Master Stat rows - fetch child stat data from API
            if (masterStatsToUse && masterStatsToUse.length > 0) {
              // Collect all unique child stat GL keys
              const childStatGlKeys = new Set();
              masterStatsToUse.forEach(masterStat => {
                if (masterStat.child_stats && Array.isArray(masterStat.child_stats)) {
                  masterStat.child_stats.forEach(glKey => childStatGlKeys.add(glKey));
                }
              });
              
              const childStatGlKeyArray = Array.from(childStatGlKeys);
              
              // Fetch all child stat GL keys in batches
              const glKeyDataMap = {};
              for (let i = 0; i < childStatGlKeyArray.length; i += BATCH_SIZE) {
                const batch = childStatGlKeyArray.slice(i, i + BATCH_SIZE);
                await Promise.all(
                  batch.map(async (glKey) => {
                    try {
                      const response = await getProjectDataByGlKey(projectId, glKey, activeUser.accessToken);
                      if (response?.data) {
                        glKeyDataMap[glKey] = response.data;
                      }
                    } catch (fetchError) {
                      debugLogger.warn('GRID_DATA', `Failed to fetch child stat GL key: ${glKey}`, fetchError);
                    }
                  })
                );
              }
              
              // Transform API responses into grid row format
              const accountDataWithGlKeys = Object.values(glKeyDataMap)
                .filter(data => data)
                .map(data => {
                  const monthlyValues = data.dates || {};
                  const row = {
                    glKey: data.gl_key,
                    entity: data.entity_name,
                    entityId: data.entity_id,
                    department: data.department_id && data.department_name
                      ? `${data.department_id} - ${data.department_name}`
                      : (data.department_name || ''),
                    departmentId: data.department_id,
                    accountName: data.account_id && data.account_name
                      ? `${data.account_id} - ${data.account_name}`
                      : (data.account_name || ''),
                    accountId: data.account_id,
                  };
                  
                  // Add monthly values to the row
                  Object.keys(monthlyValues).forEach(dateString => {
                    const monthYear = toMonthYear(dateString);
                    row[monthYear] = monthlyValues[dateString];
                  });
                  
                  return row;
                });
              
              // Build a Map for O(1) glKey → row lookups when constructing child rows
              const accountDataByGlKey = new Map(
                accountDataWithGlKeys.map(row => [row.glKey, row])
              );

              // Create Master Stat rows from the child stat data
              if (accountDataWithGlKeys.length > 0) {
                const acctTypeSectionInfoStat = getAcctTypeSection('Stat');
                
                // Add child stat rows to the grid
                masterStatsToUse.forEach(masterStat => {
                  if (!masterStat.child_stats || !masterStat.master_stat_name) return;
                  masterStat.child_stats.forEach(childGlKey => {
                    const childData = accountDataByGlKey.get(childGlKey);
                    if (!childData) return;
                    const childStatRow = {
                      ...childData,
                      acctType: 'Stat',
                      acctTypeSection: acctTypeSectionInfoStat.section,
                      acctTypeSortOrder: acctTypeSectionInfoStat.sortOrder,
                      acctTypeSubOrder: acctTypeSectionInfoStat.subOrder,
                      parentAccount: masterStat.gl_key, // Group under master stat gl_key for uniqueness across departments
                      fixedOrVariable: 'Stat',
                      driver: childData.driver || '',
                      changes: [],
                      child_accounts: [],
                      id: childGlKey,
                      isMasterStatChild: true, // Flag to identify master stat children
                      masterStatId: masterStat.id, // Numeric ID needed for audit trail tooltip
                      masterStatName: masterStat.master_stat_name, // Store master stat name for reference
                    };
                    loadedRows.push(childStatRow);
                  });
                });
              }
            }
            
        } catch (error) {
          console.error(`Error fetching data:`, error);
          throw error;
        }

        // Build Calculated Account rows unconditionally (independent of master stats).
        // Runs after both the parent-account and master-stat sections so all base rows
        // are already in loadedRows before formula evaluation.
        const calculatedAccountRows = [];

        if (calculatedAccountsToUse && calculatedAccountsToUse.length > 0) {
          try {
            // Build a deduplicated map of GL key → API data so that GL keys shared
            // across multiple calculated accounts are only fetched once.
            const uniqueGlKeys = new Set();
            calculatedAccountsToUse.forEach(calcAccount => {
              (calcAccount.reference_accounts || []).forEach(refAcct => {
                if (refAcct.glKey) uniqueGlKeys.add(refAcct.glKey);
              });
            });

            // Fetch all unique GL keys in batches
            const glKeyDataMap = {};
            const glKeyArray = Array.from(uniqueGlKeys);
            
            if (glKeyArray.length > 0) {
              for (let i = 0; i < glKeyArray.length; i += BATCH_SIZE) {
                const batch = glKeyArray.slice(i, i + BATCH_SIZE);
                await Promise.all(
                  batch.map(async (glKey) => {
                    try {
                      const response = await getProjectDataByGlKey(projectId, glKey, activeUser.accessToken);
                      if (response?.data) {
                        glKeyDataMap[glKey] = response.data;
                      }
                    } catch (fetchError) {
                      // Per-request failure: leave the key absent so downstream
                      // handling can skip or fall back gracefully.
                      debugLogger.warn('GRID_DATA', `Failed to fetch reference account GL key: ${glKey}`, fetchError);
                    }
                  })
                );
              }
            }

          // Create a restricted mathjs instance (matches the pattern used elsewhere).
          // Do NOT disable 'evaluate' or 'parse' — they are required to compute formula results.
          const calcAcctRestrictedMath = create(all, {});
          calcAcctRestrictedMath.import({
            import:      function () { throw new Error('Function import is disabled'); },
            createUnit:  function () { throw new Error('Function createUnit is disabled'); },
            simplify:    function () { throw new Error('Function simplify is disabled'); },
            derivative:  function () { throw new Error('Function derivative is disabled'); },
          }, { override: true });

          // Helper to escape regex special characters in row letters
          const escapeRegExp = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

          for (const calcAccount of calculatedAccountsToUse) {
            try {
              // Resolve reference account data from the pre-fetched map.
              // A reference account whose GL key was not fetched (e.g., fetch failed)
              // is treated as having all-zero values rather than skipping the row,
              // so the calculated account remains visible (possibly with partial data).
              const refAccountData = (calcAccount.reference_accounts || [])
                .map(refAcct => glKeyDataMap[refAcct.glKey] || null);

              const missingRefs = refAccountData.filter(d => d === null);
              if (missingRefs.length > 0) {
                debugLogger.warn(
                  'GRID_DATA',
                  `Calculated account "${calcAccount.change_label}" (id: ${calcAccount.id}) ` +
                  `has ${missingRefs.length} unresolved reference account(s). ` +
                  `Missing values will default to 0.`
                );
              }

              // Pre-compile word-boundary regexes for each row letter so that
              // token 'A' is not accidentally replaced inside 'AA' or function names.
              const rowLetterRegexMap = new Map();
              (calcAccount.reference_accounts || []).forEach(refAcct => {
                const escaped = escapeRegExp(refAcct.rowLetter);
                rowLetterRegexMap.set(refAcct.rowLetter, new RegExp(`\\b${escaped}\\b`, 'g'));
              });

              // Precompute a monthYear → value Map per reference account for O(1) lookups
              // during the month iteration, avoiding an O(D) scan per (month × refAccount).
              const refMonthValueMaps = refAccountData.map(refData => {
                if (!refData) return new Map();
                const map = new Map();
                Object.keys(refData.dates || {}).forEach(dateStr => {
                  map.set(toMonthYear(dateStr), refData.dates[dateStr] || 0);
                });
                return map;
              });

              // Get date range
              const dateRange = getMonthsBetweenDates(activeProjectInfo.start_date, activeProjectInfo.end_date);

              // Calculate monthly values
              const monthlyValues = {};
              dateRange.forEach(month => {
                try {
                  let formulaString = formula.toJavaScript(calcAccount.ref_account_formula);

                  // Replace each reference account letter with its value using
                  // word-boundary regex to avoid substring collisions.
                  // If a reference account's data could not be fetched, default its value to 0.
                  calcAccount.reference_accounts.forEach((refAcct, idx) => {
                    const refValue = refMonthValueMaps[idx].get(month) ?? 0;
                    const regex = rowLetterRegexMap.get(refAcct.rowLetter);
                    formulaString = formulaString.replace(regex, refValue);
                  });

                  // Evaluate using the restricted mathjs instance to prevent DoS
                  // from expensive expressions and for consistency with the existing
                  // calculated-account evaluation path.
                  const calculatedValue = calcAcctRestrictedMath.evaluate(formulaString);
                  monthlyValues[month] = calculatedValue;
                } catch (error) {
                  monthlyValues[month] = 0;
                }
              });

              // Get entity/department from the first *available* reference account.
              // refAccountData entries may be null when a fetch failed; use the first
              // non-null entry so the row remains visible even when some refs are missing.
              const firstRefData = refAccountData.find(d => d !== null) || null;

              // Enhance reference accounts with account names for tooltip
              const enhancedReferenceAccounts = calcAccount.reference_accounts.map((refAcct, idx) => ({
                ...refAcct,
                accountName: refAccountData[idx]?.account_name || 'Unknown Account'
              }));

              // Get account type section info
              const acctTypeSectionInfo = getAcctTypeSection('Calculated Account');

              // Create calculated account row
              const calcRow = {
                glKey: `CALC-${calcAccount.id}`,
                accountName: `CALC-${calcAccount.id} - ${calcAccount.change_label}`,
                accountId: `CALC-${calcAccount.id}`,
                entity: firstRefData?.entity_name || 'Calculated',
                entityId: firstRefData?.entity_id || 'CALC',
                department: firstRefData?.department_id && firstRefData?.department_name
                  ? `${firstRefData.department_id} - ${firstRefData.department_name}`
                  : (firstRefData?.department_name || 'Calculated Accounts'),
                departmentId: firstRefData?.department_id || 'CALC',
                acctType: 'Calculated Account',
                acctTypeSection: acctTypeSectionInfo.section,
                acctTypeSortOrder: acctTypeSectionInfo.sortOrder,
                acctTypeSubOrder: acctTypeSectionInfo.subOrder,
                parentAccount: calcAccount.change_label,
                displayName: calcAccount.change_label,
                fixedOrVariable: '',
                driver: '',
                isCalculatedAccount: true,
                calculatedAccountId: calcAccount.id,
                referenceAccounts: enhancedReferenceAccounts,
                formula: calcAccount.ref_account_formula,
                ...monthlyValues,
                changes: [],
                id: `CALC-${calcAccount.id}`,
              };

              calculatedAccountRows.push(calcRow);
            } catch (error) {
              // Log which calculated account failed to help with debugging
              debugLogger.warn(
                'GRID_DATA',
                `Failed to build calculated account row for id=${calcAccount?.id} label="${calcAccount?.change_label}"`,
                error
              );
            }
          }
          } catch (error) {
            debugLogger.error('GRID_DATA', 'Error building calculated account rows', error);
            console.error('Error building calculated account rows:', error);
            // Continue without calculated accounts if there's an error
          }
        }

        // Add Calculated Account rows to the existing loadedRows
        loadedRows.push(...calculatedAccountRows);

        return loadedRows;
  }

  useEffect(()=>{
    props.dataLoaded(basePeriodDataLoaded)
  },[basePeriodDataLoaded])
  
  // Create stable reference for changes to avoid unnecessary re-renders
  const changesVersion = useMemo(() => {
    if (!activeProjectAttributes?.changes) return '';
    return activeProjectAttributes.changes.map(c => c.id).join(',');
  }, [activeProjectAttributes?.changes]);

  // Track the previous changesVersion so we can diff and find newly-added change IDs.
  // This lets us show the spinner only on the new column instead of all stat-driver columns.
  const previousChangesVersionRef = useRef('');
  
  // Apply changes when they're updated AND grid rows are loaded
  useEffect(() => {
    // Only apply changes if:
    // 1. Grid is ready
    // 2. Base period data (rows) are loaded
    // 3. We have changes to apply
    if (gridOrchestratorRef.current?.isGridReady() && gridReady && basePeriodDataLoaded && 
        activeProjectAttributes?.changes && activeProjectAttributes.changes.length > 0) {

      // Diff the new changesVersion against the previous to find newly added IDs.
      // On first run, previousChangesVersionRef is '' so all IDs are treated as new —
      // but on first run we show the spinner for all stat-driver changes anyway.
      const prevIds = new Set(
        previousChangesVersionRef.current ? previousChangesVersionRef.current.split(',') : []
      );
      const newlyAddedChangeIds = new Set(
        (activeProjectAttributes.changes || [])
          .map(c => String(c.id))
          .filter(id => !prevIds.has(id))
      );
      previousChangesVersionRef.current = changesVersion;

      // On first run, wait 500ms for rows to render. On subsequent fires the grid
      // is already populated — 100ms is enough to let React flush the column add.
      const isFirstRunCheck = !initialChangeColumnsBuiltRef.current;
      const waitMs = isFirstRunCheck ? 500 : 100;

      safeSetTimeout(() => {
        (async () => {
          try {
            if (gridOrchestratorRef.current?.isGridReady()) {
              // Double-check rows exist before applying changes
              let finalRowCount = 0;
              await gridOrchestratorRef.current.forEachNode(() => finalRowCount++).catch(() => {});
              
              if (finalRowCount > 0) {
                const isFirstRun = !initialChangeColumnsBuiltRef.current;
                initialChangeColumnsBuiltRef.current = true;
                // Reset eager load cache only on first run. On subsequent fires
                // (e.g. a new change added), we keep previously fetched rows and
                // only fetch the new GL keys that aren't already cached.
                if (isFirstRun) {
                  eagerLoadedGlKeysRef.current = new Set();
                  eagerLoadedRowDataRef.current = new Map();
                }

                // Mark stat driver changes as calculating so their column headers
                // show a spinner until the driven-account math completes.
                //
                // Also covers master stat changes, which have the longest possible
                // wait — the backend polls up to ~37s for async distribution calculation.
                //
                // On initial load: spin all such changes (they all compute together).
                // On subsequent adds: only spin the newly-added change(s) so existing
                // columns don't get loading indicators unnecessarily.
                //
                // If pendingCalculatingLabelRef is set, a new change was identified
                // before the API call completed. Match it by label to get the real ID.
                const allChangesNeedingSpinner = (activeProjectAttributes.changes || [])
                  .filter(c =>
                    (c.affects_stat_drivers && c.affected_stat_drivers?.length > 0)
                    || c.is_master_stat_change
                  );

                // Consume the pending label ref if it matches a newly-added change
                if (pendingCalculatingLabelRef.current) {
                  const matchedChange = allChangesNeedingSpinner.find(
                    c => c.change_label === pendingCalculatingLabelRef.current
                      && newlyAddedChangeIds.has(String(c.id))
                  );
                  if (matchedChange) {
                    newlyAddedChangeIds.add(String(matchedChange.id));
                  }
                  pendingCalculatingLabelRef.current = null;
                  onPendingCalculatingLabelConsumed?.();
                }

                const statDriverChangeIdsToSpin = new Set(
                  isFirstRun
                    ? allChangesNeedingSpinner.map(c => c.id)
                    : allChangesNeedingSpinner
                        .filter(c => newlyAddedChangeIds.has(String(c.id)))
                        .map(c => c.id)
                );

                if (statDriverChangeIdsToSpin.size > 0) {
                  setCalculatingChangeIds(statDriverChangeIdsToSpin);
                }

                await eagerLoadChangeAccounts(activeProjectAttributes.changes);
                // Pre-stamp pro forma monthly values into the eager cache and any
                // already-rendered leaf rows so monthly spread shows without expand.
                await preStampProFormaMonthlyValues(activeProjectAttributes.changes);
                await buildNewChange(activeProjectAttributes.changes, activeProjectAttributes, isFirstRun);
                // On initial load, buildNewChange skips the column update (skipColumnUpdate=true),
                // which means refreshPlaceholderRowChangeValues is never called inside it.
                // However, stat driver changes require that function to stamp parent placeholder
                // rows using child_accounts (not accountsMetadata, which may not be loaded
                // yet when recalculateDrivenAccountsFromStatChanges runs). Call it here explicitly
                // so collapsed parent rows show driven-account change values without expanding.
                if (isFirstRun) {
                  refreshPlaceholderRowChangeValues(activeProjectAttributes.changes);
                }

                // All calculations complete — clear the spinner
                if (statDriverChangeIdsToSpin.size > 0) {
                  // Small delay so the calculating state is visible even on fast loads
                  safeSetTimeout(() => setCalculatingChangeIds(new Set()), 800);
                }
              }
            }
          } catch (error) {
            console.error('[useEffect:changesVersion] Error applying changes:', error);
          }
        })();
      }, waitMs);
    }
  }, [changesVersion, gridReady, basePeriodDataLoaded]);
  
  // Refresh grid when Master Stats change (create/update/delete)
  useEffect(() => {
    // Skip on initial load - only refresh on subsequent changes
    if (!masterStatsInitialLoadRef.current) {
      masterStatsInitialLoadRef.current = true;
      return;
    }
    
    // Only refresh if grid is ready AND base period data has been loaded
    // This prevents refresh during initial grid setup
    // Also check if masterStats actually exists (allow empty array to handle deletions)
    if (gridOrchestratorRef.current?.isGridReady() && 
        gridReady && 
        basePeriodDataLoaded &&
        activeProjectAttributes?.masterStats !== undefined) {
      // console.log('Master Stats changed, refreshing grid datasource');
      
      // Register and track Master Stats refresh with orchestrator
      if (loadingOrchestrator) {
        if (!loadingOrchestrator.getOperation('master-stats-refresh')) {
          loadingOrchestrator.registerOperation('master-stats-refresh', 0);
        }
        loadingOrchestrator.startOperation('master-stats-refresh');
      }
      
      // Load fresh data with updated master stats
      buildGridRowData()
        .then(rows => {
          // Invalidate cache so summary rows recalculate with new data
          netRevenueCacheRef.current = {};
          bottomLineCacheRef.current = {};
          
          setRowData(addBottomLineSummaryRow(addNetRevenueSummaryRow(rows, currentDeptsRef.current.length > 0 ? currentDeptsRef.current : departmentsList), currentDeptsRef.current.length > 0 ? currentDeptsRef.current : departmentsList));
          // Re-apply filters after the full data rebuild.
          // applyFiltersToRowData (recomputes placeholder base-period values) only runs
          // for the external entity/dept filter. onFilterChanged() runs unconditionally
          // so native AG Grid text filters are also reapplied after a full rebuild.
          // Use activeFiltersRef.current — not the stale closure — to handle in-flight changes.
          if (selectedEntityIdsRef.current.size > 0 || selectedDeptIdsRef.current.size > 0) {
            setTimeout(() => {
              applyFiltersToRowData(activeFiltersRef.current).then(() => {
                gridRef.current?.api?.onFilterChanged();
                gridRef.current?.api?.refreshCells({ force: true });
              });
            }, 0);
          } else if (gridRef.current?.api) {
            setTimeout(() => {
              gridRef.current.api.onFilterChanged();
            }, 0);
          }
          if (gridRef.current?.api) safeSetTimeout(() => expandNRBLWrappers(gridRef.current.api), 50);
          if (loadingOrchestrator) {
            loadingOrchestrator.completeOperation('master-stats-refresh');
          }
        })
        .catch(error => {
          console.error('[Master Stats Refresh] Failed to refresh data:', error);
          if (loadingOrchestrator) {
            loadingOrchestrator.failOperation('master-stats-refresh', error);
          }
        });
    }
  }, [activeProjectAttributes?.masterStats]);
  
  // Refresh grid when Calculated Accounts change (create/update/delete)
  useEffect(() => {
    // Only refresh if grid is ready AND base period data has been loaded
    // This prevents refresh during initial grid setup
    if (gridOrchestratorRef.current?.isGridReady() && 
        gridReady && 
        basePeriodDataLoaded &&
        activeProjectAttributes?.calculatedAccounts !== undefined) {
      
      debugLogger.log('CALC_ACCOUNT', 'Calculated accounts changed, refreshing grid data', {
        calculatedAccountsCount: activeProjectAttributes.calculatedAccounts.length,
        calculatedAccounts: activeProjectAttributes.calculatedAccounts.map(ca => ({
          id: ca.id,
          label: ca.change_label
        }))
      });
      
      // Register and track Calculated Accounts refresh with orchestrator
      if (loadingOrchestrator) {
        if (!loadingOrchestrator.getOperation('calc-accounts-refresh')) {
          loadingOrchestrator.registerOperation('calc-accounts-refresh', 0);
        }
        loadingOrchestrator.startOperation('calc-accounts-refresh');
      }
      
      // The grid uses the client-side row model (rowData prop), so we must reload
      // rowData directly by calling buildGridRowData.
      buildGridRowData()
        .then(rows => {
          netRevenueCacheRef.current = {};
          bottomLineCacheRef.current = {};
          
          setRowData(addBottomLineSummaryRow(addNetRevenueSummaryRow(rows, currentDeptsRef.current.length > 0 ? currentDeptsRef.current : departmentsList), currentDeptsRef.current.length > 0 ? currentDeptsRef.current : departmentsList));
          // Re-apply filters after the full data rebuild.
          // applyFiltersToRowData (recomputes placeholder base-period values) only runs
          // for the external entity/dept filter. onFilterChanged() runs unconditionally
          // so native AG Grid text filters are also reapplied after a full rebuild.
          // Use activeFiltersRef.current — not the stale closure — to handle in-flight changes.
          if (selectedEntityIdsRef.current.size > 0 || selectedDeptIdsRef.current.size > 0) {
            setTimeout(() => {
              applyFiltersToRowData(activeFiltersRef.current).then(() => {
                gridRef.current?.api?.onFilterChanged();
                gridRef.current?.api?.refreshCells({ force: true });
              });
            }, 0);
          } else if (gridRef.current?.api) {
            setTimeout(() => {
              gridRef.current.api.onFilterChanged();
            }, 0);
          }
          if (gridRef.current?.api) safeSetTimeout(() => expandNRBLWrappers(gridRef.current.api), 50);
          if (loadingOrchestrator) {
            loadingOrchestrator.completeOperation('calc-accounts-refresh');
          }
          debugLogger.log('CALC_ACCOUNT', 'Grid data refresh complete');
        })
        .catch(error => {
          debugLogger.error('CALC_ACCOUNT', 'Failed to refresh grid data', error);
          console.error('[Calculated Accounts Refresh] Failed to reload row data:', error);
          if (loadingOrchestrator) {
            loadingOrchestrator.failOperation('calc-accounts-refresh', error);
          }
        });
    }
  }, [activeProjectAttributes?.calculatedAccounts]);
  

  const onGridReady = async (params) => {
    setGridReady(true);
    
    // Check if operations are already registered (they should be from parent)
    // If not, register them now (fallback for safety)
    if (loadingOrchestrator) {
      if (!loadingOrchestrator.getOperation('changes')) {
        loadingOrchestrator.registerOperation('changes', 6);
      }
      if (!loadingOrchestrator.getOperation('grid-setup')) {
        loadingOrchestrator.registerOperation('grid-setup', 7);
      }
      loadingOrchestrator.startOperation('grid-setup');
    }
    
    try {
      // Load initial data
      const initialRows = await buildGridRowData();
      
      // Build the departments list synchronously from the always-current ref.
      // This is available immediately since departments load before onGridReady fires.
      const currentDepts = departmentsListRef.current.length > 0
        ? departmentsListRef.current
        : (context.activeProjectAttributes?.departments || [])
            .map(dept => `${dept.departmentId} - ${dept.departmentName}`)
            .filter(Boolean);
      
      // Store depts in a ref so the 100ms timeout below can reuse the same list
      // without re-reading the stale closure context.
      currentDeptsRef.current = currentDepts;
      
      setRowData(addBottomLineSummaryRow(addNetRevenueSummaryRow(initialRows, currentDepts), currentDepts));

      // Load per-department base values in the background — does NOT block grid-ready.
      // NR/BL dept rows render immediately with values from the live agg scan fallback,
      // then refresh once the pre-computed values arrive (typically <1s in prod).
      loadNRBLDeptBaseValues(initialRows, activeProjectInfo.id, activeUser.accessToken)
        .then(() => {
          if (gridRef.current?.api && isMountedRef.current) {
            netRevenueCacheRef.current = {};
            bottomLineCacheRef.current = {};
            gridRef.current.api.refreshCells({ force: true });
          }
        })
        .catch(err => console.warn('[loadNRBLDeptBaseValues] failed:', err));
      
      // Mark initial load as complete
      isInitialLoadRef.current = false;
      
      // Grid is now ready - complete grid-setup operation
      if (loadingOrchestrator) {
        loadingOrchestrator.completeOperation('grid-setup');
      }
      
      // Load master stats
      if (loadingOrchestrator && !loadingOrchestrator.getOperation('master-stats')) {
        loadingOrchestrator.registerOperation('master-stats', 8);
      }
      if (loadingOrchestrator) {
        loadingOrchestrator.startOperation('master-stats');
      }
      
      const masterStatsData = await context.getMasterStats(activeProjectInfo.id, activeUser.accessToken);
      
      if (loadingOrchestrator) {
        loadingOrchestrator.completeOperation('master-stats');
      }
      
      // Load changes
      if (loadingOrchestrator) {
        loadingOrchestrator.startOperation('changes');
      }
      
      const getChangesData = await context.setActiveProjectChangesAttribute(
        activeProjectInfo.id,
        activeUser.accessToken
      );

      if (loadingOrchestrator) {
        loadingOrchestrator.completeOperation('changes');
      }

      if (getChangesData && getChangesData.length > 0) {
        setChangesData(getChangesData);
      }
      
      // Refresh row data with master stats and changes included
      safeSetTimeout(async () => {
        if (params.api && isMountedRef.current) {
          // Mark that master stats have been initially loaded
          masterStatsInitialLoadRef.current = true;
          
          try {
            // Get fresh data with master stats and calculated accounts
            const updatedRows = await buildGridRowData(masterStatsData, activeProjectAttributes?.calculatedAccounts);
            // Invalidate cache so summary rows recalculate with the refreshed data
            netRevenueCacheRef.current = {};
            bottomLineCacheRef.current = {};
            
            // Read departments from the stable ref populated during initial load.
            // context.activeProjectAttributes is a stale closure snapshot at this point.
            const currentDepts = currentDeptsRef.current.length > 0
              ? currentDeptsRef.current
              : departmentsListRef.current;
            setRowData(addBottomLineSummaryRow(addNetRevenueSummaryRow(updatedRows, currentDepts), currentDepts));
            // Re-apply filters after the full data rebuild.
            // applyFiltersToRowData (recomputes placeholder base-period values) only runs
            // for the external entity/dept filter. onFilterChanged() runs unconditionally
            // so native AG Grid text filters are also reapplied after a full rebuild.
            // Use activeFiltersRef.current — not the stale closure — to handle in-flight changes.
            if (selectedEntityIdsRef.current.size > 0 || selectedDeptIdsRef.current.size > 0) {
              setTimeout(() => {
                applyFiltersToRowData(activeFiltersRef.current).then(() => {
                  gridRef.current?.api?.onFilterChanged();
                  gridRef.current?.api?.refreshCells({ force: true });
                });
              }, 0);
            } else if (gridRef.current?.api) {
              setTimeout(() => {
                gridRef.current.api.onFilterChanged();
              }, 0);
            }
            // Re-expand __NR__/__BL__ wrappers — setRowData resets all group expansion state
            if (params.api) {
              safeSetTimeout(() => expandNRBLWrappers(params.api), 50);
            }
          } catch (error) {
            console.error('[onGridReady] Error loading updated data:', error);
          }
        }
        
        // Enable sidebar and mark data as loaded
        setSidebarEnabled(true);
        setBasePeriodDataLoaded(true);
      }, 100);
      
    } catch (error) {
      console.error('[onGridReady] Error loading grid data:', error);
      if (loadingOrchestrator) {
        // Fail any operations that are still loading OR still pending (never started).
        // On large projects the async chain can throw before reaching startOperation('changes'),
        // leaving 'changes' in 'pending' state. isLoading() treats 'pending' as loading,
        // so progress never reaches 100 and buttons stay disabled indefinitely.
        const opsToFail = ['grid-setup', 'master-stats', 'changes'];
        opsToFail.forEach(opName => {
          const status = loadingOrchestrator.getOperation(opName)?.status;
          if (status === 'loading' || status === 'pending') {
            loadingOrchestrator.failOperation(opName, error);
          }
        });
      }
    }
  };

  const autoGroupColumnDef = useMemo(() => {
    return {
      headerName: 'Account',
      minWidth: 200,
      cellRendererParams: {
        suppressCount: true,
        suppressDoubleClickExpand: true,
        suppressEnterExpand: true,
        innerRenderer: (params) => {
          // Show inline loading indicator for groups currently being lazy-loaded.
          // Read from the ref (not state) so this renderer doesn't force a column
          // redefinition — and a full grid re-render — every time a parent expands.
          const nodeKey = params.node?.key;
          if (params.node?.group && nodeKey && expandingParentsRef.current.has(nodeKey)) {
            return (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#888' }}>
                <span className="ag-loading-icon" style={{ display: 'inline-block', width: 14, height: 14 }}>
                  <span className="ag-icon ag-icon-loading" />
                </span>
                {nodeKey}
              </span>
            );
          }
          // Special handling for Net Revenue / Bottom Line rows – only at level 0
          // (the top-level group row) to avoid matching intermediate group levels
          // that happen to share the same key (e.g., parentAccount or accountName groups).
          if (params.node?.level === 0) {
            if (params.node?.key === 'Net Revenue') {
              return 'Net Revenue';
            }
            if (params.node?.key === 'Bottom Line') {
              return 'Bottom Line';
            }
          }
          
          // For footer rows (group totals at bottom), return the group key
          if (params.node.footer) {
            return params.value || params.node.key || '';
          }
          
          // For group rows (created by rowGroup columns), return the group key
          if (params.node.group) {
            // For Master Stat groups: parentAccount is now the gl_key for uniqueness.
            // Use masterStatName from a child row for the human-readable display label.
            const firstChild = params.node.allLeafChildren?.[0];
            if (firstChild?.data?.isMasterStatChild && firstChild?.data?.masterStatName) {
              return firstChild.data.masterStatName;
            }
            
            const displayValue = params.value || params.node.key || '';
            
            // Fallback: if displayValue is still empty, try parentAccount from first child
            if (!displayValue || displayValue === '') {
              if (firstChild?.data?.parentAccount) {
                return firstChild.data.parentAccount;
              }
            }
            return displayValue;
          }
          
          // For leaf rows (actual data rows) - these will be hidden via CSS
          if (params.data) {
            // For child stats under Master Stats, display the account name
            if (params.data.isMasterStatChild) {
              return params.data.accountName || params.data.department || '';
            }
            // For calculated accounts
            if (params.data.isCalculatedAccount) {
              return params.data.accountName || params.data.parentAccount || '';
            }
            // For regular leaf rows, return empty string (they're hidden via CSS)
            return '';
          }
          
          // Fallback
          return '';
        },
      },
      tooltipComponent: 'calculatedAccountTooltip',
      tooltipValueGetter: (params) => {
        // For calculated account leaf rows
        if (params.data?.isCalculatedAccount) {
          return 'show-tooltip';
        }
        
        // For group rows at level 1 (parentAccount groups), check if it's a master stat
        if (params.node?.group && params.node.level === 1) {
          // Check if any child has isMasterStatChild flag (master stat children are flagged with isMasterStatChild)
          const firstChild = params.node.allLeafChildren?.[0];
          if (firstChild?.data?.isMasterStatChild) {
            return 'show-tooltip';
          }
        }
        
        return null; // No tooltip for other rows
      },
    };
  }, []);

  const onColumnGroupOpened = (params) => {
    const {
      api,
      columnGroups,
    } = params;

    if (!columnGroups?.[0]?.children) return;

    let columnIds = [];
    columnGroups[0].children.forEach((column) => {
      columnIds.push(column.getId());
    });

    // Defer auto-sizing to avoid timing conflicts
    safeSetTimeout(() => {
      if (api) {
        api.autoSizeColumns(columnIds);
      }
    }, 0);

  }

  /**
   * Handle row group expansion for lazy loading of parent account children
   * When a user expands a parent account, load its children on-demand
   */
  const onRowGroupOpened = useCallback(async (params) => {
    if (!params.node.expanded) return; // Only on expand, not collapse

    // ── __NR__ / __BL__ wrapper groups: silently expand, no child cascade ──
    // These hidden wrapper groups (at level 1) expand to surface the level-2
    // department group rows. Do NOT call setExpanded on the level-2 children —
    // their group header row is already visible as a result of the wrapper
    // expanding, and expanding them would immediately be reversed by the
    // level-2 guard below, causing a visible flicker.
    if (params.node.group &&
        (params.node.key?.startsWith('__NR__') || params.node.key?.startsWith('__BL__'))) {
      return; // nothing else needed — the dept row group header surfaces automatically
    }

    // ── Level-2 dept groups under NR/BL: collapse immediately ────────────────
    // These are visible summary rows — they should not be expandable.
    // isGroupExpandable returns false, but setExpanded can still be called programmatically.
    // Collapse them immediately so no blank sticky row appears below.
    if (params.node.level === 2) {
      const leaves = params.node.allLeafChildren;
      if (leaves?.length > 0 && leaves.every(n => n.data?.isNetRevenueDepartment || n.data?.isBottomLineDepartment)) {
        params.node.setExpanded(false);
        return;
      }
    }
    // ── Level 3: accountName groups are leaf-level — collapse immediately ────
    // The accountName rowGroup creates a group node for each individual account,
    // but there is nothing below it to load. Prevent the expand from doing anything.
    // Exception: __NR__/__BL__ groups handled above.
    if (params.node.level === 3) {
      params.node.setExpanded(false);
      return;
    }

    // ── Level 0: Net Revenue / Bottom Line → auto-expand intermediate groups ──
    // When the user expands Net Revenue or Bottom Line at level 0, programmatically
    // expand all the hidden wrapper groups (__NR__<dept> / __BL__<dept>) at levels 1+
    // so the visible level-2 department group rows surface immediately.
    if (params.node.level === 0 && (params.node.key === 'Net Revenue' || params.node.key === 'Bottom Line')) {
      const prefix = params.node.key === 'Net Revenue' ? '__NR__' : '__BL__';
      const toExpand = [];
      params.api.forEachNode((node) => {
        if (node.group && node.key?.startsWith(prefix) && !node.expanded) {
          toExpand.push(node);
        }
      });
      toExpand.forEach((node) => node.setExpanded(true));
      return;
    }

    const nodeLevel = params.node.level;

    // ── Level 1: parentAccount → load department summaries ──────────────────
    if (nodeLevel === 1) {
      const parentAccount = params.node.key;
      if (!parentAccount) return;

      // Find the parent-total placeholder — it has hasChildren:true but its id does
      // NOT contain a colon (department rows use "parentAccount:deptId" format).
      // If we only find department rows (ids with colons), departments are already
      // loaded and we should not add them again.
      const allChildrenWithChildren = params.node.allLeafChildren?.filter(n => n.data?.hasChildren) || [];
      const parentTotalNode = allChildrenWithChildren.find(n => !String(n.data?.id || '').includes(':'));

      if (!parentTotalNode) {
        // Departments already loaded — nothing to do
        return;
      }
      const parentTotalData = parentTotalNode.data;

      // Helper: format a department summary row for the grid
      const formatDepartmentForGrid = (dept) => {
        const row = {
          // parentAccount must match the level-1 group key so AG Grid places this row
          // inside the correct parentAccount group. Do NOT set it to anything else.
          parentAccount,
          // department must match the level-2 group key exactly
          department: dept.department_id && dept.department_name
            ? `${dept.department_id} - ${dept.department_name}`
            : (dept.department_name || dept.department_id || ''),
          departmentId: dept.department_id,
          acctType: dept.acct_type || parentTotalData?.acctType,
          acctTypeSection: parentTotalData?.acctTypeSection,
          acctTypeSortOrder: parentTotalData?.acctTypeSortOrder,
          acctTypeSubOrder: parentTotalData?.acctTypeSubOrder,
          hasChildren: true,
          id: `${parentAccount}:${dept.department_id}`,
        };
        if (dept.dates) {
          Object.keys(dept.dates).forEach(dateString => {
            const monthYear = toMonthYear(dateString);
            row[monthYear] = dept.dates[dateString];
            row[dateString] = dept.dates[dateString];
          });
        }

        // Pre-compute change column values so they display before children are loaded.
        // For standard changes (percentage/absoluteValue), apply the operator to each
        // month's base value from dept.dates.
        (activeProjectAttributes?.changes || []).forEach(change => {
          if (!change.selected_accounts || !change.date_range) return;
          const { function_operator: op, operator_value: opVal, change_label: label } = change;
          if (op !== 'percentage' && op !== 'absoluteValue') return;

          const deptId = String(dept.department_id);

          // Only count selected accounts that belong to BOTH this department AND this
          // parent account. Without the parent account check, a change on Staff Salaries
          // (same dept IDs) would incorrectly show on Contract Services department rows.
          const accountsFullDetails = activeProjectAttributes?.accountsMetadata || [];
          const selectedAccountsInDept = change.selected_accounts.filter(glKey => {
            const parts = String(glKey).split('-');
            if (parts.length < 2 || parts[1] !== deptId) return false;
            // Verify the account belongs to this parent account
            const detail = accountsFullDetails.find(a => a.gl_key === glKey);
            return detail ? detail.parent_account === parentAccount : false;
          });

          if (selectedAccountsInDept.length === 0) return;

          let monthlyTotal = 0;
          if (dept.dates) {
            // Iterate only over months within the change's date_range, not all of
            // dept.dates. dept.dates spans the full project period; writing values
            // outside the change range would show phantom change amounts in months
            // the change was never intended to affect.
            const changeDateList = getMonthsBetweenDates(change.date_range[0], change.date_range[1]) || [];
            changeDateList.forEach(monthYear => {
              // dept.dates keys are yyyy-MM-dd; find the entry that maps to this monthYear
              const matchingKey = Object.keys(dept.dates).find(k => toMonthYear(k) === monthYear);
              const baseVal = matchingKey ? (Number(dept.dates[matchingKey]) || 0) : 0;
              let delta = 0;
              if (op === 'percentage') {
                // Percentage applies to the aggregated base value for the department
                delta = baseVal * ((Number(opVal) || 0) / 100);
              } else if (op === 'absoluteValue') {
                // Absolute value applies per selected account, so multiply by count
                delta = (Number(opVal) || 0) * selectedAccountsInDept.length;
              }
              row[`${label} ${monthYear}`] = delta;
              monthlyTotal += delta;
            });
          }
          if (monthlyTotal !== 0) {
            row[`${label} total`] = monthlyTotal;
          }
        });

        // Second pass: for dominant changes, store the adjusted column value
        // (entryDelta - standardDelta) under ${change.id} ${month}.
        {
          const allChanges = activeProjectAttributes?.changes || [];
          const standardChanges = allChanges.filter(c => !c.is_dominant_change);
          const dominantChanges = allChanges.filter(c => c.is_dominant_change);
          const deptId = String(dept.department_id);
          const accountsFullDetails = activeProjectAttributes?.accountsMetadata || [];

          dominantChanges.forEach(domChange => {
            const { function_operator: domOp, operator_value: domOpVal, date_range: domDateRange } = domChange;
            if (!domDateRange || (domOp !== 'percentage' && domOp !== 'absoluteValue')) return;

            const selectedAccountsInDept = domChange.selected_accounts.filter(glKey => {
              const parts = String(glKey).split('-');
              if (parts.length < 2 || parts[1] !== deptId) return false;
              const detail = accountsFullDetails.find(a => a.gl_key === glKey);
              return detail ? detail.parent_account === parentAccount : false;
            });
            if (selectedAccountsInDept.length === 0) return;

            const dateList = getMonthsBetweenDates(domDateRange[0], domDateRange[1]) || [];
            let adjustedTotal = 0;

            dateList.forEach(monthYear => {
              const matchingKey = dept.dates && Object.keys(dept.dates).find(k => toMonthYear(k) === monthYear);
              const baseVal = matchingKey ? (Number(dept.dates[matchingKey]) || 0) : 0;

              let entryDelta = 0;
              if (domOp === 'percentage') {
                entryDelta = baseVal * ((Number(domOpVal) || 0) / 100);
              } else if (domOp === 'absoluteValue') {
                entryDelta = (Number(domOpVal) || 0) * selectedAccountsInDept.length;
              }

              // Sum standard deltas already stored on this row
              let standardDelta = 0;
              standardChanges.forEach(sc => {
                const storedVal = row[`${sc.change_label} ${monthYear}`];
                if (storedVal !== undefined && storedVal !== null) {
                  standardDelta += Number(storedVal);
                }
              });

              const adjustedDelta = entryDelta - standardDelta;
              row[`${domChange.id} ${monthYear}`] = adjustedDelta;
              adjustedTotal += adjustedDelta;
            });

            if (adjustedTotal !== 0) {
              row[`${domChange.id} total`] = adjustedTotal;
            }
          });
        }

        // Pre-compute stat driver change values for driven accounts in this department.
        // For each stat driver change, sum up the pre-computed change values for all
        // driven accounts that belong to this department and parent account.
        (activeProjectAttributes?.changes || []).forEach(change => {
          if (!change.affects_stat_drivers || !change.affected_stat_drivers) return;
          const { change_label: label, date_range: dateRange } = change;
          if (!dateRange) return;

          const deptId = String(dept.department_id);
          const accountsFullDetails = activeProjectAttributes?.accountsMetadata || [];

          // Collect all driven account GL keys in this department that belong to this parent account
          const drivenGlKeysInDept = [];
          (change.affected_stat_drivers || []).forEach(driver => {
            (driver.drivenAccounts || []).forEach(a => {
              if (String(a.department_id) !== deptId) return;
              // Verify the driven account belongs to this parent account
              const detail = accountsFullDetails.find(d => d.gl_key === a.gl_key);
              if (detail && detail.parent_account === parentAccount) {
                drivenGlKeysInDept.push(a.gl_key);
              }
            });
          });

          if (drivenGlKeysInDept.length === 0) return;

          // Sum up pre-computed change values from eagerLoadedRowDataRef
          const dateList = getMonthsBetweenDates(dateRange[0], dateRange[1]) || [];
          let monthlyTotal = 0;
          dateList.forEach(month => {
            let monthSum = 0;
            drivenGlKeysInDept.forEach(glKey => {
              const eagerRow = eagerLoadedRowDataRef.current.get(glKey);
              if (eagerRow) {
                monthSum += Number(eagerRow[`${label} ${month}`]) || 0;
              }
            });
            if (monthSum !== 0) {
              row[`${label} ${month}`] = monthSum;
              monthlyTotal += monthSum;
            }
          });
          if (monthlyTotal !== 0) {
            row[`${label} total`] = monthlyTotal;
          }
        });

        return row;
      };

      // Re-use cache if available
      const cacheKey = `${activeProjectInfo?.id}:${parentAccount}:departments`;
      if (context.loadedParentChildren?.has(cacheKey)) {
        const cached = context.loadedParentChildren.get(cacheKey);
        if (cached?.length > 0 && gridRef.current?.api && parentTotalNode && parentTotalData) {
          gridRef.current.api.applyTransactionAsync(
            { remove: [parentTotalData], add: cached.map(formatDepartmentForGrid) },
            () => {
              // Do NOT null originalPlaceholderValuesRef here. Newly-added dept rows have
              // new IDs and will be snapshotted fresh by applyFiltersToRowData's has(row.id)
              // guard. Nulling would cause a re-snapshot from already-filtered values and
              // break the "clear filter restores originals" path.
              setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(parentAccount); return n; });
              // Always re-run filters so newly added rows respect any active filter
              // (external entity/dept filter AND native agTextColumnFilter on accountName/department)
              gridRef.current?.api?.onFilterChanged();
            }
          );
        }
        return;
      }

      debugLogger.log('GRID_LAZY_LOAD', 'Loading departments for parent account', { parentAccount });
      setExpandingParentsWithRef(prev => new Set(prev).add(parentAccount));

      try {
        const departments = await context.loadDepartmentsForParent(
          activeProjectInfo.id, parentAccount, activeUser.accessToken
        );

        if (departments?.length > 0 && gridRef.current?.api) {
          gridRef.current.api.applyTransactionAsync(
            { remove: [parentTotalData], add: departments.map(formatDepartmentForGrid) },
            () => {
              // Do NOT null originalPlaceholderValuesRef here. Newly-added dept rows have
              // new IDs and will be snapshotted fresh by applyFiltersToRowData's has(row.id)
              // guard. Nulling would cause a re-snapshot from already-filtered values and
              // break the "clear filter restores originals" path.
              setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(parentAccount); return n; });
              // Always re-run filters so newly added rows respect any active filter
              // (external entity/dept filter AND native agTextColumnFilter on accountName/department)
              gridRef.current?.api?.onFilterChanged();
            }
          );
        } else {
          setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(parentAccount); return n; });
        }
      } catch (error) {
        debugLogger.error('GRID_LAZY_LOAD', 'Failed to load departments', error);
        const msg = error.response?.data?.errors
          ? (typeof error.response.data.errors === 'string' ? error.response.data.errors : JSON.stringify(error.response.data.errors))
          : error.message || 'Unknown error';
        setLazyLoadError(`Failed to load departments for "${parentAccount}". ${msg}`);
        setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(parentAccount); return n; });
      }
      return;
    }

    // ── Level 2: department → load individual accounts ───────────────────────
    if (nodeLevel === 2) {
      const parentAccount = params.node.parent?.key;
      const departmentKey = params.node.key; // "D0100 - Cardiology"
      if (!parentAccount || !departmentKey) return;

      // Find the department placeholder row — it has hasChildren:true and its id
      // contains a colon ("parentAccount:deptId"). If only account rows (GL key ids,
      // no colon) are present, accounts are already loaded — skip.
      const allChildrenWithChildren = params.node.allLeafChildren?.filter(n => n.data?.hasChildren) || [];
      const deptPlaceholderNode = allChildrenWithChildren.find(n => String(n.data?.id || '').includes(':'));

      if (!deptPlaceholderNode) {
        // Accounts already loaded — nothing to do
        return;
      }
      const deptPlaceholderData = deptPlaceholderNode.data;

      // Extract departmentId from the placeholder data
      const departmentId = deptPlaceholderData?.departmentId;
      if (!departmentId) return;

      // Helper: format account row
      const formatChildForGrid = (child) => {
        // The department field must match the level-2 group key exactly so AG Grid
        // places this row inside the correct department group. We use the same
        // format as formatDepartmentForGrid: "D0100 - Cardiology"
        const deptGroupKey = child.department_id && child.department_name
          ? `${child.department_id} - ${child.department_name}`
          : (child.department_name || child.department_id || deptPlaceholderData?.department || '');

        const childRow = {
          glKey: child.gl_key,
          accountName: child.account_id && child.account_name
            ? `${child.account_id} - ${child.account_name}`
            : (child.account_name || ''),
          accountId: child.account_id,
          entity: child.entity_name || child.entity,
          entityId: child.entity_id,
          department: deptGroupKey,
          departmentId: child.department_id,
          acctType: child.acct_type || '',
          acctTypeSection: child.acct_type_section || deptPlaceholderData?.acctTypeSection,
          acctTypeSortOrder: child.acct_type_sort_order || deptPlaceholderData?.acctTypeSortOrder,
          acctTypeSubOrder: child.acct_type_sub_order || deptPlaceholderData?.acctTypeSubOrder,
          parentAccount,
          fixedOrVariable: child.fixed_or_variable || '',
          driver: child.driver || '',
          id: child.gl_key,
          // basePeriodDates is required by getDominantChange and changeTotal helpers
          basePeriodDates: child.dates || {},
          changes: [],
        };
        if (child.dates) {
          Object.keys(child.dates).forEach(dateString => {
            const monthYear = toMonthYear(dateString);
            childRow[monthYear] = child.dates[dateString];
            childRow[dateString] = child.dates[dateString];
          });
        }

        // Merge any pre-computed stat driver change values stored in eagerLoadedRowDataRef.
        // recalculateDrivenAccountsFromStatChanges stores change column fields (e.g.
        // "Stat Driver Change Jan 2024") there for rows that weren't in the grid yet.
        const eagerData = eagerLoadedRowDataRef.current.get(child.gl_key);
        if (eagerData) {
          const changeKeys = Object.keys(eagerData).filter(k => k !== 'glKey' && k !== 'id' && !(k in childRow));
          if (process.env.NODE_ENV !== 'production') {
            console.log(`[formatChildForGrid] Merging ${changeKeys.length} eager fields into ${child.gl_key}`, changeKeys.slice(0, 3));
          }
          changeKeys.forEach(key => {
            childRow[key] = eagerData[key];
          });
        }

        return childRow;
      };

      // Re-use cache if available
      const cacheKey = `${activeProjectInfo?.id}:${parentAccount}:${departmentId}:children`;
      if (context.loadedParentChildren?.has(cacheKey)) {
        const cached = context.loadedParentChildren.get(cacheKey);
        if (cached?.length > 0 && gridRef.current?.api && deptPlaceholderNode && deptPlaceholderData) {
          gridRef.current.api.applyTransactionAsync(
            { remove: [deptPlaceholderData], add: cached.map(formatChildForGrid) },
            () => {
              // Do NOT null originalPlaceholderValuesRef here. Leaf rows have no hasChildren
              // so they are never snapshotted by applyFiltersToRowData — no risk of stale
              // originals. Nulling would corrupt any in-progress snapshot for sibling rows.
              setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(departmentKey); return n; });
              // Always re-run filters so newly added rows respect any active filter
              // (external entity/dept filter AND native agTextColumnFilter on accountName/department)
              gridRef.current?.api?.onFilterChanged();
            }
          );
        }
        return;
      }

      debugLogger.log('GRID_LAZY_LOAD', 'Loading accounts for department', { parentAccount, departmentId });
      setExpandingParentsWithRef(prev => new Set(prev).add(departmentKey));

      try {
        const children = await context.loadAccountsForDepartment(
          activeProjectInfo.id, parentAccount, departmentId, activeUser.accessToken
        );

        if (children?.length > 0 && gridRef.current?.api) {
          gridRef.current.api.applyTransactionAsync(
            { remove: [deptPlaceholderData], add: children.map(formatChildForGrid) },
            () => {
              // Do NOT null originalPlaceholderValuesRef here. Leaf rows have no hasChildren
              // so they are never snapshotted by applyFiltersToRowData — no risk of stale
              // originals. Nulling would corrupt any in-progress snapshot for sibling rows.
              setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(departmentKey); return n; });
              // Always re-run filters so newly added rows respect any active filter
              // (external entity/dept filter AND native agTextColumnFilter on accountName/department)
              gridRef.current?.api?.onFilterChanged();
              // Defer until AG Grid finishes its current render cycle
              setTimeout(() => calculateDominantChanges(), 0);
            }
          );
        } else {
          setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(departmentKey); return n; });
        }
      } catch (error) {
        debugLogger.error('GRID_LAZY_LOAD', 'Failed to load dept accounts', error);
        const msg = error.response?.data?.errors
          ? (typeof error.response.data.errors === 'string' ? error.response.data.errors : JSON.stringify(error.response.data.errors))
          : error.message || 'Unknown error';
        setLazyLoadError(`Failed to load accounts for "${departmentKey}". ${msg}`);
        setExpandingParentsWithRef(prev => { const n = new Set(prev); n.delete(departmentKey); return n; });
      }
    }
  }, [context, activeProjectInfo, activeUser, gridRef, setExpandingParentsWithRef]);
  // Debounce timer ref for column changes
  const columnChangeTimerRef = useRef(null);

  const onGridColumnsChanged = (event) => {
    // Clear any pending auto-size operations
    if (columnChangeTimerRef.current) {
      clearTimeout(columnChangeTimerRef.current);
    }
    
    // Debounce auto-sizing to avoid multiple simultaneous calls during initialization
    columnChangeTimerRef.current = setTimeout(() => {
      autoSizeAll(false);
      columnChangeTimerRef.current = null;
    }, 100);
  }

  /**
   * Expands all hidden __NR__ / __BL__ wrapper groups (level-1) so that the
   * visible level-2 department group rows surface under Net Revenue and Bottom Line.
   * Also zeros out the row heights of all synthetic wrapper/footer rows so they
   * don't create blank gaps in the virtual scroll.
   *
   * Called from onFirstDataRendered (initial load) AND after the master-stats
   * setRowData refresh, which resets all group expansion state.
   *
   * @param {object} gridApi - AG Grid API instance
   */
  const expandNRBLWrappers = (gridApi) => {
    if (!gridApi || !isMountedRef.current) return;

    // Step 0: ensure level-0 Net Revenue / Bottom Line rows are collapsed by default.
    // groupDefaultExpanded=1 expands all level-0 groups on first render, so we
    // explicitly collapse these two synthetic summary rows here. After setRowData
    // resets expansion state they will naturally stay collapsed (no re-expansion needed).
    gridApi.forEachNode(n => {
      if (n.group && n.level === 0 && (n.key === 'Net Revenue' || n.key === 'Bottom Line') && n.expanded) {
        n.setExpanded(false);
      }
    });

    // Step 1: expand all level-1 __NR__/__BL__ wrapper nodes
    const wrappers = [];
    gridApi.forEachNode(n => {
      if (n.group && (n.key?.startsWith('__NR__') || n.key?.startsWith('__BL__')) && n.level === 1 && !n.expanded) {
        wrappers.push(n);
      }
    });
    wrappers.forEach(n => n.setExpanded(true));

    // Step 2: zero-out row heights for all hidden synthetic nodes so no blank gaps appear
    const fixHeights = () => {
      let changed = 0;
      gridApi.forEachNode(n => {
        if (n.group && (n.key?.startsWith('__NR__') || n.key?.startsWith('__BL__'))) {
          if (n.rowHeight !== 0) { n.rowHeight = 0; changed++; }
        }
        if (n.footer && (n.parent?.key?.startsWith('__NR__') || n.parent?.key?.startsWith('__BL__'))) {
          if (n.rowHeight !== 0) { n.rowHeight = 0; changed++; }
        }
        if (n.footer && (n.key === 'Net Revenue' || n.key === 'Bottom Line')) {
          if (n.rowHeight !== 0) { n.rowHeight = 0; changed++; }
        }
        if (n.footer && n.level === 2) {
          const leaves = n.allLeafChildren;
          if (leaves?.length > 0 && leaves.every(l => l.data?.isNetRevenueDepartment || l.data?.isBottomLineDepartment)) {
            if (n.rowHeight !== 0) { n.rowHeight = 0; changed++; }
          }
        }
        if (n.group && (n.key?.startsWith('__NR__') || n.key?.startsWith('__BL__') || n.key === 'Net Revenue' || n.key === 'Bottom Line')) {
          if (n.sibling?.footer && n.sibling.rowHeight !== 0) { n.sibling.rowHeight = 0; changed++; }
        }
      });
      if (changed > 0) gridApi.onRowHeightChanged();
    };

    // Brief delay so the setExpanded triggers complete before we fix heights
    setTimeout(() => {
      if (!isMountedRef.current) return;
      fixHeights();
      // Second pass in case any footer nodes were created after the first fix
      setTimeout(() => { if (isMountedRef.current) fixHeights(); }, 100);
    }, 50);
  };

  const onFirstDataRendered = async (event) => {
    if (!gridRef.current || !gridRef.current.api) {
      console.warn('Grid API not available for onFirstDataRendered');
      return;
    }

    autoSizeAll(false);
    setFirstRender(true);
    
    // Auto-expand the hidden __NR__ / __BL__ wrapper groups so the department
    // rows under Net Revenue and Bottom Line are immediately visible on load.
    // Use a short timeout so the initial groupDefaultExpanded cascade completes first.
    // All three setTimeout levels check isMountedRef so they don't fire on a
    // destroyed grid after a rapid project switch.
    const api = gridRef.current.api;
    // Small delay so groupDefaultExpanded=1 finishes expanding level-0 rows first
    setTimeout(() => { expandNRBLWrappers(api); }, 50);
  };

  const getRowId = useCallback((params) => {
    return params.data.id;
  }, []);

  /**
   * Tells AG Grid whether the external entity filter is currently active.
   * Called on every filter evaluation cycle.
   */
  const isExternalFilterPresent = useCallback(() => {
    return selectedEntityIdsRef.current.size > 0 || selectedDeptIdsRef.current.size > 0;
  }, []);

  /**
   * Per-node pass/fail for the combined entity + department external filter.
   * - Group nodes: always pass — AG Grid hides them when all children fail.
   * - Synthetic NR/BL rows: always pass — they recompute from visible leaves.
   * - Level-1 parent-account placeholder rows (hasChildren + department === 'Total'):
   *     always pass — these are account-type totals, not dept-specific.
   * - Level-2 department placeholder rows (hasChildren + department !== 'Total'):
   *     filtered by dept just like leaf rows — otherwise all dept summary rows
   *     remain visible after expanding even when a dept filter is active.
   * - Real leaf rows: pass only if they match ALL active filters.
   */
  const doesExternalFilterPass = useCallback((node) => {
    const entityIds = selectedEntityIdsRef.current;
    const deptIds   = selectedDeptIdsRef.current;
    if (entityIds.size === 0 && deptIds.size === 0) return true;
    const data = node.data;
    if (!data) return true; // group node
    if (data.isNetRevenueDepartment || data.isBottomLineDepartment) return true;
    // Level-1 parent-account placeholder rows have department === 'Total'.
    // These bypass filtering entirely — they aggregate across all departments.
    // Level-2 department placeholder rows have a real department value and must
    // be filtered by dept so collapsed department rows respect the dept filter.
    if (data.hasChildren && data.department === 'Total') return true;
    if (entityIds.size > 0 && !entityIds.has(String(data.entityId))) return false;
    if (deptIds.size > 0 && !deptIds.has(String(data.department))) return false;
    return true;
  }, []);

  /**
   * Handle filter changes to refresh summary row calculations
   * When filters are applied, the summary rows (Net Revenue, Bottom Line, etc.)
   * need to recalculate based on the visible/filtered data
   */
  const onFilterChanged = useCallback((params) => {
    const api = params?.api ?? gridRef.current?.api;
    if (!api) return;
    
    // Invalidate Net Revenue value cache so recalculations use the filtered data set
    netRevenueCacheRef.current = {};
    bottomLineCacheRef.current = {};
    
    // Force refresh of all cells to recalculate aggregations with filtered data.
    // This single pass covers summary rows, group nodes, and leaf cells together,
    // so a second targeted refresh of summaryNodes is not needed.
    api.refreshCells({ force: true });
  }, []);

  /**
   * Invalidate the Net Revenue cache and refresh summary cells whenever an
   * individual cell value changes (e.g., after a user edit or applied change).
   */
  const onCellValueChanged = useCallback((params) => {
    if (!params.api) return;
    netRevenueCacheRef.current = {};
    bottomLineCacheRef.current = {};
    params.api.refreshCells({ force: true });
  }, []);

  /**
   * Determines the CSS class for a row based on its properties
   * @param {Object} params - AG Grid row params
   * @returns {String} CSS class name(s)
   */
  const getRowClass = (params) => {
    // Hide Net Revenue / Bottom Line footer rows (we only want the group rows)
    if (params.node?.footer && (params.node?.key === 'Net Revenue' || params.node?.key === 'Bottom Line')) {
      return 'ag-hidden-net-revenue-footer';
    }
    
    // Hide all level-0 section footer rows — the total is now shown on the group header row.
    // This covers Net Patient Revenue, Other Revenue, Operating Expenses, Statistics,
    // Calculated Accounts, and any future sections.
    if (params.node?.footer && params.node.level === 0) {
      return 'ag-hidden-net-revenue-footer';
    }
    
    // Style the top-level Net Revenue group row like an account-type summary row
    if (params.node?.group && params.node.level === 0 && params.node?.key === 'Net Revenue') {
      return 'ag-row-net-revenue-summary';
    }
    
    // Style the top-level Bottom Line group row like an account-type summary row
    if (params.node?.group && params.node.level === 0 && params.node?.key === 'Bottom Line') {
      return 'ag-row-bottom-line-summary';
    }
    
    // Hide the unique-prefix group nodes (levels 1 and 3) that wrap synthetic dept rows.
    // These use __NR__<dept> and __BL__<dept> keys so they never collide with real data.
    // The level-2 group (real department key) is the intended visible department row.
    if (params.node?.key?.startsWith('__NR__') || params.node?.key?.startsWith('__BL__')) {
      return 'ag-hidden-net-revenue-footer';
    }
    // Hide footer rows that groupTotalRow generates for the __NR__/__BL__ wrapper groups
    if (params.node?.footer && (params.node?.key?.startsWith('__NR__') || params.node?.key?.startsWith('__BL__'))) {
      return 'ag-hidden-net-revenue-footer';
    }
    // Hide footer rows that groupTotalRow generates for the dept-level groups under
    // Net Revenue / Bottom Line (no Total row wanted under each department).
    if (params.node?.footer && params.node?.parent?.key?.startsWith('__NR__')) {
      return 'ag-hidden-net-revenue-footer';
    }
    if (params.node?.footer && params.node?.parent?.key?.startsWith('__BL__')) {
      return 'ag-hidden-net-revenue-footer';
    }
    // Hide level-4 accountName group nodes that sit under the dept group (level 3 now
    // becomes level 3 under the dept level-2 node) — these use the same __NR__/__BL__ key.
    // Already caught by the startsWith check above.
    
    // Hide intermediate group rows that carry the literal 'Bottom Line' key at level > 0.
    if (params.node?.group && params.node.level > 0 && params.node?.key === 'Bottom Line') {
      return 'ag-hidden-net-revenue-footer';
    }
    // Same for Net Revenue intermediate group rows (only level 0 should show the label).
    if (params.node?.group && params.node.level > 0 && params.node?.key === 'Net Revenue') {
      return 'ag-hidden-net-revenue-footer';
    }
    
    // Master Stat group rows (level 1 parentAccount groups)
    if (params.node?.group && params.node.level === 1) {
      // Check if this is a master stat group by looking at the first child
      const firstChild = params.node.allLeafChildren?.[0];
      if (firstChild?.data?.isMasterStatChild) {
        return 'ag-row-master-stat';
      }
    }
    
    // Calculated accounts are interactive leaf rows - keep them visible
    // and apply their special styles BEFORE the generic leaf-row hide check
    if (params.data?.isCalculatedAccount) {
      return 'ag-row-calculated-account';
    }
    
    // Child stats under Master Stats - use explicit flag to avoid incorrectly styling regular Stat accounts
    if (params.data?.isMasterStatChild) {
      return 'ag-row-master-stat-child';
    }
    // Level-2 department group nodes under Net Revenue / Bottom Line — these are the
    // visible summary dept rows. Hide their expand icons and prevent the double arrow.
    if (params.node?.group && params.node.level === 2) {
      const leaves = params.node.allLeafChildren;
      if (leaves?.length > 0 && leaves.every(l => l.data?.isNetRevenueDepartment || l.data?.isBottomLineDepartment)) {
        return 'ag-row-summary-dept';
      }
    }
    
    // Net Revenue department rows (leaf) - keep visible as summary rows
    if (params.data?.isNetRevenueDepartment) {
      return 'ag-row-summary-dept';
    }
    // Bottom Line department rows (leaf) - keep visible as summary rows
    if (params.data?.isBottomLineDepartment) {
      return 'ag-row-summary-dept';
    }
    
    // Hide all other leaf data rows - department group rows are the visible bottom level
    if (!params.node.group) {
      return 'ag-hidden-leaf-row';
    }
    
    return '';
  };

  // Memoize grid context to prevent unnecessary re-renders
  // Must be at top level to avoid Hook violation
  // Use stable IDs instead of arrays to prevent unnecessary updates
  const gridContext = useMemo(
    () => ({
      changes: activeProjectAttributes?.changes || [],
      masterStats: activeProjectAttributes?.masterStats || [],
      projectId: activeProjectInfo?.id,
      fetchAndCacheAuditTrail: context.fetchAndCacheAuditTrail,
      fetchAndCacheMasterStatAuditTrail: context.fetchAndCacheMasterStatAuditTrail,
      accessToken: activeUser?.accessToken,
      calculatingChangeIds,
    }),
    [
      changesIds,
      activeProjectAttributes?.masterStats?.length,
      activeProjectInfo?.id,
      context.fetchAndCacheAuditTrail,
      context.fetchAndCacheMasterStatAuditTrail,
      activeUser?.accessToken,
      calculatingChangeIds,
    ],
  );

  // ── Entity filter: ref holding original (unfiltered) placeholder row values ───
  // Keyed by row id (parent account name), value is { [MMM yyyy]: number, 'base total': number }.
  // Populated on first entity filter activation; cleared when project changes or
  // accountsMetadata is updated (GL key bulk edits, etc.).
  const originalPlaceholderValuesRef = useRef(null);

  useEffect(() => {
    // Invalidate the snapshot whenever the underlying account data changes so the
    // next filter activation re-snapshots from the freshest row data.
    originalPlaceholderValuesRef.current = null;
  }, [context.agGridData]);

  /**
   * Recomputes base-period values for every hasChildren placeholder row based on
   * the currently active filters. Makes one getAccountsByGlKeys API call with
   * the combined filtered GL key set, then sums by parent.
   * When entity and dept filters are both empty, restores the original snapshotted values.
   *
   * Accepts the full activeFilters object so future filter types (taxonomy, etc.)
   * can be added here without changing the call signature.
   *
   * @param {object} filters — activeFilters snapshot: { entityIds, deptIds, ... }
   */
  const applyFiltersToRowData = useCallback(async (filters) => {
    const { entityIds, deptIds } = filters;
    const noFilter = entityIds.size === 0 && deptIds.size === 0;

    // Read current row data directly from the grid API to avoid stale closure issues
    const currentRows = [];
    if (gridRef.current?.api) {
      gridRef.current.api.forEachNode(node => { if (node.data) currentRows.push(node.data); });
    }
    const placeholderRows = currentRows.filter(r => r.hasChildren && (r.child_accounts?.length > 0));

    if (placeholderRows.length === 0) return;

    // Snapshot originals on first filter activation
    if (!originalPlaceholderValuesRef.current) {
      originalPlaceholderValuesRef.current = new Map();
    }
    placeholderRows.forEach(row => {
      if (!originalPlaceholderValuesRef.current.has(row.id)) {
        const snap = {};
        Object.keys(row).forEach(k => {
          if (/^[A-Z][a-z]{2} \d{4}$/.test(k) || k === 'base total') snap[k] = row[k];
        });
        originalPlaceholderValuesRef.current.set(row.id, snap);
      }
    });

    // No filter active — restore originals and return
    if (noFilter) {
      setRowData(prev => prev.map(row => {
        if (!row.hasChildren) return row;
        const orig = originalPlaceholderValuesRef.current?.get(row.id);
        return orig ? { ...row, ...orig } : row;
      }));
      return;
    }

    // Gather child GL keys that pass both filters.
    // GL key format: "entityId-deptId-accountId"
    // row.department format: "deptId - deptName"  (matches departmentsList strings)
    // We need the accounts_full_details to know which GL key belongs to which dept.
    // Instead: use child_accounts (gl keys) — extract entity from prefix,
    // then resolve dept by looking at the lazy-loaded row data if available,
    // or fall back to filtering only on entity if dept info isn't resolvable from the key.
    //
    // The cleanest approach: fetch all relevant GL keys first (filtered by entity),
    // then from the API response filter further by department name using the
    // returned account.department_id / account.department_name fields.
    const entityFilteredKeys = new Set();
    placeholderRows.forEach(row => {
      (row.child_accounts || []).forEach(glKey => {
        // Always apply entity filter if active
        if (entityIds.size > 0 && !entityIds.has(String(glKey).split('-')[0])) return;
        entityFilteredKeys.add(glKey);
      });
    });

    if (entityFilteredKeys.size === 0) {
      setRowData(prev => prev.map(row => {
        if (!row.hasChildren) return row;
        const orig = originalPlaceholderValuesRef.current?.get(row.id) || {};
        const zeroed = {};
        Object.keys(orig).forEach(k => { zeroed[k] = 0; });
        return { ...row, ...zeroed };
      }));
      return;
    }

    // One API call — fetch full monthly data for entity-filtered GL keys
    const response = await getAccountsByGlKeys(
      activeProjectInfo?.id,
      [...entityFilteredKeys],
      activeUser?.accessToken
    );

    if (!response?.data?.accounts) {
      console.warn('[FilterDrawer] getAccountsByGlKeys returned no data');
      return;
    }

    // Build glKey → { [MMM yyyy]: number }, applying dept filter at this stage
    // so we only sum months for accounts in the selected departments.
    const glKeyValues = new Map();
    response.data.accounts.forEach(account => {
      if (!account.gl_key) return;
      // Department filter: account returns department_id + department_name
      if (deptIds.size > 0) {
        const deptKey = `${account.department_id} - ${account.department_name}`;
        if (!deptIds.has(deptKey)) return; // skip this account
      }
      const monthMap = {};
      Object.entries(account.dates || {}).forEach(([dateStr, val]) => {
        const mk = toMonthYear(dateStr);
        monthMap[mk] = (monthMap[mk] || 0) + (Number(val) || 0);
      });
      glKeyValues.set(account.gl_key, monthMap);
    });

    // Recompute each placeholder row's base period values
    setRowData(prev => prev.map(row => {
      if (!row.hasChildren) return row;

      // Only consider child keys that are in glKeyValues (passed both filters)
      const relevantKeys = (row.child_accounts || []).filter(k => glKeyValues.has(k));

      if (relevantKeys.length === 0) {
        const orig = originalPlaceholderValuesRef.current?.get(row.id) || {};
        const zeroed = {};
        Object.keys(orig).forEach(k => { zeroed[k] = 0; });
        return { ...row, ...zeroed };
      }

      const newVals = {};
      relevantKeys.forEach(glKey => {
        const months = glKeyValues.get(glKey);
        if (!months) return;
        Object.entries(months).forEach(([mk, v]) => { newVals[mk] = (newVals[mk] || 0) + v; });
      });
      newVals['base total'] = Object.values(newVals).reduce((s, v) => s + v, 0);
      return { ...row, ...newVals };
    }));
  }, [activeProjectInfo?.id, activeUser?.accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run filter whenever entity, dept, or other row-affecting filters change.
  // Debounced 150ms to coalesce back-to-back setter calls (e.g. clear-all
  // calls setActiveFilters once but previously set two separate states).
  useEffect(() => {
    const timer = setTimeout(() => {
      // Invalidate the NR/BL value cache immediately — before the async API call —
      // so any cell render during the API flight recomputes from visible nodes rather
      // than serving the pre-filter cached total (Gap D fix).
      netRevenueCacheRef.current = {};
      bottomLineCacheRef.current = {};
      applyFiltersToRowData(activeFilters).then(() => {
        // Defer until after React commits the setRowData update.
        setTimeout(() => {
          const currentChanges = activeProjectAttributes?.changes;
          if (currentChanges?.length > 0) {
            refreshPlaceholderRowChangeValues(currentChanges);
          }
          // Caches were already cleared above; clear again in case a concurrent
          // operation repopulated them while the API call was in-flight.
          netRevenueCacheRef.current = {};
          bottomLineCacheRef.current = {};
          if (gridRef.current?.api) {
            gridRef.current.api.onFilterChanged();
            gridRef.current.api.refreshCells({ force: true });
          }
        }, 0);
      });
    }, 150);
    return () => clearTimeout(timer);
  }, [activeFilters.entityIds, activeFilters.deptIds]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply date range filter by toggling monthly column visibility across all
  // column types (base period, forecast period, and change columns) and forcing
  // a cell refresh so Total columns re-run their valueGetters.
  useEffect(() => {
    if (!gridRef.current?.api || !monthPickerRange || !effectiveDateRange) return;
    const api = gridRef.current.api;
    const allCols = api.getColumnState();

    // Extract the "MMM yyyy" portion from any colId — handles all three patterns:
    //   "Jan 2023"            (base period monthly)
    //   "Jan 2023 forecast"   (forecast period monthly)
    //   "<changeId> Jan 2023" (change monthly)
    // Total columns ("forecast total", "<id> total") contain no month pattern and are skipped.
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthRe = /([A-Z][a-z]{2} \d{4})/;
    const colToNum = (colId) => {
      const m = colId.match(/([A-Za-z]{3}) (\d{4})/);
      if (!m) return null;
      const idx = monthNames.indexOf(m[1]);
      if (idx === -1) return null;
      return Number(m[2]) * 100 + (idx + 1);
    };

    const fromNum = effectiveDateRange.from.year * 100 + effectiveDateRange.from.month;
    const toNum   = effectiveDateRange.to.year   * 100 + effectiveDateRange.to.month;

    const updates = allCols
      .filter(c => monthRe.test(c.colId))
      .map(c => {
        const n = colToNum(c.colId);
        if (n === null) return null;
        return { key: c.colId, visible: n >= fromNum && n <= toNum };
      })
      .filter(Boolean);

    const toShow = updates.filter(u => u.visible).filter(u => {
      // Don't re-show monthly columns belonging to changes that are currently hidden
      // via disabledChangeIds — the disabledChangeIds effect owns their visibility.
      const leadingNum = parseInt(u.key.split(' ')[0], 10);
      return isNaN(leadingNum) || !disabledChangeIdsRef.current.has(leadingNum);
    }).map(u => u.key);
    const toHide = updates.filter(u => !u.visible).map(u => u.key);
    if (toShow.length) api.setColumnsVisible(toShow, true);
    if (toHide.length) api.setColumnsVisible(toHide, false);

    // Force Total columns to re-run their valueGetters with the new date range
    api.refreshCells({ force: true });
  }, [effectiveDateRange, monthPickerRange]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* ── Filter drawer (full-width, slides down from nav) ──────────── */}
      <ProjectHomeFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        activeProjectInfo={activeProjectInfo}
        activeProjectAttributes={activeProjectAttributes}
        appliedFilters={activeFilters}
        onApplyFilters={(patch) => setActiveFilters(prev => ({ ...prev, ...patch }))}
        monthPickerRange={monthPickerRange}
        savedViews={savedViews}
        savedViewsSaving={savedViewsSaving}
        onSaveView={handleSaveView}
        onSetDefaultView={handleSetDefaultView}
        onRenameView={handleRenameSavedView}
        onDeleteView={handleDeleteSavedView}
      />

      {/* ── Compact filter bar ────────────────────────────────────────── */}
      {(() => {
        const { entityIds, deptIds, dateRange, disabledChangeIds: hiddenChanges } = activeFilters;
        const activeCount = entityIds.size + deptIds.size + (dateRange ? 1 : 0) + hiddenChanges.size;
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1,
              py: 0.5,
              borderBottom: '1px solid #e0e0e0',
              background: '#fafafa',
              minHeight: 40,
              flexWrap: 'wrap',
              ...(filterPulseActive && activeCount > 0 && {
                animation: 'filter-bar-pulse 1.4s ease-in-out 3',
              }),
            }}
          >
            {/* Filter trigger button */}
            <Tooltip title="Filters & Columns">
              <Badge badgeContent={activeCount || null} color="primary" overlap="circular">
                <IconButton
                  size="small"
                  onClick={() => setFilterDrawerOpen(v => !v)}
                  sx={{
                    color: activeCount > 0 ? 'primary.main' : 'action.active',
                    '&:hover': { background: 'rgba(25, 118, 210, 0.08)' },
                  }}
                  aria-label="Open filters and columns"
                >
                  <FilterListIcon fontSize="small" />
                </IconButton>
              </Badge>
            </Tooltip>

            {/* Active filter chips — one per active filter dimension */}
            {activeCount === 0 ? (
              <Typography
                variant="body2"
                onClick={() => setFilterDrawerOpen(v => !v)}
                sx={{ color: 'text.secondary', fontSize: 13, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
              >
                All data shown
              </Typography>
            ) : (
              <>
                {entityIds.size > 0 && (
                  <Chip size="small"
                    label={`${entityIds.size} entit${entityIds.size === 1 ? 'y' : 'ies'}`}
                    color="primary" variant="outlined"
                    onDelete={() => setActiveFilters(prev => ({ ...prev, entityIds: new Set() }))}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {deptIds.size > 0 && (
                  <Chip size="small"
                    label={`${deptIds.size} dept${deptIds.size === 1 ? '' : 's'}`}
                    color="secondary" variant="outlined"
                    onDelete={() => setActiveFilters(prev => ({ ...prev, deptIds: new Set() }))}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {dateRange && effectiveDateRange && (
                  <Chip size="small"
                    label={`${months[effectiveDateRange.from.month - 1]}. ${effectiveDateRange.from.year} – ${months[effectiveDateRange.to.month - 1]}. ${effectiveDateRange.to.year}`}
                    color="info" variant="outlined"
                    onDelete={() => setActiveFilters(prev => ({ ...prev, dateRange: null }))}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {hiddenChanges.size > 0 && (
                  <Chip size="small"
                    label={`${hiddenChanges.size} change${hiddenChanges.size === 1 ? '' : 's'} hidden`}
                    color="warning" variant="outlined"
                    onDelete={() => setActiveFilters(prev => ({ ...prev, disabledChangeIds: new Set() }))}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {/* Clear all — resets every filter dimension at once */}
                <Button size="small" variant="text"
                  onClick={() => setActiveFilters(makeEmptyFilters())}
                  sx={{ fontSize: 11, py: 0, minWidth: 0, ml: 0.25, color: 'text.secondary' }}
                >
                  Clear all
                </Button>
              </>
            )}
          </Box>
        );
      })()}

      <div
        className='project-home-ag-grid ag-theme-alpine'
        style={{
          height: 'calc(100vh - 240px)',
          textAlign: 'left',
          width: '100%'
        }}>

        {/* Don't render grid until we have column definitions - prevents premature getRows calls */}
        {/* Note: GridLoadingOverlay handles all loading states, so we don't show a separate message here */}
        {columnDefs.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center', visibility: 'hidden' }}>
            {/* Placeholder to maintain layout - actual loading shown by GridLoadingOverlay */}
          </div>
        ) : (
          <AgGridReact
          onGridReady={onGridReady}
          getRowId={getRowId}
          getRowClass={getRowClass}
          animateRows={true} // Optional - set to 'true' to have rows animate when sorted
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={columnDefs} // Column Defs for Columns
          // columnDefs={columns}
          columns
          columnTypes={columnTypes} // Custom column types
          components={{
            calculatedAccountTooltip: CalculatedAccountTooltip,
            changeColumnTooltip: ChangeColumnTooltip,
            changeColumnHeader: ChangeColumnHeader,
            parentChangeGroupHeader: ParentChangeGroupHeader,
          }}
          context={gridContext}
          defaultColDef={defaultColDef} // Default Column Properties
          enableRangeHandle={true}
          enableRangeSelection={true}
          enableRowGroup={true}
          getContextMenuItems={getContextMenuItems}
          getMainMenuItems={getMainMenuItems}
          groupDisplayType={'singleColumn'}
          groupDefaultExpanded={1}
          isGroupExpandable={(params) => {
            // Level-1 __NR__/__BL__ wrapper groups and level-2 department group nodes
            // under Net Revenue / Bottom Line should not show expand arrows.
            // Level-1 wrappers are hidden via CSS; level-2 dept rows are the visible
            // summary rows that should appear as non-expandable leaves.
            // Level-0 Net Revenue / Bottom Line are explicitly expandable — they are
            // summary nodes whose only leaf children are the synthetic dept rows, which
            // would otherwise trigger the "all leaves are synthetic" guard below.
            if (params.node?.group) {
              const key = params.node.key;
              const level = params.node.level;
              // Always allow level-0 summary rows to expand
              if (level === 0 && (key === 'Net Revenue' || key === 'Bottom Line')) return true;
              if (key?.startsWith('__NR__') || key?.startsWith('__BL__')) return false;
              const leaves = params.node.allLeafChildren;
              if (leaves?.length > 0 && leaves.every(n => n.data?.isNetRevenueDepartment || n.data?.isBottomLineDepartment)) {
                return false;
              }
            }
            return true;
          }}
          groupTotalRow={'bottom'}
          groupRowsSticky={true}
          groupHeaderHeight={32} // Compact group header height
          headerHeight={32} // Compact header height
          suppressAggFuncInHeader={true}
          onColumnGroupOpened={onColumnGroupOpened}
          onColumnVisible={onColumnVisible}
          onRowGroupOpened={onRowGroupOpened}
          onCellClicked={cellClickedListener} // Optional - registering for Grid Event
          onCellValueChanged={onCellValueChanged} // Invalidate Net Revenue cache on edits
          onFilterChanged={onFilterChanged} // Refresh summary rows when filters change
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridColumnsChanged={onGridColumnsChanged}
          onFirstDataRendered={onFirstDataRendered}
          ref={gridRef} // Ref for accessing Grid's API
          rowData={rowData}
          getRowHeight={(params) => {
            // Zero-height all level-0 section footer rows — they are hidden and replaced
            // by the group header row which now shows the aggregated total.
            if (params.node?.footer && params.node.level === 0) return 0;
            // Return 0 height for footer rows belonging to __NR__ / __BL__ wrapper groups
            // and for footer rows of the level-2 synthetic dept groups. This prevents
            // blank gaps in the virtual scroll even though these rows are display:none.
            if (params.node?.footer) {
              const parentKey = params.node.parent?.key;
              if (parentKey?.startsWith('__NR__') || parentKey?.startsWith('__BL__')) return 0;
              // Also zero-height the level-1 __NR__/__BL__ group rows themselves
              if (params.node.key?.startsWith('__NR__') || params.node.key?.startsWith('__BL__')) return 0;
              // Footer for the level-2 dept groups under NR/BL
              const leaves = params.node.allLeafChildren;
              if (leaves?.length > 0 && leaves.every(l => l.data?.isNetRevenueDepartment || l.data?.isBottomLineDepartment)) return 0;
            }
            // Also zero-height the __NR__/__BL__ group rows (levels 1 and 3)
            if (params.node?.group) {
              const key = params.node.key;
              if (key?.startsWith('__NR__') || key?.startsWith('__BL__')) return 0;
            }
            return 28;
          }}
          // rowGroupPanelShow={'always'}
          rowGroupPanelShow={'never'}
          rowSelection='multiple' // Options - allows click selection of rows
          suppressLoadingOverlay={true} // Disable AG Grid's built-in loading overlay - we use GridLoadingOverlay instead
          suppressReactUi={true} // Prevent AG Grid React from using internal React state that causes render conflicts
          sideBar={sideBarDef} // Set options for sidebar
          tooltipShowDelay={900} // Snappy enough to feel responsive without accidental triggers
          tooltipHideDelay={5000} // Keep tooltip visible long enough to read
          tooltipMouseTrack={false} // Tooltips stay in place instead of following cursor
          tooltipInteraction={true} // Keep tooltip alive while cursor is over it
        />
        )}
        
        {/* Loading indicator for lazy loading parent expansions */}
        {expandingParents.size > 0 && (
          <div style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            background: 'rgba(255, 255, 255, 0.95)',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '12px 16px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            zIndex: 1000,
            fontSize: '14px'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid #f3f3f3',
              borderTop: '2px solid #3498db',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span>
              Loading {expandingParents.size} parent account{expandingParents.size > 1 ? 's' : ''}...
            </span>
          </div>
        )}

        {/* Error snackbar for lazy-load failures */}
        <Snackbar
          open={!!lazyLoadError}
          autoHideDuration={SNACKBAR_ERROR_DURATION_MS}
          onClose={() => setLazyLoadError(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setLazyLoadError(null)}
            severity="error"
            sx={{ width: '100%' }}
          >
            {lazyLoadError}
          </Alert>
        </Snackbar>

      </div>
    </div>
  );

  /**
   * Applies account change data to column configuration for a data grid
   * Creates monthly columns and a total column, then inserts them into the existing columns array
   *
   * @param {Array} columns - The existing columns array to modify
   * @param {Object|Array} accountChange - The account change data object or array of changes with same label
   * @param {Array} accountChange.date_range - Start and end dates [startDate, endDate]
   * @param {string} accountChange.id - Unique identifier for the change
   * @param {string} accountChange.change_label - Display label for the change
   * @param {string} accountChange.parent_change - Parent change identifier (optional)
   */

  /**
   * Creates column definitions for each month in the date range
   *
   * @param {Array} monthlyDates - Array of month strings
   * @param {Object|Array} accountChange - The account change data object or array of changes
   * @returns {Array} Array of monthly column definitions
   */
  function createMonthlyColumns(monthlyDates, accountChange) {
    const changes = Array.isArray(accountChange) ? accountChange : [accountChange];
    const primaryChange = changes[0];
    const changeLabel = primaryChange.change_label;

    return monthlyDates.map(month => ({
      changeId: primaryChange.id,
      colId: `${changeLabel} ${month}`,
      columnGroupShow: 'open',
      field: `${changeLabel} ${month}`,
      headerName: month,
      resizable: true,
      type: 'numericRight',
      aggFunc: netRevenueAggFunc,
      valueFormatter: currencyFormatter,
      valueGetter: (params) => {
        // For all rows (including Master Stats), just read the stored value
        // Master Stat change values should be pre-calculated and stored when changes are applied
        const fieldName = `${changeLabel} ${month}`;
        const value = params.data?.[fieldName];

        return value !== undefined ? value : null;
      }
    }));
  }

  /**
   * Creates the total column definition
   *
   * @param {Object|Array} accountChange - The account change data object or array of changes
   * @param {Array} monthlyColumns - Array of monthly column definitions
   * @returns {Object} Total column definition
   */
  function createTotalColumn(accountChange, monthlyColumns) {
    const changes = Array.isArray(accountChange) ? accountChange : [accountChange];
    const primaryChange = changes[0];
    const changeLabel = primaryChange.change_label;

    return {
      aggFunc: (params) => {
        // Check if this is the "Net Patient Revenue" section at level 0
        if (params.rowNode.level === 0 && params.rowNode.key === 'Net Patient Revenue') {
          const fieldName = `${changeLabel} total`;
          
          let revenueSum = 0;
          let deductionSum = 0;
          
          // Use filter-aware childrenAfterFilter traversal instead of allLeafChildren
          const filteredLeaves = getFilteredLeafChildren(params.rowNode);
          filteredLeaves.forEach(child => {
            const value = child.data?.[fieldName];
            if (value !== undefined && value !== null) {
              const acctType = child.data?.acctType;
              if (acctType === 'Revenue' || acctType === 'Rev') {
                revenueSum += value;
              } else if (acctType === 'Deduction' || acctType === 'Ded') {
                deductionSum += value;
              }
            }
          });
          
          return revenueSum - deductionSum;
        }
        
        // For leaf rows, use the pre-computed stored value — it's the authoritative
        // result of calculateSingleMonthChange and avoids recomputing the formula.
        // For group (non-leaf) rows, always prefer summing child values so that AG
        // Grid's own aggregation reflects the actual children currently in the grid,
        // rather than a stale pre-stamped total that may not include new changes added
        // after the placeholder row was first constructed.
        const fieldName = `${changeLabel} total`;

        if (!params.rowNode.group && params.rowNode.data && params.rowNode.data[fieldName] !== undefined) {
          // Leaf row: trust the stored value
          return params.rowNode.data[fieldName];
        }
        // Group row (or leaf with no stored value): sum children
        if (params.values && params.values.length > 0) {
          return params.values.reduce((sum, val) => sum + (val || 0), 0);
        }
        // Final fallback: use stored value even for group rows (e.g. placeholder rows
        // whose children haven't been lazy-loaded yet and have no child values to sum)
        if (params.rowNode.data && params.rowNode.data[fieldName] !== undefined) {
          return params.rowNode.data[fieldName];
        }
        return null;
      },
      changeId: primaryChange.id,
      columnGroupShow: 'closed',
      field: `${changeLabel} total`,
      headerName: 'Total',
      resizable: true,
      type: ['numericRight', 'changeTotal'],
      valueGetter: (params) => {
        // First, try to get value from row data (for parent rows with pre-calculated values)
        const fieldName = `${changeLabel} total`;

        if (params.data && params.data[fieldName] !== undefined) {
          return params.data[fieldName];
        }

        // For leaf rows, calculate the value
        if (!params.data || !params.data.id) {
          return null;
        }

        // Aggregate totals across all changes with the same label
        let aggregatedTotal = 0;
        let hasAnyValue = false;

        changes.forEach(change => {
          const total = changeTotal(params, change);
          if (total !== '' && total !== null && total !== undefined) {
            aggregatedTotal += total;
            hasAnyValue = true;
          }
        });

        return hasAnyValue ? aggregatedTotal : null;
      }
    };
  }

  /**
   * Creates the main column group that contains all child columns
   *
   * @param {Object|Array} accountChange - The account change data or array of changes
   * @param {Array} childColumns - Array of child column definitions
   * @returns {Object} Column group definition
   */
  function createChangeColumnGroup(accountChange, childColumns) {
    const changes = Array.isArray(accountChange) ? accountChange : [accountChange];
    const primaryChange = changes[0];
    const changeLabel = primaryChange.change_label;

    return {
      children: childColumns,
      colId: changeLabel,
      field: changeLabel,
      groupId: changeLabel,
      headerName: changeLabel,
      marryChildren: true,
      openByDefault: false,
      resizable: true,
      type: 'numericRight',
      valueFormatter: currencyFormatter,
      metadata: {
        changeId: primaryChange.id,
        changeIds: changes.map(c => c.id), // Store all change IDs for this label
        parent_change: primaryChange.parent_change,
      },
    };
  }

  /**
   * Inserts a column group into the columns array at the appropriate position
   * 
   * @param {Array} columns - The columns array to modify
   * @param {Object} columnGroup - The column group to insert
   * @param {string} parentChangeId - ID of the parent change (if any)
   */
  function insertColumnIntoArray(columns, columnGroup, parentChangeId) {
    // Try to find parent change and add as child
    if (parentChangeId) {
      const parentColumn = findColumnByField(columns, parentChangeId);
      if (parentColumn) {
        // Ensure parent has children array
        if (!parentColumn.children) {
          parentColumn.children = [];
        }
        parentColumn.children.push(columnGroup);
        return;
      }
    }

    // If no parent found, insert after basePeriod column or at the end
    insertAfterBasePeriodOrEnd(columns, columnGroup);
  }

  /**
   * Finds a column by its field property
   * 
   * @param {Array} columns - Array of columns to search
   * @param {string} fieldName - Field name to search for
   * @returns {Object|null} Found column or null
   */
  function findColumnByField(columns, fieldName) {
    return columns.find(column => column.field === fieldName) || null;
  }

  /**
   * Inserts a column after the basePeriod column, or at the end if basePeriod not found
   * 
   * @param {Array} columns - The columns array to modify
   * @param {Object} columnGroup - The column group to insert
   */
  function insertAfterBasePeriodOrEnd(columns, columnGroup) {
    const basePeriodIndex = columns.findIndex(column => column.field === 'basePeriod');

    if (basePeriodIndex !== -1) {
      // Insert after basePeriod column
      columns.splice(basePeriodIndex + 1, 0, columnGroup);
    } else {
      // basePeriod not found, add to end
      columns.push(columnGroup);
    }
  }
}